import * as React from 'react';
import classNames from 'classnames';
import debug from 'debug';
import { isNil, sortBy, debounce, head, isString, throttle, without } from 'lodash/fp';
import { area, curveMonotoneX, line } from 'd3-shape';
import { scaleLinear, scaleTime } from 'd3-scale';
import { extent, max, bisector } from 'd3-array';
import * as t from 'io-ts';
import { isEmpty, lensPath, set, dissocPath } from 'ramda';
import MurmurHash3 from 'imurmurhash';
import { Button as Button$1, Toggle as Toggle$1 } from '@carbon/react';
import { PlayOutline, PauseOutline, Warning, WarningAlt, ViewOff, Unknown, RuleCancelled, Unlink, CenterCircle, Tag as Tag$1, Share, Settings, IbmCloudPakMantaAutomatedDataLineage, RepoArtifact, InformationSquare, VpnConnection, Popup, OpenPanelRight, Movement, OverflowMenuVertical, Replicate, WatsonHealthStatusAcknowledge, Logout, Help, Location, PointOfPresence, DnsServices, Terminal, ListChecked, Filter, Export, Email, Download, WatsonHealthSpineLabel, Network_4, SearchLocate, Deploy, Dashboard, Copy, SettingsAdjust, Chat, Close, CloseFilled, CheckmarkOutline, CheckmarkFilled, Checkmark, Catalog, CallsAll, DataShare, Security, View, Insert, CertificateCheck } from '@carbon/react/icons';
import { useMeasure } from '@react-hookz/web';
import { usePopper } from 'react-popper';
import { createPortal } from 'react-dom';
import emStyled from '@emotion/styled';
import * as PropTypes from 'prop-types';
import { isNil as isNil$1 } from 'lodash';
import onClickOutside from 'react-onclickoutside';
import { Slide, ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactSelect from 'react-select';
import hoistNonReactStatics from 'hoist-non-react-statics';
import onElementResize from 'element-resize-event';

function _defineProperty(e, r, t) {
  return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
    value: t,
    enumerable: !0,
    configurable: !0,
    writable: !0
  }) : e[r] = t, e;
}
function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}
function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : i + "";
}

// eslint-disable-next-line
const compose = (f, g) => x => f(g(x));

// https://github.com/microsoft/TypeScript/issues/30271

function elemT(array) {
  // eslint-disable-next-line
  return array;
}

let BlockType = /*#__PURE__*/function (BlockType) {
  BlockType["CrudRule"] = "CrudRule";
  BlockType["CrudRuleGroup"] = "CrudRuleGroup";
  return BlockType;
}({});

// ---------------------------------------------------------------------------

const Condition = t.keyof({
  AND: null,
  OR: null
});

// ---------------------------------------------------------------------------

function isNotEmpty(value) {
  return !isEmpty(value);
}
function isNotNil(value) {
  return !isNil(value);
}
function isNotNilOrEmpty(value) {
  return isNotNil(value) && isNotEmpty(value);
}

// ---------------------------------------------------------------------------

class ExhaustiveError extends Error {
  constructor(value) {
    // eslint-disable-next-line
    super(`Unreachable case: ${value}`);
  }
}
class DefaultPropError extends Error {
  constructor(message, ...rest) {
    super(...rest);
    this.message = message;
    this.name = 'DefaultPropError';

    // eslint-disable-next-line @typescript-eslint/unbound-method
    if (isNotNil(Error.captureStackTrace)) {
      Error.captureStackTrace(this, DefaultPropError);
    } else {
      this.stack = new Error(message).stack;
    }
  }
}
class DependentPropError extends Error {
  constructor(message, ...rest) {
    super(...rest);
    this.message = message;
    this.name = 'DependentPropError';

    // eslint-disable-next-line @typescript-eslint/unbound-method
    if (isNotNil(Error.captureStackTrace)) {
      Error.captureStackTrace(this, DefaultPropError);
    } else {
      this.stack = new Error(message).stack;
    }
  }
}

// ---------------------------------------------------------------------------

// @see: https://github.com/RandoriDev/randori-flask/blob/4e1b86c01648cc25fdf0f5c62e2bf7f418ef70b2/rflask/sqlalchemy_querybuilder/sqlalchemy_querybuilder.py

const GenericOperator = t.keyof({
  begins_with: null,
  between: null,
  contains: null,
  ends_with: null,
  equal: null,
  greater: null,
  greater_or_equal: null,
  greater_or_equal_utc_seconds_ago: null,
  greater_utc_seconds_ago: null,
  icontains: null,
  in: null,
  is_empty: null,
  is_not_empty: null,
  is_not_null: null,
  is_null: null,
  less: null,
  less_or_equal: null,
  less_or_equal_utc_seconds_ago: null,
  less_utc_seconds_ago: null,
  not_begins_with: null,
  not_contains: null,
  not_ends_with: null,
  not_equal: null,
  not_icontains: null,
  not_in: null
});
const JsonOperator = t.keyof({
  contained_by: null,
  contains: null,
  equal: null,
  has_key: null,
  is_empty: null,
  is_not_empty: null,
  is_not_null: null,
  is_null: null,
  not_contained_by: null,
  not_contains: null,
  not_equal: null,
  not_has_key: null
});
const ArrayOperator = t.keyof({
  contained_by: null,
  contains: null,
  contains_element: null,
  contains_element_ilike: null,
  equal: null,
  inet_contains_element_ilike: null,
  inet_contained_by_element: null,
  inet_not_contains_element_ilike: null,
  inet_not_contained_by_element: null,
  not_contains_element_ilike: null,
  not_contained_by: null,
  not_contains: null,
  not_contains_element: null,
  not_equal: null
});
const InetOperator = t.keyof({
  contained_by: null,
  contains: null,
  equal: null,
  greater: null,
  greater_or_equal: null,
  is_not_null: null,
  is_null: null,
  less: null,
  less_or_equal: null,
  not_contained_by: null,
  not_equal: null
});
const TsTzDataOperator = t.keyof({
  contained_by: null,
  contains: null,
  contains_element: null,
  equal: null,
  not_contains: null,
  not_contains_element: null,
  not_equal: null
});
const RegexOperator = t.keyof({
  matched_by: null,
  matches: null
});
const EtcOperator = t.keyof({
  after: null,
  before: null,
  more: null
});
const Operator = t.union([ArrayOperator, EtcOperator, GenericOperator, InetOperator, JsonOperator, RegexOperator, TsTzDataOperator]);
const OperatorLabel = t.keyof({
  after: null,
  before: null,
  'begins with': null,
  'contained by': null,
  contains: null,
  'does not contain': null,
  'greater or equal': null,
  'has key': null,
  'is in': null,
  'is not null': null,
  'is not': null,
  'is null': null,
  is: null,
  'less or equal': null,
  'less than': null,
  'more than': null,
  'not in': null,
  between: null,
  'contains element': null,
  'ends with': null,
  in: null,
  'is empty': null,
  'is not empty': null,
  'matched by': null,
  matches: null,
  'does not begin with': null,
  'is not contained by': null,
  'does not end with': null,
  'does not have key': null,
  'contains element ilike': null,
  'not contains element ilike': null,
  'inet contains element ilike': null,
  'inet contained by element': null,
  'inet not contains element ilike': null,
  'inet not contained by element': null,
  'not contained by': null
});
// eslint-disable-next-line complexity
function getOperatorLabel(operator, rule) {
  if (isNil(operator) || isEmpty(operator)) {
    return '';
  }
  switch (operator) {
    case 'before':
      return 'before';
    case 'begins_with':
      return 'begins with';
    case 'between':
      return 'between';
    case 'contained_by':
      return 'contained by';
    case 'contains':
      return 'contains';
    case 'contains_element':
      return 'contains';
    case 'contains_element_ilike':
      return 'contains element ilike';
    case 'ends_with':
      return 'ends with';
    case 'equal':
      return 'is';
    case 'greater_or_equal':
      return 'greater or equal';
    case 'icontains':
      return 'contains';
    case 'in':
      return 'in';
    case 'inet_contains_element_ilike':
      return 'contains';
    case 'inet_contained_by_element':
      return 'contained by';
    case 'inet_not_contains_element_ilike':
      return 'does not contain';
    case 'inet_not_contained_by_element':
      return 'not contained by';
    case 'is_empty':
      return 'is empty';
    case 'is_not_empty':
      return 'is not empty';
    case 'is_not_null':
      return 'is not null';
    case 'is_null':
      return 'is null';
    case 'less_or_equal':
      return 'less or equal';
    case 'matched_by':
      return 'matched by';
    case 'matches':
      return 'matches';
    case 'not_contains_element_ilike':
      return 'not contains element ilike';
    case 'not_begins_with':
      return 'does not begin with';
    case 'not_contained_by':
      return 'is not contained by';
    case 'not_contains':
      return 'does not contain';
    case 'not_contains_element':
      return 'does not contain';
    case 'not_ends_with':
      return 'does not end with';
    case 'not_equal':
      return 'is not';
    case 'not_has_key':
      return 'does not have key';
    case 'not_icontains':
      return 'does not contain';
    case 'not_in':
      return 'not in';
    case 'not_contained_by':
      return 'not contained by';

    // -----------------------------------------------------------------------

    case 'greater':
    case 'greater_or_equal_utc_seconds_ago':
    case 'greater_utc_seconds_ago':
    case 'more':
      if (rule.type === 'datetime') {
        return 'before';
      } else {
        return 'more than';
      }
    case 'after':
    case 'less':
    case 'less_or_equal_utc_seconds_ago':
    case 'less_utc_seconds_ago':
      if (rule.type === 'datetime') {
        return 'after';
      } else {
        return 'less than';
      }
    case 'has_key':
      if (rule.id === 'table.tags') {
        return 'is in';
      } else {
        return 'has key';
      }
    default:
      throw new ExhaustiveError(operator);
  }
}

// ---------------------------------------------------------------------------

// @TODO: Eliminate fluff fields
//
// Which of these fields can be eliminated? this would help with serialization
// size.
const CrudRule = t.intersection([t.interface({
  field: t.string
}), t.partial({
  id: t.string,
  input: t.string,
  label: t.string,
  operator: Operator,
  randoriOnly: t.boolean,
  type: t.string,
  ui_id: t.string,
  value: t.unknown
})]);
const CrudRuleGroup = t.recursion('CrudRuleGroup', () => t.intersection([t.type({
  condition: Condition,
  rules: t.array(t.union([CrudRule, CrudRuleGroup]))
}), t.partial({
  label: t.string,
  labelOverride: t.string,
  randoriOnly: t.boolean,
  ui_id: t.string,
  dontRenderAsRule: t.boolean
})]));
const RuleOrRuleGroup = t.union([CrudRule, CrudRuleGroup]);
function isRuleGroup(rule) {
  return rule.condition !== undefined;
}
function isRule(rule) {
  return rule.id !== undefined;
}

function getDisplayName(CC) {
  return CC.displayName || CC.name || 'Component';
}

// ---------------------------------------------------------------------------

function isRenderProps(renderProp) {
  return typeof renderProp === 'function';
}

const __ENTER_KEY__ = 'Enter';
const validateEnterKeyPress = eventHandler => e => {
  if (e.key !== __ENTER_KEY__) {
    return; // bail
  }
  eventHandler();
};

// ---------------------------------------------------------------------------

const log$c = debug('ROOTKIT:values');
const setScrollbarWidthVar = (innerWidth = window.innerWidth, clientWidth = document.documentElement.clientWidth) => {
  const scrollbarWidth = innerWidth - clientWidth;
  log$c('--rootkit__scrollbar-width', {
    clientWidth,
    innerWidth,
    scrollbarWidth
  });
  document.documentElement.style.setProperty('--rootkit__scrollbar-width', `${scrollbarWidth}px`);
};

// ---------------------------------------------------------------------------

const Axis = props => {
  const _width = props.width - props.padding;
  const _height = props.height - props.padding;
  const highestValue = props.data.reduce((prev, curr) => {
    const {
      value
    } = curr;
    if (prev > value) {
      return prev;
    } else {
      return value;
    }
  }, 0);
  if (props.type === 'x') {
    return /*#__PURE__*/React.createElement(X, _extends({}, props, {
      height: _height,
      max: highestValue,
      width: _width
    }));
  } else {
    return /*#__PURE__*/React.createElement(Y, _extends({}, props, {
      height: _height,
      max: highestValue,
      width: _width
    }));
  }
};
const X = props => {
  const scale = isNotNilOrEmpty(props.scale) ? props.scale : scaleLinear().domain([0, props.max]).range([0, props.width - props.padding]);
  const Xticks = scale.ticks(props.numberOfTicks).map(value => ({
    value: value,
    xOffset: scale(value)
  }));

  // @TODO: Make text anchor correct
  return /*#__PURE__*/React.createElement("g", {
    transform: `translate(${props.padding}, ${props.height + props.padding})`
  }, Xticks.map(({
    value,
    xOffset
  }, index) => {
    return /*#__PURE__*/React.createElement("g", {
      key: value.toString(),
      transform: `translate(${xOffset}, 0)`
    }, props.renderTick(value, index));
  }));
};
const Y = props => {
  const yTransform = isNotNilOrEmpty(props.yTransform) ? props.yTransform : `translate(${0}, -${props.padding * 2})`;
  const scale = isNotNilOrEmpty(props.scale) ? props.scale : scaleLinear().domain([0, props.max]).range([props.height - props.padding, 0]);
  const Yticks = scale.ticks(props.numberOfTicks).map(value => ({
    value,
    yOffset: scale(value)
  }));

  // @TODO: Make text anchor correct
  return /*#__PURE__*/React.createElement("g", {
    transform: yTransform
  }, Yticks.map(({
    value,
    yOffset
  }, index) => {
    return /*#__PURE__*/React.createElement("g", {
      key: value.toString(),
      transform: `translate(0, ${yOffset})`
    }, props.renderTick(value, index));
  }));
};

// ---------------------------------------------------------------------------

const log$b = debug('ROOTKIT:AreaGraph');
const APPROX_X_TICK_WIDTH = 80;
const APPROX_Y_TICK_HEIGHT = 15;
// eslint-disable-next-line complexity
const AreaGraph = props => {
  const [hoveredValues, setHoveredValues] = React.useState();
  const [hoverDataHead, setHovereDataHead] = React.useState();
  const _data = props.data.map(data => sortBy(['x'], data));
  const _dataFlattened = props.data.reduce((arr, data) => [...arr, ...data], []);
  const _getTotalData = (data, xType) => {
    const maybeHead = head(data);
    if (isNotNilOrEmpty(maybeHead) && maybeHead !== undefined) {
      return maybeHead;
    } else {
      const defaultXValue = xType === 'time' ? new Date().getTime() : 0;
      return [{
        y: 0,
        x: defaultXValue
      }];
    }
  };
  const _totalData = _getTotalData(_data, props.xScaleKind);
  const svgRef = React.useRef(null);
  if (props.yAxisSide === 'right') {
    throw new Error('yAxisSide: right - currently unsupported');
  }
  const AXIS_COLOR = 'rgba(17, 25, 31, 0.1)';
  const AXIS_LABEL_COLOR = 'rgba(17, 25, 31, 0.5)';
  const STROKE_WIDTH = 0.7;

  // if we have lots of data, dont draw overlapping ticks
  const xTickCount = Math.min(_totalData.length, props.width / APPROX_X_TICK_WIDTH);
  const yTickCount = Math.min(_totalData.length, props.height / APPROX_Y_TICK_HEIGHT);
  const padding = isNotNilOrEmpty(props.padding) ? props.padding : 0;
  const defaultFormatter = val => val.toString();
  const xFormatter = isNotNilOrEmpty(props.xFormatter) ? props.xFormatter : defaultFormatter;
  const yFormatter = isNotNilOrEmpty(props.yFormatter) ? props.yFormatter : defaultFormatter;
  const xValues = _totalData.map(d => ({
    value: d.x
  }));
  const yValues = _totalData.map(d => ({
    value: d.y
  }));
  const [lowestX, highestX] = extent(xValues.map(x => x.value));
  const highestY = max(yValues.map(y => y.value));
  if (isNil(highestY) || isNil(lowestX) || isNil(highestX)) {
    throw new Error('Range values are bad');
  }
  const xScaleConfig = {
    domainHigh: highestX,
    domainLow: lowestX,
    rangeHigh: props.width - padding * 3,
    rangeLow: padding * 2
  };
  const xScale = getXScale(xScaleConfig, props.xScaleKind);
  const yScale = scaleLinear().domain([0, highestY]).range([props.height, padding * 2]);
  const svgArea = area().curve(curveMonotoneX).x(d => xScale(d.x)).y1(d => yScale(d.y)).y0(props.height);
  const svgLine = line().curve(curveMonotoneX).x(d => xScale(d.x)).y(d => yScale(d.y));
  const bisect = bisector(d => d.value);

  // @TODO: Investigate React.useLayoutEffect
  //
  // This is obviously nutty. We should schedule the hover side-effect better
  // using a hook.
  let ticking = false;
  const _handleMouseMove = e => {
    const svg = svgRef.current;
    if (isNil(svg)) {
      return;
    }
    try {
      // @see: https://stackoverflow.com/a/42711775
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const cursorpt = pt.matrixTransform(svg.getScreenCTM().inverse());
      const xVal = xScale.invert(cursorpt.x);
      const closestX = bisect.center(xValues, xVal);
      const matchedCoordinates = _dataFlattened.filter(d => {
        const _x = d.x;
        return _x === xValues[closestX].value;
      });
      if (isNotNil(props.onMouseMove)) {
        const eventData = isNotNil(matchedCoordinates) ? {
          hoveredData: matchedCoordinates,
          closestX,
          cursorpt
        } : undefined;
        const headMatchedCoordinates = head(matchedCoordinates);
        const eventDataHead = isNotNil(headMatchedCoordinates) ? headMatchedCoordinates : undefined;
        setHoveredValues(eventData);
        setHovereDataHead(eventDataHead);
        props.onMouseMove(e, eventData);
      }
      ticking = false;
    } catch (err) {
      log$b(err);
      if (isNotNil(props.onMouseMove)) {
        setHoveredValues(undefined);
        props.onMouseMove(e, undefined);
      }
      ticking = false;
    }
  };
  const _debouncedHandler = debounce(0, _handleMouseMove);
  const _scheduleMouseMove = e => {
    if (!ticking) {
      requestAnimationFrame(() => _debouncedHandler(e));
    }
    ticking = true;
  };
  const _handleMouseOut = e => {
    if (isNotNil(props.onMouseMove)) {
      setHoveredValues(undefined);
      props.onMouseMove(e, undefined);
    }
    ticking = false;
  };
  const onMouseMove = isNotNilOrEmpty(props.onMouseMove) ? _scheduleMouseMove : e => e;
  const onMouseOut = isNotNilOrEmpty(props.onMouseMove) ? _handleMouseOut : e => e;
  const svgClasses = classNames('area-graph', props.addlClasses);
  const yTransform = `translate(${0}, -${padding})`;
  const areaTransform = `translate(${padding}, -${padding})`;
  const getTextXPosition = xValue => {
    const topQuarter = Math.min(Math.round(xValues.length * 3 / 4), xValues.length - 1);
    /* 
      renders the tooltip to the left of the data point for any data points
      in the upper quartile so that they are not rendered outside of the graph
      and cut off
    */
    const offset = xValue >= xValues[topQuarter].value ? -210 : 20;
    return xScale(xValue) + offset;
  };
  const getTextYPosition = yValue => {
    const bottomThird = Math.round(highestY / 3);
    /* 
      ensures the tooltip does render outside of the graph and get cut off
      for data points that are too low in the graph or graphs with little range in data
    */
    const valueToScale = Math.max(bottomThird, yValue, 1);
    return yScale(valueToScale);
  };
  return /*#__PURE__*/React.createElement("svg", _extends({
    className: svgClasses,
    onMouseMove: onMouseMove,
    onMouseOut: onMouseOut,
    viewBox: `0 0 ${props.width} ${props.height}`,
    ref: svgRef
  }, props.addlAttrs), /*#__PURE__*/React.createElement(Axis, {
    data: xValues,
    height: props.height,
    numberOfTicks: xTickCount,
    padding: padding,
    scale: xScale,
    type: "x",
    width: props.width,
    renderTick: value => {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("text", {
        key: value.toString(),
        style: {
          fill: AXIS_LABEL_COLOR,
          fontSize: '10px',
          textAnchor: 'middle'
        }
      }, xFormatter(value)));
    }
  }), /*#__PURE__*/React.createElement(Axis, {
    data: yValues,
    height: props.height,
    numberOfTicks: yTickCount,
    padding: padding,
    scale: yScale,
    type: "y",
    width: props.width,
    yTransform: yTransform,
    renderTick: (value, index) => {
      const textTransform = `translate(${padding * 1.5}px, 3px)`;
      return /*#__PURE__*/React.createElement(React.Fragment, null, index !== 0 &&
      /*#__PURE__*/
      // leave off first notch
      React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("line", {
        stroke: AXIS_COLOR,
        strokeWidth: STROKE_WIDTH,
        x1: padding * 3,
        x2: props.width,
        vectorEffect: "non-scaling-stroke"
      }), /*#__PURE__*/React.createElement("text", {
        key: value.toString(),
        style: {
          fill: AXIS_LABEL_COLOR,
          fontSize: '10px',
          transform: textTransform
        }
      }, yFormatter(value))));
    }
  }), _data.map((data, i) => {
    const areaD = svgArea(data);
    const lineD = svgLine(data);
    return /*#__PURE__*/React.createElement("g", {
      transform: areaTransform,
      key: `graph-${i}`
    }, isNotNilOrEmpty(lineD) && /*#__PURE__*/React.createElement("path", {
      d: lineD,
      fill: "none",
      stroke: props.strokeColor[i],
      strokeWidth: STROKE_WIDTH
    }), isNotNilOrEmpty(areaD) && /*#__PURE__*/React.createElement("path", {
      d: areaD,
      fill: props.fillColor[i]
    }));
  }), isNotNil(hoveredValues) && isNotNil(hoverDataHead) && /*#__PURE__*/React.createElement("g", {
    transform: areaTransform
  }, /*#__PURE__*/React.createElement("rect", {
    width: "205",
    height: "65",
    fill: "white",
    stroke: "#D3D7DB",
    strokeWidth: "1",
    rx: "3",
    x: getTextXPosition(hoverDataHead.x) - 10,
    y: getTextYPosition(hoverDataHead.y) - 5
  }), /*#__PURE__*/React.createElement("text", {
    className: "rootkit__typography--tooltip",
    alignmentBaseline: "middle",
    textAnchor: "left",
    x: getTextXPosition(hoverDataHead.x),
    y: getTextYPosition(hoverDataHead.y)
  }, /*#__PURE__*/React.createElement("tspan", {
    x: getTextXPosition(hoverDataHead.x),
    dy: "1.2em",
    style: {
      fontWeight: 700
    }
  }, new Date(hoverDataHead.x).toDateString().slice(4)), hoveredValues.hoveredData.map((data, i) => /*#__PURE__*/React.createElement("tspan", {
    dy: "1.2em",
    key: `${data.y}-${i}`,
    x: getTextXPosition(hoverDataHead.x)
  }, data.y, " ", isNotNil(data.label) ? `- ${data.label}` : ''))), /*#__PURE__*/React.createElement("line", {
    stroke: "#8396A6",
    x1: xScale(hoverDataHead.x),
    y1: "0",
    x2: xScale(hoverDataHead.x),
    y2: "200"
  }), hoveredValues.hoveredData.map((data, i) => /*#__PURE__*/React.createElement("circle", {
    key: `${data.x}-${data.y}-${i}`,
    r: 3,
    fill: "black",
    stroke: "black",
    cx: xScale(data.x),
    cy: yScale(data.y)
  }))));
};

// ---------------------------------------------------------------------------

function getXScale(config, kind = 'number') {
  if (kind === 'number') {
    return scaleLinear().domain([config.domainLow, config.domainHigh]).range([config.rangeLow, config.rangeHigh]);
  } else {
    return scaleTime().domain([config.domainLow, config.domainHigh]).rangeRound([config.rangeLow, config.rangeHigh]);
  }
}

const SMALL = 'small';
const MEDIUM = 'medium';
const LARGE = 'large';
const SIZES = new Set([SMALL, MEDIUM, LARGE]);
const Avatar = props => {
  let size = props.size;
  if (size === undefined) {
    size = MEDIUM;
  }
  if (!SIZES.has(size)) {
    throw Error(`${size} is not a valid size`);
  }
  return /*#__PURE__*/React.createElement("div", {
    className: `avatar-circle avatar-circle-${size}`,
    style: {
      backgroundColor: props.backgroundColor
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: `avatar-initials-${size}`,
    style: {
      color: props.color
    }
  }, props.initials));
};

// ---------------------------------------------------------------------------

const TEN_BITS_MASK = 2 ** 10 - 1;

/**
 * Returns up to 2 upper case initials from the provided name.
 *
 * @export
 * @param {string} name
 * @return {*}  {string}
 */
/* eslint-disable no-magic-numbers */
function getInitials(name) {
  try {
    const trimmed = name.trim();
    const words = trimmed.split(' ');
    const firstLetter = words[0][0].toUpperCase();
    if (words.length === 1) {
      return firstLetter;
    }
    const secondLetter = words[words.length - 1][0].toUpperCase();
    return `${firstLetter}${secondLetter}`;
  } catch (e) {
    return '';
  }
}
/* eslint-enable no-magic-numbers */

/**
 * Computes a color, up to a maximum brightness (between 0.0 and 1.0), using name as the seed.  This compute color
 * function is deterministic and the same name will always yield the same color.
 *
 * @export
 * @param {string} name
 * @param {number} maxBrightness
 * @return {*}  {string}
 */
/* eslint-disable no-magic-numbers */
function computeColor(name, maxBrightness) {
  if (maxBrightness < 0 || maxBrightness > 1) {
    throw Error('maxBrightness must be in the range of 0.0 and 1.0 (inclusive)');
  }
  const hash = MurmurHash3(name).result();
  const maxValue = Math.floor(255 * maxBrightness);

  // Take 10 bits, 3 times, from the 32 bit hash and use those as the basis for the 3 colors.
  const firstNumber = hash & TEN_BITS_MASK;
  const secondNumber = (hash & TEN_BITS_MASK << 10) >> 10;
  const thirdNumber = (hash & TEN_BITS_MASK << 20) >> 20;
  const red = firstNumber % maxValue;
  const green = secondNumber % maxValue;
  const blue = thirdNumber % maxValue;
  return `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;
}
/* eslint-enable no-magic-numbers */

var Utils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  computeColor: computeColor,
  getInitials: getInitials
});

const AvatarUtils = Utils;

// ---------------------------------------------------------------------------

// eslint-disable-next-line complexity
const BarGraph = props => {
  const autoMargins = props.autoMargins ?? true;
  const showEndNotch = props.showEndNotch ?? false;
  const showStartNotch = props.showStartNotch ?? false;

  // account for negative percents
  // let startPercentage: number = propOr(0, 'startPercentage', props)
  let startPercentage = props.startPercentage ?? 0;
  let percentOfSize = props.percentOfSize;
  if (Math.sign(props.percentOfSize) === -1) {
    percentOfSize = Math.abs(percentOfSize);
    startPercentage = startPercentage - percentOfSize - 1; // nudge the negative case to line up with ruler
  }
  const startBufferStyle = {
    width: `${startPercentage}%`
  };
  const style = {
    width: `${percentOfSize}%`,
    marginLeft: autoMargins && startPercentage === 0 || showStartNotch ? 0 : 5,
    marginRight: autoMargins && percentOfSize !== 100 || showEndNotch ? 0 : 5
  };
  const remainderStyle = {
    width: `${100 - (percentOfSize + startPercentage)}%`
  };
  const wrapperClasses = classNames('bar-wrapper', props.addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: "bar-graph"
  }, isNotNil(props.label) && /*#__PURE__*/React.createElement(React.Fragment, null, props.label()), /*#__PURE__*/React.createElement("div", {
    className: wrapperClasses,
    "aria-label": 'Bar Graph'
  }, /*#__PURE__*/React.createElement("span", {
    className: "bar-remainder",
    style: startBufferStyle,
    "aria-hidden": true
  }, "\xA0"), showStartNotch && /*#__PURE__*/React.createElement("span", {
    className: "tick start"
  }, "\xA0"), /*#__PURE__*/React.createElement("span", {
    ref: props.barRef,
    className: "bar",
    style: style,
    "aria-label": `${percentOfSize}%`
  }, "\xA0"), showEndNotch && /*#__PURE__*/React.createElement("span", {
    className: "tick end"
  }, "\xA0"), /*#__PURE__*/React.createElement("span", {
    className: "bar-remainder",
    style: remainderStyle,
    "aria-hidden": true
  }, "\xA0")));
};
BarGraph.defaultProps = {};

// ---------------------------------------------------------------------------

const StackedBar = props => {
  const barClasses = classNames('bar', props.section.class);
  const barStyles = {
    marginRight: props.section.value ? 1 : 0,
    width: `${props.section.value / props.totalValue * 100}%`
  };
  const orientation = props.startingPoint < props.totalValue / 2 ? 'left' : 'right';
  const tooltipClassnames = classNames('bar-tooltip', orientation);
  const ariaLabel = `${props.section.value / props.totalValue * 100}%`;
  return /*#__PURE__*/React.createElement("span", {
    "aria-label": ariaLabel,
    className: barClasses,
    onClick: props.section.onClick,
    style: barStyles
  }, "\xA0", isNotNil(props.section.tooltip) && /*#__PURE__*/React.createElement("span", {
    className: tooltipClassnames
  }, props.section.tooltip()));
};

// ---------------------------------------------------------------------------

const StackedBarGraph = props => {
  const sectionSums = props.sections.reduce((prev, curr) => prev + curr.value, 0);
  const startingPointList = props.sections.reduce((prev, curr, i) => [...prev, prev[i] + curr.value], [0]);

  // fallback total can be sum of all sections
  const totalValue = isNotNil(props.totalValue) ? props.totalValue : sectionSums;
  const wrapperClasses = classNames('bar-wrapper', props.addlClasses);
  const remainderStyle = {
    width: `${totalValue - sectionSums}%`
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "bar-graph"
  }, isNotNil(props.label) && /*#__PURE__*/React.createElement(React.Fragment, null, props.label()), /*#__PURE__*/React.createElement("div", {
    className: wrapperClasses,
    "aria-label": 'Bar Graph'
  }, props.sections.map((section, i) => /*#__PURE__*/React.createElement(StackedBar, {
    key: i,
    section: section,
    totalValue: totalValue,
    startingPoint: startingPointList[i]
  })), /*#__PURE__*/React.createElement("span", {
    className: "bar-remainder",
    style: remainderStyle,
    "aria-hidden": true
  }, "\xA0")));
};

// ---------------------------------------------------------------------------

const CarbonIcon = ({
  kind,
  ...restProps
}) => {
  switch (kind) {
    case 'artifacts':
      return /*#__PURE__*/React.createElement(CertificateCheck, restProps);
    case 'assign-impact':
      return /*#__PURE__*/React.createElement(Insert, restProps);
    case 'attackers-perspective':
      return /*#__PURE__*/React.createElement(View, restProps);
    case 'authorize':
      return /*#__PURE__*/React.createElement(Security, restProps);
    case 'callbacks':
      return /*#__PURE__*/React.createElement(DataShare, restProps);
    case 'calls-all':
      return /*#__PURE__*/React.createElement(CallsAll, restProps);
    case 'catalog':
      return /*#__PURE__*/React.createElement(Catalog, restProps);
    case 'checkmark':
      return /*#__PURE__*/React.createElement(Checkmark, restProps);
    case 'checkmark-filled':
      return /*#__PURE__*/React.createElement(CheckmarkFilled, restProps);
    case 'checkmark-outline':
      return /*#__PURE__*/React.createElement(CheckmarkOutline, restProps);
    case 'close-filled':
      return /*#__PURE__*/React.createElement(CloseFilled, restProps);
    case 'v3-close':
      return /*#__PURE__*/React.createElement(Close, restProps);
    case 'comment':
      return /*#__PURE__*/React.createElement(Chat, restProps);
    case 'configuration':
      return /*#__PURE__*/React.createElement(SettingsAdjust, restProps);
    case 'copy':
      return /*#__PURE__*/React.createElement(Copy, restProps);
    case 'dashboard':
      return /*#__PURE__*/React.createElement(Dashboard, restProps);
    case 'deploy':
      return /*#__PURE__*/React.createElement(Deploy, restProps);
    case 'carbon-details':
      return /*#__PURE__*/React.createElement(SearchLocate, restProps);
    case 'detections':
      return /*#__PURE__*/React.createElement(Network_4, restProps);
    case 'discovery-path':
      return /*#__PURE__*/React.createElement(WatsonHealthSpineLabel, restProps);
    case 'download':
      return /*#__PURE__*/React.createElement(Download, restProps);
    case 'email':
      return /*#__PURE__*/React.createElement(Email, restProps);
    case 'export':
      return /*#__PURE__*/React.createElement(Export, restProps);
    case 'filter':
      return /*#__PURE__*/React.createElement(Filter, restProps);
    case 'guidance':
      return /*#__PURE__*/React.createElement(ListChecked, restProps);
    case 'hoc-cli':
      return /*#__PURE__*/React.createElement(Terminal, restProps);
    case 'hostnames':
      return /*#__PURE__*/React.createElement(DnsServices, restProps);
    case 'implants':
      return /*#__PURE__*/React.createElement(PointOfPresence, restProps);
    case 'ips':
      return /*#__PURE__*/React.createElement(Location, restProps);
    case 'knowledge-base':
      return /*#__PURE__*/React.createElement(Help, restProps);
    case 'logout':
      return /*#__PURE__*/React.createElement(Logout, restProps);
    case 'mark-status':
      return /*#__PURE__*/React.createElement(WatsonHealthStatusAcknowledge, restProps);
    case 'matching-entities':
      return /*#__PURE__*/React.createElement(Replicate, restProps);
    case 'more':
      return /*#__PURE__*/React.createElement(OverflowMenuVertical, restProps);
    case 'movement':
      return /*#__PURE__*/React.createElement(Movement, restProps);
    case 'open-panel-right':
      return /*#__PURE__*/React.createElement(OpenPanelRight, restProps);
    case 'popup':
      return /*#__PURE__*/React.createElement(Popup, restProps);
    case 'ports':
      return /*#__PURE__*/React.createElement(VpnConnection, restProps);
    case 'profiles':
      return /*#__PURE__*/React.createElement(InformationSquare, restProps);
    case 'repo-artifact':
      return /*#__PURE__*/React.createElement(RepoArtifact, restProps);
    case 'runbook-actions':
      return /*#__PURE__*/React.createElement(CenterCircle, restProps);
    case 'runbooks':
      return /*#__PURE__*/React.createElement(IbmCloudPakMantaAutomatedDataLineage, restProps);
    case 'settings':
      return /*#__PURE__*/React.createElement(Settings, restProps);
    case 'share':
      return /*#__PURE__*/React.createElement(Share, restProps);
    case 'tag':
      return /*#__PURE__*/React.createElement(Tag$1, restProps);
    case 'targets':
      return /*#__PURE__*/React.createElement(CenterCircle, restProps);
    case 'unaffiliate':
      return /*#__PURE__*/React.createElement(Unlink, restProps);
    case 'unauthorize':
      return /*#__PURE__*/React.createElement(RuleCancelled, restProps);
    case 'unknown':
      return /*#__PURE__*/React.createElement(Unknown, restProps);
    case 'view-off':
      return /*#__PURE__*/React.createElement(ViewOff, restProps);
    case 'warning-alt':
      return /*#__PURE__*/React.createElement(WarningAlt, restProps);
    case 'warning':
      return /*#__PURE__*/React.createElement(Warning, restProps);
    case 'pause-outline':
      return /*#__PURE__*/React.createElement(PauseOutline, restProps);
    case 'play-outline':
      return /*#__PURE__*/React.createElement(PlayOutline, restProps);
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------

function getDimensions(natural, dynamic) {
  return {
    ...natural,
    ...dynamic
  };
}

/**
 * Factory method for producing consistent icon function components.
 *
 * @export
 * @param {string} kind
 * @param {number} width
 * @param {number} height
 * @param {(Array<React.ReactElement> | React.ReactElement)} paths
 * @return {*}  {React.FunctionComponent<IconProps>}
 */
function makeIcon(kind, width, height, paths) {
  const _Icon = props => {
    const {
      aria,
      addlClasses,
      modifier,
      ...restProps
    } = props;
    const defaultDimensions = {
      width,
      height
    };
    const dimensions = getDimensions(defaultDimensions, props.size);
    const classes = classNames('icon', {
      [`icon--${modifier}`]: !isNil(modifier)
    }, `icon-${kind}`, addlClasses);
    return /*#__PURE__*/React.createElement("span", _extends({
      className: classes
    }, restProps, {
      style: {
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`
      }
    }), /*#__PURE__*/React.createElement("svg", _extends({}, aria, {
      viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
      xmlns: "http://www.w3.org/2000/svg"
    }), /*#__PURE__*/React.createElement("g", {
      fill: "none",
      fillRule: "evenodd",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }, paths)));
  };
  return _Icon;
}
function isRandoriIcon$1(name) {
  return ICONKIND.includes(name);
}
const ICONKIND = ['account', 'account-settings', 'add-circle', 'arrow-thick', 'arrow-trend', 'attack', 'authorized', 'authorized--partial', 'caret', 'caret--thin', 'caret--thin-rev', 'cascade', 'chain-break', 'chain-break-diag', 'chain-diag', 'check', 'cli', 'clipboard', 'close', 'cloud', 'comment', 'company', 'compass', 'confidence', 'copy-paste', 'customer', 'dashboard', 'date', 'deny', 'details', 'dock-bottom', 'dock-right', 'domains', 'drawer', 'edit', 'external-link', 'eye', 'failure', 'filter', 'filter-dismiss', 'grab-handle', 'help', 'hoc', 'image', 'impact', 'info', 'ips', 'link', 'list', 'lock', 'lock-account', 'logout', 'map', 'mark', 'menu', 'minus', 'more', 'nav-caret', 'nav-collapse', 'nav-expand', 'networks', 'no', 'note', 'notifications', 'pencil', 'people', 'player', 'plus', 'policy', 'ports', 'promote', 'question', 'rat', 'recon', 'remove', 'ribbon', 'ribbon-new', 'robot', 'save', 'search', 'search--small', 'send', 'separator', 'server', 'services', 'share', 'sign-post', 'slide--closed', 'slide--open', 'sort--asc', 'sort--desc', 'spinner', 'star', 'status', 'status--in-progress', 'status--investigate', 'status--none', 'status--resolved', 'status--reviewed', 'stop', 'success', 'tag', 'target', 'technology', 'thumb-search', 'trash', 'trend-arrow--down', 'trend-arrow--up', 'unauthorized', 'update', 'upload', 'user', 'v2-close', 'v2-collapse', 'v2-expand', 'v2-info', 'warning', 'warning-2', 'wrench'];

var _IconUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ICONKIND: ICONKIND,
  getDimensions: getDimensions,
  isRandoriIcon: isRandoriIcon$1,
  makeIcon: makeIcon
});

// ---------------------------------------------------------------------------

const IconAccount$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 18,
    height: 18
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-account', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M14.5 15.614v-.558a2.25 2.25 0 00-1.134-1.954l-2.414-1.382M7.545 11.719l-2.411 1.383A2.25 2.25 0 004 15.056v.559",
    strokeMiterlimit: "10",
    fill: "none"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.25 12.25a3 3 0 01-3-3v-1.5a3 3 0 016 0v1.5a3 3 0 01-3 3z",
    fill: "none",
    strokeLinecap: "square",
    strokeMiterlimit: "10"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.25 17.5a8.25 8.25 0 100-16.5 8.25 8.25 0 000 16.5z",
    fill: "none",
    strokeLinecap: "square",
    strokeMiterlimit: "10"
  })));
};

// ---------------------------------------------------------------------------

const IconAccountSettings$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 25,
    height: 25
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-account-settings', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M13.3154 0.113979C12.2157 -0.0383432 11.1747 -0.0372944 10.0845 0.11293C9.8613 0.143688 9.68624 0.319942 9.657 0.543356L9.30367 3.24269C8.638 3.43072 8.0056 3.69443 7.41463 4.02485L5.25335 2.36473C5.07483 2.2276 4.8267 2.22668 4.64716 2.36246C3.81969 2.98828 3.02972 3.76467 2.36308 4.64551C2.22718 4.82508 2.22809 5.07334 2.36531 5.25191L4.02504 7.41179C3.69308 8.00381 3.42845 8.63732 3.24014 9.30416L0.540875 9.65748C0.317536 9.68671 0.141316 9.86167 0.110478 10.0848C-0.0315817 11.1126 -0.0420111 12.2087 0.110462 13.3136C0.141258 13.5368 0.317497 13.7118 0.540875 13.741L3.24015 14.0943C3.42845 14.7612 3.69308 15.3947 4.02504 15.9867L2.3653 18.1466C2.22812 18.3251 2.22717 18.5733 2.36298 18.7529C2.9891 19.5807 3.76555 20.3686 4.64696 21.0359C4.82654 21.1718 5.07486 21.171 5.25345 21.0337L7.41446 19.3729C8.00556 19.704 8.63803 19.9678 9.30369 20.1558L9.657 22.8542C9.68623 23.0774 9.86105 23.2536 10.084 23.2845C11.1745 23.4358 12.2157 23.4368 13.3154 23.2845C13.5384 23.2536 13.7133 23.0774 13.7425 22.8541L14.0959 20.1536C14.7602 19.9654 15.3921 19.7011 15.9823 19.37L18.1421 21.0297C18.3207 21.1669 18.5689 21.1678 18.7485 21.032C19.6113 20.3791 20.3799 19.6094 21.0325 18.7469C21.1684 18.5674 21.1674 18.3191 21.0302 18.1406L19.3701 15.9802C19.6997 15.3905 19.9632 14.7593 20.1511 14.0954L22.8507 13.742C23.074 13.7128 23.2503 13.5378 23.2811 13.3146C23.4292 12.2413 23.4355 11.2002 23.2811 10.0837C23.2502 9.86064 23.074 9.68571 22.8507 9.65648L20.1511 9.30313C19.9632 8.63917 19.6997 8.00803 19.3701 7.41826L21.0302 5.25791C21.1674 5.07944 21.1684 4.83135 21.0327 4.65179C20.3799 3.78798 19.6111 3.01927 18.7485 2.36653C18.5689 2.23069 18.3208 2.23158 18.1422 2.36871L15.9821 4.02773C15.3921 3.69734 14.7602 3.43313 14.0959 3.24493L13.7425 0.544377C13.7133 0.321084 13.5384 0.144878 13.3154 0.113979ZM10.2515 3.70614L10.5982 1.05787C11.3378 0.980735 12.0568 0.980446 12.8013 1.05859L13.148 3.70812C13.1749 3.91338 13.3254 4.08086 13.5267 4.12934C14.3259 4.32186 15.0763 4.6374 15.7585 5.05554C15.935 5.16377 16.1601 5.15194 16.3243 5.02579L18.4435 3.39819C19.0145 3.86276 19.5364 4.38474 20.0009 4.95623L18.3723 7.07559C18.2462 7.2397 18.2342 7.46455 18.3422 7.64112C18.7595 8.32336 19.0741 9.07295 19.2667 9.87235C19.3152 10.0736 19.4826 10.2242 19.6879 10.251L22.3363 10.5977C22.4151 11.3508 22.4127 12.0697 22.3363 12.8008L19.6879 13.1475C19.4826 13.1743 19.3152 13.3249 19.2667 13.5262C19.0741 14.3255 18.7595 15.0751 18.3422 15.7574C18.2342 15.9339 18.2462 16.1588 18.3723 16.3229L20.0008 18.4421C19.5362 19.0133 19.0144 19.5357 18.4436 20.0002L16.3244 18.3718C16.16 18.2455 15.9347 18.2337 15.758 18.3422C15.0761 18.7611 14.3258 19.0766 13.5267 19.2692C13.3254 19.3176 13.1749 19.4851 13.148 19.6904L12.8013 22.3399C12.0567 22.418 11.3378 22.4177 10.5982 22.34L10.2515 19.6923C10.2247 19.487 10.074 19.3195 9.87261 19.2711C9.07124 19.0785 8.32098 18.7639 7.63823 18.3451C7.46159 18.2367 7.2364 18.2485 7.07209 18.3748L4.9521 20.0041C4.37148 19.5309 3.84632 19.0016 3.39489 18.4479L5.02323 16.3289C5.14954 16.1645 5.16133 15.9393 5.05286 15.7626C4.63297 15.0788 4.31749 14.3276 4.12496 13.5255C4.0766 13.3241 3.90906 13.1734 3.70366 13.1465L1.05586 12.7999C0.979422 12.0506 0.983338 11.309 1.05582 10.5986L3.70366 10.252C3.90906 10.2251 4.07661 10.0744 4.12496 9.87296C4.31749 9.07089 4.63297 8.31971 5.05286 7.63587C5.16133 7.45922 5.14954 7.23396 5.02323 7.06959L3.39493 4.95061C3.86767 4.37086 4.39793 3.84582 4.95209 3.39428L7.07219 5.02277C7.23635 5.14887 7.46125 5.16077 7.6378 5.0527C8.3208 4.63464 9.07123 4.32 9.87261 4.12741C10.074 4.07901 10.2247 3.91149 10.2515 3.70614ZM8.04759 11.6993C8.04759 9.68449 9.68166 8.05127 11.6956 8.05127C13.7105 8.05127 15.3436 9.68441 15.3436 11.6993C15.3436 13.714 13.7106 15.3463 11.6956 15.3463C9.6815 15.3463 8.04759 13.7139 8.04759 11.6993ZM11.6956 7.05127C9.12953 7.05127 7.04759 9.13205 7.04759 11.6993C7.04759 14.2666 9.12969 16.3463 11.6956 16.3463C14.2626 16.3463 16.3436 14.2666 16.3436 11.6993C16.3436 9.13213 14.2627 7.05127 11.6956 7.05127Z"
  })));
};

// ---------------------------------------------------------------------------

const IconAddCircle$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 14,
    height: 14
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-add-circle', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M10.2998 7.00003H3.70017",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7 10.2999V3.7002",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "7",
    cy: "7.00008",
    r: "6.5",
    transform: "rotate(45 7 7.00008)",
    fill: "none"
  })));
};

// ---------------------------------------------------------------------------

const IconArrowDown$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 8,
    height: 10
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-arrow--down', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3.90918 9L3.90918 1",
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.81836 6.09091L3.90927 9L1.00018 6.09091",
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

// ---------------------------------------------------------------------------

const IconArrowThick$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 8,
    height: 10
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-arrow-thick', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    stroke: "none",
    clipRule: "evenodd",
    d: "M5.5 10h-3V4H0l4-4 4 4H5.5v6z",
    fill: "#DA021B"
  })));
};

// ---------------------------------------------------------------------------

const IconArrowTrend$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 14,
    height: 13
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-arrow-trend', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    fill: "none",
    stroke: "none",
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.984.258.217 7.276h4.511v5.013H9.24V7.276h4.512L6.984.258Z",
    stroke: "none",
    fill: "#ccc"
  })));
};

// ---------------------------------------------------------------------------

const IconArrowUp$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 7,
    height: 10
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-arrow--up', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3.5 1V9",
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M1 3.90909L3.5 1L6 3.90909",
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

// ---------------------------------------------------------------------------

const IconAttack$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 20,
    height: 19
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-attack', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("g", {
    fillRule: "nonzero"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M16.737.609c.105.11.14.221.105.369l-1.158 4.578a.205.205 0 0 1-.105.184l-8.035 6.978.842.886a.37.37 0 0 1 0 .516 1.612 1.612 0 0 1-1.158.517c-.421 0-.807-.184-1.123-.48l-2.42 2.548a.938.938 0 0 1 .175.553c0 .259-.106.48-.246.628a.801.801 0 0 1-.596.258.826.826 0 0 1-.597-.258L.246 15.597a.902.902 0 0 1 0-1.218c.315-.296.807-.332 1.122-.037l2.421-2.547a1.746 1.746 0 0 1 .036-2.363c.105-.148.35-.148.49 0l.843.886 6.631-8.454a.76.76 0 0 1 .176-.11L16.386.497c.14-.037.246 0 .35.11zm-4.492 7.076l2.737-2.388.983-3.876-3.684 1.034-6.632 8.417 1.333 1.403 1.99-1.736.011.01 3.262-2.864zm-9.157 9.758c.035-.037.035-.074.035-.074s0-.037-.035-.074l-.21-.221-1.79-1.883-.21-.222c0-.036-.036-.036-.071-.036-.035 0-.035 0-.07.036-.035.074-.035.148 0 .185l2.175 2.289c.07.074.106.074.176 0zm.035-1.181l2.456-2.548-1.298-1.366-2.421 2.548 1.263 1.366zm3.438-2.585c.246.259.632.37.948.222l-.807-.85-1.79-1.882-.807-.849c-.105.332-.07.738.21 1.034l.211.221 1.79 1.883.245.221z",
    strokeWidth: "0.1"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.363 4.958L7.39 2.455 3.706 1.42l.983 3.876L7.306 7.58l-.444.566-2.77-2.406a.205.205 0 0 1-.105-.184L2.829.978c-.035-.148 0-.258.105-.37.105-.11.21-.147.351-.11l4.421 1.255c.07.037.14.074.176.111l1.954 2.49-.473.604zM10.376 10.258l2.313 2.017 1.333-1.403-1.768-2.244.555-.482 1.704 2.172.842-.886c.14-.148.386-.148.491 0 .632.664.632 1.698.036 2.363l2.42 2.547c.316-.295.808-.259 1.123.037a.902.902 0 0 1 0 1.218l-2.175 2.289a.826.826 0 0 1-.596.258.801.801 0 0 1-.597-.258.914.914 0 0 1-.246-.628c0-.221.07-.406.176-.553l-2.421-2.548c-.316.296-.702.48-1.123.48-.421 0-.842-.184-1.158-.517a.37.37 0 0 1 0-.516l.842-.886-2.291-1.99.54-.47zm6.207 7.185c.07.074.106.074.176 0l2.175-2.289c.035-.037.035-.11 0-.185-.035-.036-.035-.036-.07-.036s-.07 0-.07.036l-.21.222-1.79 1.883-.21.221c-.036.037-.036.074-.036.074s0 .037.035.074zm-.035-1.181l1.263-1.366-2.42-2.548-1.299 1.366 2.456 2.548zm-3.438-2.585l.245-.221 1.79-1.883.21-.221c.281-.296.316-.702.21-1.034l-.806.85-1.79 1.882-.807.849c.316.148.702.037.948-.222z",
    strokeWidth: "0.1"
  }))));
};

// ---------------------------------------------------------------------------

const IconAuthorized$1 = makeIcon('authorized', 20, 20, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  d: "m17.64 1.763-7.5-1.73a.647.647 0 0 0-.28 0l-7.5 1.73a.625.625 0 0 0-.485.612v8.875c0 5.61 6.596 8.215 7.918 8.68a.626.626 0 0 0 .414 0c1.322-.463 7.918-3.06 7.918-8.68V2.375a.625.625 0 0 0-.484-.612ZM8.787 13.42 5.366 10l.884-.884 2.464 2.465 4.968-5.795.95.812-5.846 6.821Z"
})));

// ---------------------------------------------------------------------------

const IconAuthorizedPartial$1 = makeIcon('authorized--partial', 20, 20, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  d: "m17.64 1.763-7.5-1.73a.647.647 0 0 0-.28 0l-7.5 1.73a.625.625 0 0 0-.485.612v8.875c0 5.61 6.596 8.215 7.918 8.68a.626.626 0 0 0 .414 0c1.322-.463 7.918-3.06 7.918-8.68V2.375a.625.625 0 0 0-.484-.612Z"
}), /*#__PURE__*/React.createElement("mask", {
  id: "a",
  width: "18",
  height: "20",
  x: "1",
  y: "0",
  maskUnits: "userSpaceOnUse",
  style: {
    maskType: 'alpha'
  }
}, /*#__PURE__*/React.createElement("path", {
  d: "m17.64 1.763-7.5-1.73a.647.647 0 0 0-.28 0l-7.5 1.73a.625.625 0 0 0-.485.612v8.875c0 5.61 6.596 8.215 7.918 8.68a.626.626 0 0 0 .207.036c.07 0 .207-.035.207-.035 1.322-.464 7.918-3.06 7.918-8.68V2.374a.625.625 0 0 0-.484-.612Z"
})), /*#__PURE__*/React.createElement("g", {
  mask: "url(#a)"
}, /*#__PURE__*/React.createElement("path", {
  id: "b",
  d: "M1.188-.531h8.813v21.313H1.188z"
}))));

// ---------------------------------------------------------------------------

const IconCaretThin$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 7,
    height: 4
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-caret--thin', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6 1L3.5 3 1 1",
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};
const IconCaret$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 10,
    height: 6
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-caret', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: "icon-caret-a",
    d: "M0 0l5 6 5-6z"
  })), /*#__PURE__*/React.createElement("g", {
    fillRule: "evenodd",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("mask", {
    id: "icon-caret-b",
    fill: "#fff"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#icon-caret-a"
  })), /*#__PURE__*/React.createElement("g", {
    mask: "url(#icon-caret-b)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M-7-9h24v24H-7z"
  })))));
};

/* eslint-disable no-magic-numbers */
const IconCascade$1 = makeIcon('cascade', 14, 14, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  d: "M9.84375 4.15625H1.53125V13.3438H9.84375V4.15625Z"
}), /*#__PURE__*/React.createElement("path", {
  d: "M5.03125 0.65625H13.3438V9.84375"
}), /*#__PURE__*/React.createElement("path", {
  d: "M3.28125 2.40625H11.5938V11.5938"
})));
/* eslint-enable no-magic-numbers */

// ---------------------------------------------------------------------------

const IconChainBreak$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 24,
    height: 22
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-chain-break', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M8.04 15.501H5.205c-2.494 0-4.515-2.006-4.515-4.485v-.013a4.512 4.512 0 014.515-4.502H8.04M15.96 15.501h2.835c2.494 0 4.515-2.006 4.515-4.485v-.013a4.512 4.512 0 00-4.515-4.502H15.96M9.983 5.089L8.208 1.833M12.018 4.348V.876M14.017 5.089l1.775-3.256M9.983 16.877l-1.775 3.257M12.018 17.618v3.472M14.017 16.877l1.775 3.257"
  }))));
};

/* eslint-disable no-magic-numbers */
const IconChainBreakDiag$1 = makeIcon('chain-break-diag', 16, 16, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  d: "M7.6499 3.45005L9.1024 1.99755C9.7576 1.35808 10.6369 1.00012 11.5524 1.00012C12.4679 1.00012 13.3472 1.35808 14.0024 1.99755V1.99755C14.6419 2.65275 14.9998 3.53201 14.9998 4.44755C14.9998 5.36309 14.6419 6.24235 14.0024 6.89755L12.5499 8.35005"
}), /*#__PURE__*/React.createElement("path", {
  d: "M8.34993 12.55L6.89743 14.0025C6.24223 14.642 5.36297 15 4.44743 15C3.53189 15 2.65263 14.642 1.99743 14.0025V14.0025C1.35796 13.3473 1 12.4681 1 11.5525C1 10.637 1.35796 9.75773 1.99743 9.10252L3.44993 7.65002"
}), /*#__PURE__*/React.createElement("path", {
  d: "M5.2002 10.8L5.9002 10.1"
}), /*#__PURE__*/React.createElement("path", {
  d: "M10.1001 5.89995L10.8001 5.19995"
}), /*#__PURE__*/React.createElement("path", {
  d: "M1 1L15 15"
})));
/* eslint-enable no-magic-numbers */

/* eslint-disable no-magic-numbers */
const IconChainDiag$1 = makeIcon('chain-diag', 16, 16, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  d: "M7.2998 3.79996L9.10232 1.99744C9.75753 1.35796 10.6368 1 11.5523 1C12.4679 1 13.3472 1.35796 14.0024 1.99744V1.99744C14.6419 2.65265 14.9998 3.53191 14.9998 4.44746C14.9998 5.36301 14.6419 6.24228 14.0024 6.89749L12.1999 8.70001"
}), /*#__PURE__*/React.createElement("path", {
  d: "M8.70001 12.2001L6.89749 14.0026C6.24228 14.6421 5.36301 15.0001 4.44746 15.0001C3.53191 15.0001 2.65265 14.6421 1.99744 14.0026V14.0026C1.35796 13.3474 1 12.4681 1 11.5526C1 10.637 1.35796 9.75778 1.99744 9.10257L3.79996 7.30005"
}), /*#__PURE__*/React.createElement("path", {
  d: "M5.2002 10.8L10.8003 5.19995"
})));
/* eslint-enable no-magic-numbers */

// ---------------------------------------------------------------------------

const IconCheck$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 25,
    height: 25
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-check', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("g", {
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("g", {
    id: "Confirm"
  }, /*#__PURE__*/React.createElement("polygon", {
    id: "Star",
    stroke: "#4A90E2",
    fill: "#4A90E2",
    fillRule: "nonzero",
    points: "10 17.0005011 6.17316568 19.2387953 5.04989817 14.9501018 0.76120467 13.8268343 2.99949885 10 0.76120467 6.17316568 5.04989817 5.04989817 6.17316568 0.76120467 10 2.99949885 13.8268343 0.76120467 14.9501018 5.04989817 19.2387953 6.17316568 17.0005011 10 19.2387953 13.8268343 14.9501018 14.9501018 13.8268343 19.2387953"
  }), /*#__PURE__*/React.createElement("polyline", {
    id: "Path-4",
    stroke: "#FFFFFF",
    points: "6 10.9090353 8.4568512 13.2679708 13.2439766 7"
  })))));
};

const IconCli$1 = makeIcon('cli', 25, 23, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("g", {
  fill: "none",
  fillRule: "evenodd"
}, /*#__PURE__*/React.createElement("path", {
  d: "M18.228 1.417H1.77c-.757 0-1.352.602-1.352 1.359v11.442a1.35 1.35 0 0 0 1.352 1.365h16.46c.758 0 1.354-.607 1.354-1.365V2.776c0-.757-.596-1.36-1.355-1.36zM.417 13.083h19.166",
  strokeLinecap: "round",
  strokeLinejoin: "round"
}), /*#__PURE__*/React.createElement("path", {
  d: "M5 4 L 8 7 L 5 10"
}), /*#__PURE__*/React.createElement("path", {
  d: "M10 9 H 14"
}))));

// ---------------------------------------------------------------------------

const IconClipboard$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 19,
    height: 25
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-clipboard', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    d: "M14.96 3.976H18v16.002l-4 4H1V3.976h2.92",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    clipRule: "evenodd",
    d: "M10.935 3v-.5a1.5 1.5 0 10-3 0V3h-1a1 1 0 100 2h5a1 1 0 100-2h-1z",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    d: "M5 17.977h4M5 14.977h9M5 11.977h9M5 8.978h9",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

// ---------------------------------------------------------------------------

const IconClose$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 25,
    height: 25
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-close', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M1 19.909L19.98 1",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M19.98 19.909L0.999981 1",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

// ---------------------------------------------------------------------------

const IconCloud$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 20,
    height: 12
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-cloud', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M18.467 4.984c-.83-.755-1.929-1.153-3.11-1.127-.607-1.492-2.188-2.784-4.514-2.575C9.33.11 7.384-.198 5.603.468c-1.8.67-3.084 2.196-3.466 4.103C.622 5.564-.043 7.14.399 8.727.871 10.418 2.48 11.6 4.31 11.6h11.197c2.292 0 4.228-1.773 4.228-3.873.001-1.01-.462-2.01-1.267-2.743zm-2.961 5.783H4.309c-1.71 0-2.793-1.136-3.107-2.263-.359-1.285.212-2.527 1.527-3.322a.414.414 0 0 0 .195-.287c.285-1.697 1.395-3.06 2.97-3.647A4.588 4.588 0 0 1 7.497.956c1.054 0 2.093.369 2.953 1.08a.41.41 0 0 0 .315.092c2.265-.279 3.522 1.06 3.907 2.288a.402.402 0 0 0 .437.29c1.04-.096 2.06.226 2.795.895.633.575.995 1.35.995 2.126.002 1.649-1.554 3.04-3.394 3.04z",
    fillRule: "nonzero",
    stroke: "none"
  })));
};

/* eslint-disable no-magic-numbers */
const IconComment$1 = makeIcon('comment', 16, 15, /*#__PURE__*/React.createElement("path", {
  d: "M15 1H1V10.5455H5.45455L8 13.7273L10.5455 10.5455H15V1Z"
}));
/* eslint-enable no-magic-numbers */

// ---------------------------------------------------------------------------

const IconCompany$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 17,
    height: 22
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-company', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M15.583 20.652v.89a.458.458 0 0 1-.458.458H1.375a.458.458 0 0 1-.458-.458V2.749H.458A.458.458 0 0 1 0 2.29V.458C0 .205.205 0 .458 0h15.584c.253 0 .458.205.458.458V2.29a.458.458 0 0 1-.458.459h-.459v17.903zm-.916-10.464v-7.44H1.833v18.335H5.5v-5.041c0-.254.205-.459.458-.459h4.584c.253 0 .458.205.458.459v5.041h3.667V10.188zm-8.25 10.895h3.666V16.5H6.417v4.583zM.917.917v.915h14.666V.917H.917zm5.958 8.25c.253 0 .458.204.458.458v3.666a.458.458 0 0 1-.458.458h-2.75a.458.458 0 0 1-.458-.458V9.625c0-.254.205-.459.458-.459h2.75zm-.458 3.665v-2.75H4.583v2.75h1.834zm5.958-3.666c.253 0 .458.205.458.459v3.666a.458.458 0 0 1-.458.458h-2.75a.458.458 0 0 1-.458-.458V9.625c0-.254.205-.459.458-.459h2.75zm-.458 3.666v-2.75h-1.833v2.75h1.833zM7.333 4.124v3.667a.458.458 0 0 1-.458.458h-2.75a.458.458 0 0 1-.458-.458V4.124c0-.253.205-.458.458-.458h2.75c.253 0 .458.205.458.458zm-.916 3.208v-2.75H4.583v2.75h1.834zm6.416-3.208v3.667a.458.458 0 0 1-.458.458h-2.75a.458.458 0 0 1-.458-.458V4.124c0-.253.205-.458.458-.458h2.75c.253 0 .458.205.458.458zm-.916 3.208v-2.75h-1.833v2.75h1.833z",
    fillRule: "nonzero",
    stroke: "none"
  })));
};

const IconCompass$1 = makeIcon('compass', 25, 25, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  d: "M8.75 1.25V3.43182",
  stroke: "#4D6374",
  strokeMiterlimit: "10"
}), /*#__PURE__*/React.createElement("path", {
  d: "M16.7502 9.25H14.5684",
  stroke: "#4D6374",
  strokeMiterlimit: "10"
}), /*#__PURE__*/React.createElement("path", {
  d: "M8.75 17.2501V15.0682",
  stroke: "#4D6374",
  strokeMiterlimit: "10"
}), /*#__PURE__*/React.createElement("path", {
  d: "M0.75 9.25H2.93182",
  stroke: "#4D6374",
  strokeMiterlimit: "10"
}), /*#__PURE__*/React.createElement("path", {
  d: "M8.75 17.25C13.1683 17.25 16.75 13.6683 16.75 9.25C16.75 4.83172 13.1683 1.25 8.75 1.25C4.33172 1.25 0.75 4.83172 0.75 9.25C0.75 13.6683 4.33172 17.25 8.75 17.25Z",
  stroke: "#4D6374",
  strokeMiterlimit: "10",
  strokeLinecap: "square"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12.3866 5.61365L10.2048 10.7046L5.11389 12.8864L7.29571 7.79547L12.3866 5.61365Z",
  stroke: "#4D6374",
  strokeMiterlimit: "10",
  strokeLinecap: "square"
})));

// ---------------------------------------------------------------------------

const IconConfidence$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 20
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-confidence', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M15.464 8a.4.4 0 0 0 .263-.7L8.307.82a.4.4 0 0 0-.527 0L.347 7.3a.4.4 0 0 0 .264.7h4.13l-1.49 11.548a.402.402 0 0 0 .397.452h8.778a.402.402 0 0 0 .397-.452L11.333 8h4.131zm-3.492 11.2H4.103l1.49-11.549a.4.4 0 0 0-.396-.451H1.678l6.365-5.549L14.398 7.2h-3.52a.402.402 0 0 0-.396.451l1.49 11.549z",
    fillRule: "nonzero",
    stroke: "none"
  })));
};

// ---------------------------------------------------------------------------

const IconCopyPaste$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 17,
    height: 16
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-copy-paste', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.91 1H1v8.91h8.91V1zM14.364 15h1.272v-1.273M6.09 13.727V15h1.274M11.818 15H9.91M14.364 5.454h1.272v1.273M11.818 5.454H9.91M6.09 11.182V9.909M15.636 11.182v-1.91",
    fill: "none",
    strokeLinecap: "square",
    strokeMiterlimit: "10"
  })));
};

// ---------------------------------------------------------------------------

const IconCustomer$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 20,
    height: 18
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-customer', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M5.91 5.91v11.454M1 5.91h18",
    fill: "none",
    strokeMiterlimit: "10"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M19 1H1v16.364h18V1z",
    fill: "none",
    strokeLinecap: "square",
    strokeMiterlimit: "10"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3.773 3.455a.318.318 0 11-.637 0 .318.318 0 01.637 0zM6.227 3.455a.318.318 0 11-.636 0 .318.318 0 01.636 0z"
  })));
};

// ---------------------------------------------------------------------------

const IconDashboard$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 20,
    height: 17
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-dashboard', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("g", {
    fillRule: "nonzero",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M19.788 9.165c-.416-4.898-4.354-8.823-9.161-9.13C7.92-.137 5.339.796 3.352 2.66 1.33 4.558.17 7.255.17 10.06c0 1.885.515 3.723 1.488 5.314a2.661 2.661 0 0 0 2.261 1.258h12.157c.919 0 1.785-.48 2.258-1.253a10.16 10.16 0 0 0 1.454-6.214zm-2.165 5.778a1.82 1.82 0 0 1-1.546.855H3.92c-.632 0-1.226-.33-1.55-.859a9.347 9.347 0 0 1-1.366-4.88c0-2.575 1.064-5.05 2.92-6.79a8.808 8.808 0 0 1 6.65-2.4c4.399.28 8.004 3.877 8.384 8.368a9.34 9.34 0 0 1-1.335 5.706z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M17.383 9.948h-.729a.417.417 0 0 0 0 .834h.729a.417.417 0 0 0 0-.834zM15.108 4.545l-.548.56a.417.417 0 0 0 .596.584l.548-.56a.417.417 0 0 0-.596-.584zM10.012 3.592c.23 0 .416-.186.416-.416v-.714a.417.417 0 0 0-.833 0v.714c0 .23.186.416.417.416zM4.775 4.554a.416.416 0 1 0-.595.583l.54.551a.416.416 0 0 0 .588.006.417.417 0 0 0 .007-.589l-.54-.55zM3.388 9.95h-.76a.417.417 0 0 0 0 .833h.76a.417.417 0 0 0 0-.834zM14.5 7.133l-3.565 3.645a1.735 1.735 0 0 0-.923-.269h-.001c-.477 0-.925.19-1.262.534a1.838 1.838 0 0 0 0 2.555c.337.344.784.534 1.262.534.477 0 .925-.19 1.26-.534.591-.604.671-1.53.25-2.227l3.573-3.655a.417.417 0 0 0-.595-.583zm-3.823 5.882a.947.947 0 0 1-1.332 0 1 1 0 0 1 0-1.39.924.924 0 0 1 1.33 0 .996.996 0 0 1 .002 1.39z"
  }))));
};

// ---------------------------------------------------------------------------

const IconDate$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 20,
    height: 20
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-date', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M17.583 0H2.417C1.107 0 0 1.107 0 2.417v15.166C0 18.893 1.107 20 2.417 20h15.166C18.893 20 20 18.893 20 17.584V2.417C20 1.107 18.893 0 17.583 0zM2.417.833h15.166c.859 0 1.584.725 1.584 1.584V5H.833V2.417c0-.859.725-1.584 1.584-1.584zm15.166 18.334H2.417a1.604 1.604 0 0 1-1.584-1.584V5.833h18.334v11.75c0 .859-.725 1.584-1.584 1.584zM6.25 3.417a.416.416 0 1 1 0-.834h.083a.417.417 0 0 1 0 .834H6.25zm7.5 0c-.23 0-.458-.187-.458-.417 0-.23.145-.417.375-.417h.083a.417.417 0 0 1 0 .834zm-.148 4.943l-1.75.667a.416.416 0 1 0 .296.779l1.185-.452v6.813a.417.417 0 0 0 .834 0V8.75a.417.417 0 0 0-.565-.39zm-5.665 3.585l2.386-2.933A.417.417 0 0 0 10 8.333H5.583a.417.417 0 0 0 0 .834h3.54L6.76 12.07a.416.416 0 0 0-.046.456.397.397 0 0 0 .401.222c.418-.027 1.615.015 2.172.607.269.284.367.668.3 1.173-.121.907-.746 1.211-1.249 1.306-1.02.194-2.316-.3-2.725-1.037a.416.416 0 1 0-.728.406c.496.893 1.77 1.508 2.988 1.508.21-.001.418-.018.62-.057 1.077-.204 1.775-.938 1.92-2.014.101-.76-.074-1.385-.52-1.857-.53-.562-1.336-.767-1.956-.838z",
    fillRule: "nonzero",
    stroke: "none"
  })));
};

// ---------------------------------------------------------------------------

const IconDeny$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 14,
    height: 14
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-deny', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M11.9637 2.03638L2.03638 11.9637",
    strokeMiterlimit: "10"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M13.5 7C13.5 10.5899 10.5899 13.5 7 13.5C3.41015 13.5 0.5 10.5899 0.5 7C0.5 3.41015 3.41015 0.5 7 0.5C10.5899 0.5 13.5 3.41015 13.5 7Z",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  })));
};

// ---------------------------------------------------------------------------

/* eslint-disable no-magic-numbers */
const IconDetails$1 = makeIcon('details', 16, 16, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  d: "M0.606567 1L5.99118 6.38462",
  strokeMiterlimit: "10",
  strokeLinecap: "round",
  strokeLinejoin: "round"
}), /*#__PURE__*/React.createElement("path", {
  d: "M9.2218 9.61523L14.6064 14.9999",
  strokeMiterlimit: "10",
  strokeLinecap: "round",
  strokeLinejoin: "round"
}), /*#__PURE__*/React.createElement("path", {
  d: "M7.06811 1H0.606567V7.46154",
  strokeMiterlimit: "10",
  strokeLinecap: "round",
  strokeLinejoin: "round"
}), /*#__PURE__*/React.createElement("path", {
  d: "M14.6067 8.53857V15.0001H8.14514",
  strokeMiterlimit: "10",
  strokeLinecap: "round",
  strokeLinejoin: "round"
})));
/* eslint-enable no-magic-numbers */

// ---------------------------------------------------------------------------

/* eslint-disable no-magic-numbers */
const IconDockBottom$1 = makeIcon('dock-bottom', 18, 16, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  strokeLinecap: "round",
  strokeLinejoin: "round",
  strokeMiterlimit: "10",
  d: "M17.01.933H.858v14H17.01v-14Z"
}), /*#__PURE__*/React.createElement("path", {
  strokeLinecap: "round",
  strokeLinejoin: "round",
  strokeMiterlimit: "10",
  d: "M14.857 9.549H3.01v3.23h11.846V9.55Z"
})));
/* eslint-enable no-magic-numbers */

// ---------------------------------------------------------------------------

/* eslint-disable no-magic-numbers */
const IconDockRight$1 = makeIcon('dock-right', 18, 16, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  strokeLinecap: "round",
  strokeLinejoin: "round",
  strokeMiterlimit: "10",
  d: "M17.51.933H1.358v14H17.51v-14Z"
}), /*#__PURE__*/React.createElement("path", {
  strokeLinecap: "round",
  strokeLinejoin: "round",
  strokeMiterlimit: "10",
  d: "M15.3 13.087V2.875h-3.232v10.212H15.3Z"
})));
/* eslint-enable no-magic-numbers */

// ---------------------------------------------------------------------------

const IconDomains$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 20,
    height: 14
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-domains', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M2.137.33h15.726c.743 0 1.013.077 1.284.223.272.145.485.358.63.63.146.271.223.54.223 1.284v8.726c0 .743-.077 1.013-.223 1.284a1.514 1.514 0 0 1-.63.63c-.271.146-.54.223-1.284.223H2.137c-.743 0-1.013-.077-1.284-.223a1.514 1.514 0 0 1-.63-.63C.077 12.206 0 11.937 0 11.193V2.467c0-.743.077-1.013.223-1.284C.368.91.58.698.853.553 1.124.407 1.393.33 2.137.33zm0 .833c-.552 0-.72.033-.891.124a.681.681 0 0 0-.289.289c-.091.171-.124.339-.124.89v8.727c0 .552.033.72.124.891.068.127.162.221.289.289.171.091.339.124.89.124h15.727c.552 0 .72-.033.891-.124a.681.681 0 0 0 .289-.289c.091-.171.124-.339.124-.89V2.466c0-.552-.033-.72-.124-.891a.681.681 0 0 0-.289-.289c-.171-.091-.339-.124-.89-.124H2.136zM16 8.413a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1zM5.154 8.152a.417.417 0 0 1-.784.002l-.762-2.098a.417.417 0 0 1 .784-.285l.367 1.012.362-1.01a.417.417 0 0 1 .784-.002l.367 1.011.36-1.009a.417.417 0 1 1 .785.28l-.75 2.099a.417.417 0 0 1-.784.002l-.367-1.012-.362 1.01zm4 0a.417.417 0 0 1-.784.002l-.762-2.098a.417.417 0 1 1 .784-.285l.367 1.012.362-1.01a.417.417 0 0 1 .784-.002l.367 1.011.36-1.009a.417.417 0 1 1 .785.28l-.75 2.099a.417.417 0 0 1-.784.002l-.367-1.012-.362 1.01zm4.001 0a.417.417 0 0 1-.784.002l-.763-2.098a.417.417 0 1 1 .784-.285l.368 1.012.36-1.01a.417.417 0 0 1 .785-.002l.367 1.012.362-1.01a.417.417 0 0 1 .784.28l-.75 2.099a.417.417 0 0 1-.785.002l-.367-1.012-.36 1.01z",
    fillRule: "evenodd",
    stroke: "none"
  })));
};

// ---------------------------------------------------------------------------

const IconDrawer$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 16
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-drawer', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M15.5 0.5H6.5V3.5H15.5V0.5Z",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M15.5 6.5H6.5V9.5H15.5V6.5Z",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M15.5 12.5H6.5V15.5H15.5V12.5Z",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3.5 5L0.5 8L3.5 11",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

// ---------------------------------------------------------------------------

const IconEdit$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 14,
    height: 14
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-edit', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M11.2 5.2L8.8 2.8",
    fill: "none",
    strokeMiterlimit: "10"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4 12.4L1 13l.6-3 9-9L13 3.4l-9 9z",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  })));
};

/* eslint-disable no-magic-numbers */
const IconExternalLink$1 = makeIcon('external-link', 12, 11, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  d: "M11.0001 0.5L5.66675 5.83333"
}), /*#__PURE__*/React.createElement("path", {
  d: "M6.33325 0.5H10.9999V5.16667"
}), /*#__PURE__*/React.createElement("path", {
  d: "M3.66667 0.5H1.66667C1.29867 0.5 1 0.798667 1 1.16667V9.83333C1 10.2013 1.29867 10.5 1.66667 10.5H10.3333C10.7013 10.5 11 10.2013 11 9.83333V7.83333"
})));
/* eslint-enable no-magic-numbers */

// ---------------------------------------------------------------------------

const IconEye$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 24,
    height: 24
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-eye', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: "icon-eye",
    d: "M12 18c-4.418 0-8-5.12-8-6 0-.88 3.582-6 8-6s8 5.073 8 6c0 .927-3.582 6-8 6zm0-2a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0-2a2 2 0 1 1 0-4 2 2 0 0 1 0 4z"
  })), /*#__PURE__*/React.createElement("g", {
    fillRule: "evenodd",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("mask", {
    id: "icon-eye-mask",
    fill: "#fff"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#eye"
  })), /*#__PURE__*/React.createElement("use", {
    fill: "#444",
    fillRule: "nonzero",
    xlinkHref: "#icon-eye",
    stroke: "none"
  }), /*#__PURE__*/React.createElement("g", {
    mask: "url(#icon-eye-mask)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M0 0h24v24H0z"
  })))));
};

// ---------------------------------------------------------------------------

const IconFailure$1 = props => {
  if (props.modifier === 'inverted') {
    console.warn('IconFailure cannot be inverted');
  }
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 22,
    height: 22
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-failure', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: "icon-failure",
    d: "M14.025 3.975c-.3-.3-.75-.3-1.05 0L9 7.95 5.025 3.975c-.3-.3-.75-.3-1.05 0-.3.3-.3.75 0 1.05L7.95 9l-3.975 3.975c-.3.3-.3.75 0 1.05.15.15.3.225.525.225a.68.68 0 0 0 .525-.225L9 10.05l3.975 3.975a.81.81 0 0 0 .525.225.81.81 0 0 0 .525-.225c.3-.3.3-.75 0-1.05L10.05 9l3.975-3.975c.3-.3.3-.75 0-1.05z"
  })), /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "11",
    cy: "11",
    r: "11",
    fill: "#DA021B",
    stroke: "none"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(2 2)"
  }, /*#__PURE__*/React.createElement("mask", {
    id: "icon-failure-b",
    fill: "#fff",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#icon-failure"
  })), /*#__PURE__*/React.createElement("g", {
    fill: "#fff",
    mask: "url(#icon-failure-b)",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M0 0h18v18H0z"
  }))))));
};

// ---------------------------------------------------------------------------

const IconFilter$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 16
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-filter', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M1 1H15L9.90909 8.63636V15H6.09091V8.63636L1 1Z",
    stroke: "#4D6374",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

// ---------------------------------------------------------------------------

const IconFilterDismiss$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 12,
    height: 12
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-filter-dismiss', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8 4L4 8M8 8L4 4",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "6",
    cy: "6",
    r: "5.5",
    fill: "none"
  })));
};

// ---------------------------------------------------------------------------

const IconGrabHandle$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 16
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-grab-handle', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M0.999956 8.00002C0.999956 8.70293 1.56977 9.27275 2.27268 9.27275C2.97559 9.27275 3.54541 8.70293 3.54541 8.00002C3.54541 7.29711 2.97559 6.72729 2.27268 6.72729C1.56977 6.72729 0.999956 7.29711 0.999956 8.00002Z",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M0.999956 2.27273C0.999956 2.97564 1.56977 3.54545 2.27268 3.54545C2.97559 3.54545 3.54541 2.97564 3.54541 2.27273C3.54541 1.56982 2.97559 1 2.27268 1C1.56977 1 0.999956 1.56982 0.999956 2.27273Z",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M0.999956 13.7273C0.999956 14.4302 1.56977 15 2.27268 15C2.97559 15 3.54541 14.4302 3.54541 13.7273C3.54541 13.0244 2.97559 12.4546 2.27268 12.4546C1.56977 12.4546 0.999956 13.0244 0.999956 13.7273Z",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.99996 8.00002C6.99996 8.70293 7.56977 9.27275 8.27268 9.27275C8.97559 9.27275 9.54541 8.70293 9.54541 8.00002C9.54541 7.29711 8.97559 6.72729 8.27268 6.72729C7.56977 6.72729 6.99996 7.29711 6.99996 8.00002Z",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.99996 2.27273C6.99996 2.97564 7.56977 3.54545 8.27268 3.54545C8.97559 3.54545 9.54541 2.97564 9.54541 2.27273C9.54541 1.56982 8.97559 1 8.27268 1C7.56977 1 6.99996 1.56982 6.99996 2.27273Z",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.99996 13.7273C6.99996 14.4302 7.56977 15 8.27268 15C8.97559 15 9.54541 14.4302 9.54541 13.7273C9.54541 13.0244 8.97559 12.4546 8.27268 12.4546C7.56977 12.4546 6.99996 13.0244 6.99996 13.7273Z",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  })));
};

// ---------------------------------------------------------------------------

const IconHelp$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 18,
    height: 18
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-help', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.25 17.5a8.25 8.25 0 100-16.5 8.25 8.25 0 000 16.5z",
    fill: "none",
    strokeLinecap: "square",
    strokeMiterlimit: "10"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.25 14.125a.375.375 0 100-.75.375.375 0 000 .75z",
    stroke: "none"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.64 5.17c1.485-.656 3.406-.578 4.062.484.657 1.062.204 2.297-.921 3.25-1.125.953-1.531 1.47-1.531 2.22",
    fill: "none",
    strokeLinecap: "square",
    strokeMiterlimit: "10"
  })));
};

// ---------------------------------------------------------------------------

const IconHoc$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 19,
    height: 20
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-hoc', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.612 12.454a2.454 2.454 0 100-4.908 2.454 2.454 0 000 4.908z",
    fill: "none",
    strokeLinecap: "square",
    strokeMiterlimit: "10"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M16.158 10a6.58 6.58 0 00-.154-1.402l2.22-1.68-1.636-2.835-2.572 1.085a6.535 6.535 0 00-2.422-1.407L11.249 1H7.976L7.63 3.761c-.9.289-1.727.768-2.423 1.407L2.636 4.083 1 6.917l2.22 1.681a6.463 6.463 0 000 2.803L1 13.084l1.636 2.834 2.572-1.085A6.534 6.534 0 007.63 16.24L7.976 19h3.272l.346-2.761a6.534 6.534 0 002.423-1.407l2.571 1.085 1.636-2.834-2.22-1.681c.101-.46.153-.93.154-1.402v0z",
    fill: "none",
    strokeLinecap: "square",
    strokeMiterlimit: "10"
  })));
};

// ---------------------------------------------------------------------------

const IconImage$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 49,
    height: 36
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-image', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd",
    stroke: "#AAACAE",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "2"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M47.973 2.588c0-.876-.71-1.588-1.584-1.588H3.557c-.874 0-1.584.712-1.584 1.588v30.826c0 .876.71 1.586 1.584 1.586h42.832c.874 0 1.584-.71 1.584-1.586V2.588z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M13.9 17.209a4.172 4.172 0 1 0-.003-8.343 4.172 4.172 0 0 0 .003 8.343zM11.946 35l13.818-19.464 5.714 7.708 7.346-11.554 9.148 13.302"
  }))));
};

// ---------------------------------------------------------------------------

const IconImpact$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 13
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-impact', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.90912 12.4546H15",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.90912 8H15",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.90912 3.54544H15",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.36364 3.54544H5.45455C2.99436 3.54544 1 5.5398 1 7.99999C1 10.4602 2.99436 12.4545 5.45455 12.4545H7.36364",
    fill: "none",
    strokeMiterlimit: "10"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4.81818 6.09091L7.36363 3.54545L4.81818 1",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  })));
};

// ---------------------------------------------------------------------------

const IconInfo$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 24,
    height: 24
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-info', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    stroke: "none",
    fill: "#dee2e9",
    d: "M12 4a8 8 0 1 0 0 16 8 8 0 0 0 0-16zm-.04 12.474s-.371.247-.625.245l-.036.017v-.017a.806.806 0 0 1-.16-.023l-.08-.02c-.443-.11-.678-.55-.568-.992l.617-2.487.278-1.123c.144-.579-.124-.447-.43-.297-.245.12-.514.252-.612.038-.14-.303.753-.924 1.46-1.416l.1-.07s.372-.246.626-.245l.036-.017-.001.017c.053.002.107.01.161.023l.08.02c.443.11.713.558.603 1l-.616 2.488-.28 1.123c-.143.578.12.446.42.295.24-.121.507-.255.606-.04.135.292-.712.88-1.41 1.363l-.169.118zm.162-6.929a1.157 1.157 0 1 0 .557-2.246 1.157 1.157 0 0 0-.557 2.246z"
  })));
};

// ---------------------------------------------------------------------------

const IconIPs$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 20,
    height: 22
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-ips', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M6.925 17.81l-.4 2.47c0 .18.18.327.4.327h6.148c.22 0 .4-.146.4-.326l-.4-2.47M18.228 1.417H1.77c-.757 0-1.352.602-1.352 1.359v11.442a1.35 1.35 0 0 0 1.352 1.365h16.46c.758 0 1.354-.607 1.354-1.365V2.776c0-.757-.596-1.36-1.355-1.36zM.417 13.083h19.166",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.301 9.637h-.556V5.969L4.6 6.405v-.508l1.656-.637h.045v4.377zm4.67 0H8.104v-.4l1.433-1.593c.129-.142.236-.269.322-.379.086-.11.155-.212.208-.305a.999.999 0 0 0 .144-.522.876.876 0 0 0-.053-.305.708.708 0 0 0-.397-.415.808.808 0 0 0-.323-.061c-.146 0-.273.02-.38.063a.71.71 0 0 0-.268.179.738.738 0 0 0-.159.28c-.035.111-.052.234-.052.37h-.56c0-.184.033-.358.097-.52a1.25 1.25 0 0 1 .278-.43c.12-.121.27-.219.445-.29.175-.073.375-.109.6-.109.206 0 .392.03.557.092.165.06.305.145.42.254.113.108.201.236.262.384a1.258 1.258 0 0 1 .026.87 1.984 1.984 0 0 1-.18.378 3.188 3.188 0 0 1-.26.368 8.53 8.53 0 0 1-.315.362L8.774 9.183h2.197v.454zm1.754-2.456h.397c.14 0 .264-.02.371-.057a.794.794 0 0 0 .27-.157.651.651 0 0 0 .168-.237.788.788 0 0 0 .057-.304c0-.254-.065-.446-.196-.575-.13-.13-.32-.194-.57-.194a.946.946 0 0 0-.324.052.687.687 0 0 0-.41.39.804.804 0 0 0-.057.31h-.56a1.15 1.15 0 0 1 .373-.856c.119-.11.26-.195.426-.258.165-.063.349-.095.551-.095.197 0 .376.027.538.08.163.053.302.131.418.236.116.104.207.233.27.387.065.155.097.333.097.535a1.015 1.015 0 0 1-.16.525 1.131 1.131 0 0 1-.513.428c.142.046.26.107.356.18a.954.954 0 0 1 .349.542c.023.102.034.203.034.303 0 .205-.035.386-.105.544-.07.159-.167.293-.292.402-.124.109-.27.192-.44.25-.17.056-.352.085-.549.085-.192 0-.373-.027-.542-.081a1.286 1.286 0 0 1-.44-.238 1.146 1.146 0 0 1-.297-.381 1.16 1.16 0 0 1-.11-.515h.557c0 .115.02.219.058.313a.67.67 0 0 0 .167.24.75.75 0 0 0 .263.154c.103.036.218.054.344.054.127 0 .24-.017.343-.05a.68.68 0 0 0 .262-.151.68.68 0 0 0 .166-.254.996.996 0 0 0 .059-.36.848.848 0 0 0-.068-.353.675.675 0 0 0-.189-.248.799.799 0 0 0-.295-.147 1.416 1.416 0 0 0-.38-.048h-.397V7.18z",
    fill: "#fff"
  }))));
};

// ---------------------------------------------------------------------------

const IconLink$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 14,
    height: 16
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-link', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M5.31198 6.7634L8.82225 4.57007",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M5.31198 9.23669L8.82225 11.43",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M10.8 5.66667C12.0887 5.66667 13.1333 4.622 13.1333 3.33333C13.1333 2.04467 12.0887 1 10.8 1C9.51134 1 8.46667 2.04467 8.46667 3.33333C8.46667 4.622 9.51134 5.66667 10.8 5.66667Z",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M10.8 15C12.0887 15 13.1333 13.9554 13.1333 12.6667C13.1333 11.378 12.0887 10.3334 10.8 10.3334C9.51134 10.3334 8.46667 11.378 8.46667 12.6667C8.46667 13.9554 9.51134 15 10.8 15Z",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3.33333 10.3333C4.622 10.3333 5.66667 9.28862 5.66667 7.99996C5.66667 6.71129 4.622 5.66663 3.33333 5.66663C2.04467 5.66663 1 6.71129 1 7.99996C1 9.28862 2.04467 10.3333 3.33333 10.3333Z",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

// ---------------------------------------------------------------------------

const IconList$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 18,
    height: 17
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-list', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    stroke: "none",
    d: "M2.5 0h-2a.5.5 0 0 0-.5.5v2a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5zM2 2H1V1h1v1zm.5 5.001h-2a.5.5 0 0 0-.5.5v2a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5zm-.5 2H1v-1h1v1zm.5 5.001h-2a.5.5 0 0 0-.5.5v2a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5zm-.5 2H1v-1h1v1zM17.44 15H5.509a.5.5 0 0 0 0 1H17.44a.5.5 0 0 0 0-1zm0-7H5.509a.5.5 0 0 0 0 1H17.44a.5.5 0 0 0 0-1zM5.509 2H17.44a.5.5 0 0 0 0-1H5.509a.5.5 0 0 0 0 1z"
  })));
};

/* eslint-disable no-magic-numbers */
const IconLock$1 = makeIcon('lock', 16, 20, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  d: "M15.1387 10.139H1.13867V18.889H15.1387V10.139Z"
}), /*#__PURE__*/React.createElement("path", {
  d: "M4.63867 10.1389V4.88892C4.64144 3.96151 5.01108 3.07288 5.66686 2.4171C6.32264 1.76132 7.21127 1.39169 8.13867 1.38892V1.38892C9.06608 1.39169 9.95471 1.76132 10.6105 2.4171C11.2663 3.07288 11.6359 3.96151 11.6387 4.88892V10.1389"
})));
/* eslint-enable no-magic-numbers */

// ---------------------------------------------------------------------------

const IconLockAccount$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 15
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-lock-account', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M14.6078 6.97583H3.85181V14.1465H14.6078V6.97583Z",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.22978 11.7562C9.88983 11.7562 10.4249 11.2211 10.4249 10.5611C10.4249 9.90104 9.88983 9.36597 9.22978 9.36597C8.56974 9.36597 8.03467 9.90104 8.03467 10.5611C8.03467 11.2211 8.56974 11.7562 9.22978 11.7562Z",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.58348 6.97572V3.98793C6.59129 3.20348 6.28721 2.44805 5.73813 1.88775C5.18904 1.32746 4.4399 1.00819 3.65545 1.00015H3.5957C2.81125 0.992341 2.05581 1.29642 1.49552 1.8455C0.935225 2.39459 0.615952 3.14373 0.60791 3.92818V6.97572",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  })));
};

// ---------------------------------------------------------------------------

const IconLogout$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 20,
    height: 16
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-logout', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.229 11.286c1.2 2.057 3.428 3.428 5.914 3.428 3.771 0 6.857-3.085 6.857-6.857C19 4.086 15.914 1 12.143 1 9.57 1 7.429 2.371 6.229 4.429M13 7.857H1",
    fill: "none",
    strokeMiterlimit: "10"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3.571 10.429L1 7.857l2.571-2.571",
    fill: "none",
    strokeLinecap: "square",
    strokeMiterlimit: "10"
  })));
};

// ---------------------------------------------------------------------------

const IconMap$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 18,
    height: 18
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-map', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    stroke: "none",
    d: "M8.99.203C4.117.203.155 4.165.155 9.036c0 4.87 3.963 8.833 8.833 8.833 4.871 0 8.834-3.962 8.834-8.833S13.86.203 8.989.203zm0 16.916c-4.458 0-8.084-3.626-8.084-8.083C.906 4.579 4.532.953 8.989.953c4.458 0 8.084 3.626 8.084 8.083 0 4.457-3.626 8.083-8.084 8.083zm6.845-11.166a.375.375 0 0 0-.354.394c.017.319-.336.692-.995 1.048-1.005.545-.849.988-.472 2.06.284.811-1.284 3.438-1.98 3.989-.09-.19-.17-.663-.177-1.079 0-1.469 0-3.688-1.953-4.433-.548-.21-1.294-.479-1.734-.575-.03-.006-.107-.023-.129-.2-.033-.29.14-.702.45-.85.173-.083.29-.069.412-.055.128.015.343.04.516-.127.203-.197.2-.49.146-1.11-.041-.478-.204-.835-.481-1.06-.434-.353-1.01-.283-1.429-.233-.115.014-.223.028-.313.028-.833 0-.967.625-1.258 1.993l-.055.268-.007.03a.71.71 0 0 1-.064-.088c-.055-.144-.132-.903-.17-1.523a.374.374 0 0 0-.7-.162l-.595 1.04c-.019.034-.023.036-.031.073-.001.003-.125.271-.869.533-.094.034-.147.039-.16.047-.084-.2.1-.986.391-1.656a.375.375 0 0 0-.688-.3c-.245.563-.76 1.93-.248 2.478.151.163.441.32.956.139.918-.325 1.211-.723 1.305-.937.043.316.093.524.147.62.063.11.402.661.884.583.293-.046.49-.288.582-.72l.057-.268c.276-1.295.332-1.399.525-1.399.115 0 .253-.015.402-.033.319-.038.68-.081.866.07.114.092.183.275.207.543.016.192.023.324.026.414a1.36 1.36 0 0 0-.678.138c-.651.311-.934 1.064-.87 1.613.052.444.32.76.715.846.308.066.885.26 1.625.542 1.36.518 1.47 1.997 1.47 3.738.007.447.08 1.516.605 1.794a.615.615 0 0 0 .289.072.726.726 0 0 0 .4-.13c.696-.453 2.797-3.546 2.32-4.9-.32-.913-.32-.913.123-1.153.955-.517 1.421-1.106 1.386-1.748-.011-.207-.162-.372-.395-.354zM4.828 10.046c-.253-.096-.6-.228-.696-.324a3.018 3.018 0 0 0-.674-.513A3.851 3.851 0 0 1 3 8.9a.511.511 0 0 0-.533-.08c-.142.061-.575.247-.758 2.324a.375.375 0 0 0 .747.066c.055-.619.173-1.26.282-1.57.126.088.234.153.336.215.18.107.322.192.529.399.209.209.573.348.958.495.165.063.41.156.563.236l-.09.078c-.541.46-.833.774-1.55 2.88a.375.375 0 1 0 .709.241c.683-2.002.912-2.197 1.327-2.55.063-.054.131-.111.204-.177.244-.22.263-.441.235-.588-.077-.42-.565-.607-1.13-.822z"
  })));
};

// ---------------------------------------------------------------------------

const IconMark$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 12
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-mark', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    stroke: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M1 8.333 3.667 11l8-10M15 4.333h-2M15 7.667h-4.667M15 11H7.667",
    fill: "none",
    strokeLinecap: "square",
    strokeMiterlimit: "10",
    stroke: "#4D6374"
  })));
};

// ---------------------------------------------------------------------------

const IconMenu$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 25,
    height: 25
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-menu', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M17.0029 11H1.00293",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M17.001 6.00098H1",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M17.001 1.00098H1",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

// ---------------------------------------------------------------------------

const IconMinus$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 8,
    height: 2
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-minus', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8 0H0V2H8V0Z"
  })));
};

// ---------------------------------------------------------------------------

const IconMore$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 5,
    height: 16
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-more', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M1.00002 8C1.00002 8.70291 1.56984 9.27273 2.27274 9.27273C2.97565 9.27273 3.54547 8.70291 3.54547 8C3.54547 7.29709 2.97565 6.72727 2.27274 6.72727C1.56984 6.72727 1.00002 7.29709 1.00002 8Z",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M1.00002 2.27273C1.00002 2.97564 1.56984 3.54545 2.27274 3.54545C2.97565 3.54545 3.54547 2.97564 3.54547 2.27273C3.54547 1.56982 2.97565 1 2.27274 1C1.56984 1 1.00002 1.56982 1.00002 2.27273Z",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M1.00002 13.7273C1.00002 14.4302 1.56984 15 2.27274 15C2.97565 15 3.54547 14.4302 3.54547 13.7273C3.54547 13.0244 2.97565 12.4545 2.27274 12.4545C1.56984 12.4545 1.00002 13.0244 1.00002 13.7273Z",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  })));
};

// ---------------------------------------------------------------------------

const IconNavCaret$1 = props => {
  const {
    addlClasses,
    aria,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 9,
    height: 5
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-nav-caret', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8.5 1l-4 3.2L.5 1",
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

// ---------------------------------------------------------------------------

const IconNavCollapse$1 = props => {
  const {
    addlClasses,
    aria,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 9,
    height: 3
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-nav-collapse', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8.5.5h-8v2h8v-2z",
    stroke: "none"
  })));
};

// ---------------------------------------------------------------------------

const IconNavExpand$1 = props => {
  const {
    addlClasses,
    aria,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 9,
    height: 8
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-nav-expand', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M5.5 5v3h-2V5h-3V3h3V0h2v3h3v2h-3z",
    stroke: "none"
  })));
};

// ---------------------------------------------------------------------------

const IconNetworks$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 20,
    height: 20
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-networks', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M17.917 11.667c1.149 0 2.083-.935 2.083-2.084A2.086 2.086 0 0 0 17.917 7.5a2.086 2.086 0 0 0-2.042 1.667H7.157c1.642-.717 3.607-2.046 5.267-4.543 1.408-2.118 2.687-2.086 3.478-2.036a2.082 2.082 0 0 0 2.015 1.579c1.149 0 2.083-.935 2.083-2.084A2.086 2.086 0 0 0 17.917 0c-1.036 0-1.89.761-2.05 1.752-.924-.051-2.517-.028-4.137 2.41-2.747 4.135-6.458 4.852-7.614 4.976A2.085 2.085 0 0 0 2.083 7.5 2.086 2.086 0 0 0 0 9.583c0 1.149.935 2.084 2.083 2.084.996 0 1.83-.704 2.034-1.64 1.153.121 4.855.828 7.613 4.977 1.425 2.144 2.827 2.423 3.774 2.423.134 0 .248-.008.363-.015a2.08 2.08 0 0 0 2.05 1.755c1.149 0 2.083-.935 2.083-2.084S19.066 15 17.917 15c-.974 0-1.787.674-2.014 1.578-.788.047-2.07.086-3.48-2.035C10.765 12.046 8.8 10.716 7.157 10h8.72a2.086 2.086 0 0 0 2.04 1.667zm0-10.834c.689 0 1.25.561 1.25 1.25 0 .69-.561 1.25-1.25 1.25-.69 0-1.25-.56-1.25-1.25 0-.689.56-1.25 1.25-1.25zm0 15c.689 0 1.25.561 1.25 1.25 0 .69-.561 1.25-1.25 1.25-.69 0-1.25-.56-1.25-1.25 0-.689.56-1.25 1.25-1.25zm0-7.5c.689 0 1.25.561 1.25 1.25 0 .69-.561 1.25-1.25 1.25-.69 0-1.25-.56-1.25-1.25 0-.689.56-1.25 1.25-1.25zm-15.834 2.5c-.689 0-1.25-.56-1.25-1.25 0-.689.561-1.25 1.25-1.25.69 0 1.25.561 1.25 1.25 0 .69-.56 1.25-1.25 1.25z",
    stroke: "none",
    fillRule: "nonzero"
  })));
};

// ---------------------------------------------------------------------------

const IconNo$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 24,
    height: 24
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-no', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M19.998 3.987C15.587-.423 8.41-.423 4 3.987-.411 8.398-.411 15.574 4 19.985c4.41 4.411 11.588 4.412 15.999.001 4.41-4.41 4.41-11.588-.001-15.999zM4 19.985L19.997 3.987"
  }))));
};

// ---------------------------------------------------------------------------

const IconNote$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 14,
    height: 16
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-note', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    stroke: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3.5 11.5h7M3.5 8.5h7M3.5 5.5h2M8.5.5h-8v15h13v-10l-5-5z",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8.5.5v5h5",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

// ---------------------------------------------------------------------------

const IconNotifications$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 18,
    height: 22
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-notifications', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("g", {
    fillRule: "nonzero",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M16.903 12.924a2.975 2.975 0 0 0-.115-.204 11.409 11.409 0 0 1-1.661-6.52A5.341 5.341 0 0 0 12.054.911 7.112 7.112 0 0 0 5.692.905a5.34 5.34 0 0 0-3.087 5.287 11.411 11.411 0 0 1-1.659 6.525 2.644 2.644 0 0 0-.335 2.008 2.643 2.643 0 0 0 1.4 1.779 15.682 15.682 0 0 0 6.854 1.584c2.34 0 4.68-.529 6.862-1.587a2.667 2.667 0 0 0 1.176-3.577zm-1.567 2.786a14.742 14.742 0 0 1-12.934.003 1.773 1.773 0 0 1-.931-1.187 1.768 1.768 0 0 1 .226-1.346 12.307 12.307 0 0 0 1.788-7.047A4.468 4.468 0 0 1 6.075 1.7a6.251 6.251 0 0 1 5.597.006 4.461 4.461 0 0 1 2.575 4.427l-.001.016a12.293 12.293 0 0 0 1.792 7.036 1.781 1.781 0 0 1-.702 2.525zM10.927 18.61a.439.439 0 0 0-.507.364c-.119.724-.792 1.234-1.596 1.164a1.397 1.397 0 0 1-1.512-1.164.44.44 0 1 0-.871.143c.193 1.18 1.274 2.01 2.383 1.9a2.284 2.284 0 0 0 2.467-1.9.44.44 0 0 0-.364-.507z"
  }))));
};

// ---------------------------------------------------------------------------

const IconPencil$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 14,
    height: 14
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-pencil', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M11.1998 5.20005L8.7998 2.80005",
    strokeMiterlimit: "10"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4 12.4L1 13L1.6 10L10.6 1L13 3.4L4 12.4Z",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  })));
};

// ---------------------------------------------------------------------------

const IconPeople$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 20,
    height: 15
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-people', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("g", {
    fillRule: "nonzero",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M13.585 11.51c-3.66-1.447-4.334-2.06-4.453-2.25v-.438c.415-.477.74-1.091.95-1.793a1.54 1.54 0 0 0 .262-1.988V3.637c0-1.964-1.187-3.09-3.257-3.09-2.04 0-3.258 1.155-3.258 3.09v1.405a1.545 1.545 0 0 0 .26 1.988c.21.7.535 1.315.95 1.792v.436c-.111.171-.756.791-4.455 2.254a.9.9 0 0 0-.566.844v1.718c0 .5.399.906.888.906h12.357c.49 0 .888-.406.888-.906v-1.718a.909.909 0 0 0-.566-.846zm-.267 2.563c0 .043-.028.072-.054.072H.907c-.027 0-.054-.03-.054-.072l.039-1.788c4.101-1.62 4.809-2.312 4.963-2.818a.434.434 0 0 0 .017-.121v-.687a.419.419 0 0 0-.115-.288c-.402-.42-.714-1.006-.902-1.694a.417.417 0 0 0-.158-.228.71.71 0 0 1-.127-1.015.417.417 0 0 0 .09-.259V3.637c0-1.477.837-2.257 2.423-2.257 1.609 0 2.424.76 2.424 2.257v1.537c0 .094.031.186.09.26A.716.716 0 0 1 9.47 6.45a.418.418 0 0 0-.16.228c-.187.689-.498 1.274-.9 1.694a.417.417 0 0 0-.116.288v.687c0 .04.006.081.018.121.154.505.863 1.197 4.964 2.818l.039.069v1.717h.002zM19.43 11.74c-.954-.363-1.953-.553-2.755-.705-.543-.103-1.28-.242-1.443-.346v-.382c1.811-.083 2.585-.662 2.672-.732a.415.415 0 0 0-.058-.688c-.388-.219-.78-1.961-.898-3.418 0-1.497-.836-2.829-2.181-3.473-1.244-.595-2.608-.453-3.646.382a.417.417 0 0 0 .522.65c.782-.63 1.814-.737 2.764-.281 1.053.504 1.707 1.547 1.709 2.756.054.677.26 2.641.864 3.626-.395.159-1.093.352-2.165.354a.417.417 0 0 0-.416.417v.9c0 .042.007.084.02.124.178.565.943.71 2.101.93.77.145 1.727.327 2.632.692l-.03 1.598h-3.647a.417.417 0 0 0 0 .833h3.647a.864.864 0 0 0 .863-.863v-1.568a.864.864 0 0 0-.555-.805z"
  }))));
};

// ---------------------------------------------------------------------------

const IconPlayer$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 25,
    height: 18
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-player', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    clipRule: "evenodd",
    d: "M15.563 9.568l-3.936 2.86a.473.473 0 01-.75-.384V6.327c0-.384.435-.613.75-.384l3.936 2.858c.26.19.26.58 0 .767z",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    clipRule: "evenodd",
    d: "M21.791 2.027C21.178 1.587 19.471 1 12.315 1c-7.668 0-8.805.673-9.17.893C1.202 3.06 1.018 8.228 1 9.276c.024 1.384.248 5.988 2.144 7.128.364.219 1.496.888 9.171.888 7.161 0 8.867-.583 9.476-1.023 1.656-1.191 1.847-5.294 1.852-7.011-.004-1.381-.15-6.005-1.852-7.231z"
  })));
};

// ---------------------------------------------------------------------------

const IconPlus$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 12,
    height: 12
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-plus', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    stroke: "none",
    fillRule: "evenodd",
    d: "M7 5h4.25c.414 0 .75.448.75 1s-.336 1-.75 1H7v4a1 1 0 0 1-2 0V7H.75C.336 7 0 6.552 0 6s.336-1 .75-1H5V1a1 1 0 1 1 2 0v4z"
  })));
};

// ---------------------------------------------------------------------------

const IconPolicy$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 20,
    height: 20
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-policy', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M5.90909 10.8182H1V1H10.8182V5.90909",
    stroke: "#4D6374",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M18.9998 9.18176H9.18164V18.9999H18.9998V9.18176Z",
    stroke: "#4D6374",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M14.0908 1.81824C16.3498 1.81824 18.1817 3.65015 18.1817 5.90915",
    stroke: "#4D6374",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M5.90927 18.1819C3.65027 18.1819 1.81836 16.3499 1.81836 14.0909",
    stroke: "#4D6374",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  })));
};

// ---------------------------------------------------------------------------

const IconPorts$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 22,
    height: 18
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-ports', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M6.553 1.145c0-.47-.541-.777-.944-.482L.622 4.639a.595.595 0 0 0 0 .964L5.61 9.58c.35.254.944.053.944-.481V7.076l5.293-.565c1.813-.27 1.904-2.443 0-2.778l-5.293-.566V1.145zM15.44 16.634a.59.59 0 0 0 .944.481l4.987-3.976a.596.596 0 0 0 0-.963l-4.987-3.978c-.354-.257-.945-.04-.945.482l.001 2.022-5.294.566c-1.813.27-1.903 2.443 0 2.777l5.293.566.001 2.023z"
  }))));
};

// ---------------------------------------------------------------------------

const IconPromote$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 15,
    height: 16
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-promote', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    d: "M1 9.4l4.2 4.2L15 1",
    strokeLinecap: "square",
    strokeMiterlimit: "10"
  })));
};

// ---------------------------------------------------------------------------

const IconQuestion$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 25,
    height: 25
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-question', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("g", {
    id: "Page-1",
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("g", {
    id: "New",
    transform: "translate(1.000000, 1.000000)"
  }, /*#__PURE__*/React.createElement("polygon", {
    id: "Star",
    stroke: "#82929e",
    fill: "#82929e",
    fillRule: "nonzero",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    points: "10 20 9.3720948 19.9802673 8.7466677 19.921147 8.1261869 19.8228725 7.51310113 19.6858316 6.90983006 19.5105652 6.31875447 19.2977649 5.74220708 19.0482705 5.18246326 18.7630668 4.64173205 18.4432793 4.12214748 18.0901699 3.6257601 17.7051324 3.15452894 17.2896863 2.71031373 16.8454711 2.29486757 16.3742399 1.90983006 15.8778525 1.55672074 15.3582679 1.2369332 14.8175367 0.95172948 14.2577929 0.70223514 13.6812455 0.48943484 13.0901699 0.31416839 12.4868989 0.17712749 11.8738131 0.07885299 11.2533323 0.01973272 10.6279052 0 10 0.01973272 9.3720948 0.07885299 8.7466677 0.17712749 8.1261869 0.31416839 7.51310113 0.48943484 6.90983006 0.70223514 6.31875447 0.95172948 5.74220708 1.2369332 5.18246326 1.55672074 4.64173205 1.90983006 4.12214748 2.29486757 3.6257601 2.71031373 3.15452894 3.15452894 2.71031373 3.6257601 2.29486757 4.12214748 1.90983006 4.64173205 1.55672074 5.18246326 1.2369332 5.74220708 0.95172948 6.31875447 0.70223514 6.90983006 0.48943484 7.51310113 0.31416839 8.1261869 0.17712749 8.7466677 0.07885299 9.3720948 0.01973272 10 0 10.6279052 0.01973272 11.2533323 0.07885299 11.8738131 0.17712749 12.4868989 0.31416839 13.0901699 0.48943484 13.6812455 0.70223514 14.2577929 0.95172948 14.8175367 1.2369332 15.3582679 1.55672074 15.8778525 1.90983006 16.3742399 2.29486757 16.8454711 2.71031373 17.2896863 3.15452894 17.7051324 3.6257601 18.0901699 4.12214748 18.4432793 4.64173205 18.7630668 5.18246326 19.0482705 5.74220708 19.2977649 6.31875447 19.5105652 6.90983006 19.6858316 7.51310113 19.8228725 8.1261869 19.921147 8.7466677 19.9802673 9.3720948 20 10 19.9802673 10.6279052 19.921147 11.2533323 19.8228725 11.8738131 19.6858316 12.4868989 19.5105652 13.0901699 19.2977649 13.6812455 19.0482705 14.2577929 18.7630668 14.8175367 18.4432793 15.3582679 18.0901699 15.8778525 17.7051324 16.3742399 17.2896863 16.8454711 16.8454711 17.2896863 16.3742399 17.7051324 15.8778525 18.0901699 15.3582679 18.4432793 14.8175367 18.7630668 14.2577929 19.0482705 13.6812455 19.2977649 13.0901699 19.5105652 12.4868989 19.6858316 11.8738131 19.8228725 11.2533323 19.921147 10.6279052 19.9802673"
  }), /*#__PURE__*/React.createElement("g", {
    id: "?",
    transform: "translate(6.000000, 1.000000)",
    fill: "#FFFFFF",
    fontFamily: "Helvetica",
    fontSize: "14",
    fontWeight: "normal"
  }, /*#__PURE__*/React.createElement("text", null, /*#__PURE__*/React.createElement("tspan", {
    x: "0",
    y: "14"
  }, "?")))))));
};

// ---------------------------------------------------------------------------

const IconRAT$1 = props => {
  if (props.modifier === 'inverted') {
    console.warn('IconRAT');
  }
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 19,
    height: 20
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-rat', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("g", {
    clipPath: "url(#clip0)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M.962 5.269h0c.054-.084.186-.233.418-.443.224-.202.514-.437.855-.693a30.83 30.83 0 012.449-1.637 26.437 26.437 0 012.645-1.421c.405-.184.775-.33 1.091-.43C8.744.543 8.982.5 9.137.5c.152 0 .387.043.708.147.313.102.679.252 1.08.44.804.375 1.726.892 2.624 1.445a36.547 36.547 0 012.45 1.646c.71.523 1.182.93 1.343 1.133.03.038.1.18.173.486.068.286.126.657.172 1.092.092.868.131 1.953.123 3.05a32.245 32.245 0 01-.159 3.086c-.048.446-.105.833-.17 1.14-.068.322-.134.506-.179.581-.046.079-.173.226-.412.44-.229.204-.528.44-.882.697a32.202 32.202 0 01-2.532 1.635 30.948 30.948 0 01-2.675 1.414c-.4.183-.758.329-1.054.427-.309.103-.508.14-.61.14-.101 0-.3-.037-.609-.141-.296-.1-.653-.247-1.054-.432A31.464 31.464 0 014.799 17.5a33.773 33.773 0 01-2.534-1.639 12.37 12.37 0 01-.884-.692c-.24-.211-.368-.355-.414-.43-.04-.065-.104-.237-.17-.554-.061-.3-.115-.68-.16-1.121a32.627 32.627 0 01-.136-3.065 31.882 31.882 0 01.128-3.064c.044-.44.098-.82.161-1.12.067-.315.132-.484.172-.546z",
    stroke: "#fff"
    // fill='none'
  })), /*#__PURE__*/React.createElement("path", {
    clipRule: "evenodd",
    d: "M11.648 15h1.256c.17 0 .241-.115.17-.271l-1.97-4.08c-.027-.043-.013-.098.044-.114 1.169-.343 1.898-1.298 1.898-2.625 0-1.74-1.184-2.91-2.951-2.91H6.228C6.085 5 6 5.085 6 5.228v9.542c0 .143.085.228.228.228h1.198c.143 0 .228-.085.228-.228v-3.994c0-.057.044-.085.085-.085h1.668c.044 0 .085.014.099.058l1.868 4.079c.06.128.145.172.274.172zM7.739 6.428h2.126c.913 0 1.525.615 1.525 1.512 0 .87-.614 1.47-1.525 1.47H7.739c-.057 0-.085-.028-.085-.085v-2.81c0-.059.03-.087.085-.087z",
    fill: "#fff",
    stroke: "none",
    fillRule: "evenodd"
  }), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("clipPath", {
    id: "clip0"
  }, /*#__PURE__*/React.createElement("path", {
    fill: "#fff"
    // stroke='none'
    ,
    d: "M0 0h18.31v20H0z"
  })))));
};

// ---------------------------------------------------------------------------

const IconRecon$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 25,
    height: 25
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-recon', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8.4,21.2c-0.1,0-0.2,0-0.3-0.1c-0.2-0.2-0.2-0.5,0-0.7c2.2-2.5,3.1-5.9,2.5-9.2c-0.1-0.3,0.1-0.5,0.4-0.6 c0.3-0.1,0.5,0.1,0.6,0.4c0.7,3.6-0.3,7.3-2.7,10.1C8.7,21.1,8.6,21.2,8.4,21.2z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M11.3,21.5c-0.1,0-0.2,0-0.3-0.1c-0.2-0.2-0.3-0.5-0.1-0.7c2-3,2.8-6.6,2.1-10.1c0,0,0,0,0,0 c-0.1-0.5-0.4-0.9-0.8-1.2c-0.4-0.3-1-0.4-1.5-0.3C9.5,9.3,8.9,10.3,9,11.3c0,0,0,0,0,0.1C9.1,11.7,8.9,12,8.7,12 c-0.3,0.1-0.5-0.1-0.6-0.3c0,0,0,0,0,0c0,0,0,0,0-0.1c-0.2-0.8,0-1.6,0.4-2.3c0.4-0.7,1.1-1.1,1.9-1.3c0.8-0.2,1.6,0,2.3,0.4 c0.7,0.4,1.1,1.1,1.3,1.9c0,0,0,0,0,0c0.8,3.8,0,7.6-2.2,10.8C11.6,21.4,11.5,21.5,11.3,21.5z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M5.9,20.1c-0.1,0-0.3-0.1-0.4-0.2c-0.2-0.2-0.2-0.5,0-0.7c1.7-1.6,2.6-3.8,2.6-6.1c0-0.3,0.2-0.5,0.5-0.5 c0,0,0,0,0,0c0.3,0,0.5,0.2,0.5,0.5c0,2.6-1,5.1-2.9,6.9C6.2,20.1,6.1,20.1,5.9,20.1z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M14.6,20.8c-0.1,0-0.1,0-0.2-0.1c-0.2-0.1-0.4-0.4-0.2-0.7c0.8-1.5,1.2-3.2,1.5-4.9c0-0.3,0.3-0.5,0.6-0.4 c0.3,0,0.5,0.3,0.4,0.6c-0.2,1.8-0.8,3.6-1.6,5.2C15,20.7,14.8,20.8,14.6,20.8z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3.9,18.6c-0.1,0-0.3-0.1-0.4-0.2c-0.2-0.2-0.1-0.5,0.1-0.7c1.6-1.3,2.4-3.5,2-5.5c0,0,0,0,0-0.1 c-0.3-1.4,0-2.9,0.8-4.1c0.8-1.2,2.1-2.1,3.5-2.3c3-0.6,5.9,1.3,6.5,4.3c0.3,1.3,0.4,2.5,0.3,3.8c0,0.3-0.2,0.5-0.5,0.5 c-0.3,0-0.5-0.2-0.5-0.5c0-1.2-0.1-2.4-0.3-3.6c-0.2-1.2-0.9-2.2-1.9-2.9c-1-0.7-2.2-0.9-3.4-0.7C8.9,6.8,7.9,7.5,7.3,8.5 c-0.7,1-0.9,2.2-0.7,3.3c0,0,0,0,0,0.1c0.5,2.5-0.4,5-2.3,6.6C4.2,18.5,4,18.6,3.9,18.6z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M18.1,18.5c0,0-0.1,0-0.1,0c-0.3-0.1-0.4-0.3-0.4-0.6c0.7-2.7,0.8-5.5,0.2-8.3c-0.5-2.4-2.1-4.3-4.4-5.1 c-0.3-0.1-0.4-0.4-0.3-0.6c0.1-0.3,0.4-0.4,0.6-0.3c2.6,1,4.4,3.2,5,5.9c0.6,2.9,0.5,5.9-0.3,8.8C18.5,18.4,18.3,18.5,18.1,18.5z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M3.7,9.8c0,0-0.1,0-0.1,0C3.3,9.8,3.1,9.5,3.2,9.2c0.7-3.1,3.1-5.4,6.2-6.1C10.3,3,11.3,3,12.2,3.1 c0.3,0,0.5,0.3,0.4,0.6s-0.3,0.5-0.6,0.4C11.3,4,10.4,4,9.6,4.1C6.9,4.7,4.8,6.8,4.2,9.4C4.1,9.7,3.9,9.8,3.7,9.8z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M2.4,16.6c-0.1,0-0.3-0.1-0.4-0.2c-0.2-0.2-0.2-0.5,0.1-0.7C3,15,3.4,13.8,3.2,12.6c0,0,0,0,0-0.1 C3,12,3,11.5,3,10.9c0-0.3,0.2-0.5,0.5-0.5c0.3,0,0.5,0.2,0.5,0.5c0,0.5,0,1,0.1,1.4c0,0,0,0,0,0.1c0.3,1.5-0.2,3.1-1.4,4.1 C2.6,16.6,2.5,16.6,2.4,16.6z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M21,11.7C21,11.7,21,11.7,21,11.7c-0.3,0-0.5-0.2-0.5-0.5c0-0.7,0-1.3-0.2-2c0,0,0,0,0-0.1 c-1-5.1-6-8.5-11.2-7.4C7.8,2,6.6,2.5,5.4,3.3C5.2,3.5,4.9,3.4,4.8,3.2C4.6,3,4.6,2.6,4.9,2.5C6.1,1.6,7.5,1,8.9,0.7 c5.7-1.1,11.2,2.5,12.4,8.1c0,0,0,0,0,0.1c0.2,0.8,0.2,1.5,0.2,2.3C21.5,11.5,21.3,11.7,21,11.7z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M1.3,13.9c-0.2,0-0.4-0.2-0.5-0.4c0-0.1-0.1-0.2-0.1-0.4c0,0,0,0,0-0.1C0,9.6,1.1,6.1,3.6,3.6 c0.2-0.2,0.5-0.2,0.7,0c0.2,0.2,0.2,0.5,0,0.7c-2.3,2.2-3.2,5.4-2.6,8.5c0,0,0,0,0,0.1c0,0.1,0.1,0.3,0.1,0.4 c0.1,0.3-0.1,0.5-0.4,0.6C1.4,13.9,1.3,13.9,1.3,13.9z"
  })));
};

// ---------------------------------------------------------------------------

const IconRemove$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 9,
    height: 9
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-remove', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: "icon-remove-a",
    d: "M9 1.352L7.647 0 4.5 3.148 1.353 0 0 1.352 3.148 4.5 0 7.648 1.353 9 4.5 5.853 7.647 9 9 7.648 5.852 4.5z"
  })), /*#__PURE__*/React.createElement("g", {
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("mask", {
    id: "icon-remove-b",
    fill: "#fff",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#icon-remove-a"
  })), /*#__PURE__*/React.createElement("g", {
    mask: "url(#icon-remove-b)",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M-7-7h24v24H-7z"
  })))));
};

// ---------------------------------------------------------------------------

const IconRibbon$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 14,
    height: 18
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-ribbon', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M1 17L7 12.6364L13 17V2.45455C13 2.06878 12.8595 1.69881 12.6095 1.42603C12.3594 1.15325 12.0203 1 11.6667 1H2.33333C1.97971 1 1.64057 1.15325 1.39052 1.42603C1.14048 1.69881 1 2.06878 1 2.45455V17Z",
    stroke: "#4D6374",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

// ---------------------------------------------------------------------------

const IconRibbonNew$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 14,
    height: 18
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-ribbon-new', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M1 17L7 12.6364L13 17V2.45455C13 2.06878 12.8595 1.69881 12.6095 1.42603C12.3594 1.15325 12.0203 1 11.6667 1H2.33333C1.97971 1 1.64057 1.15325 1.39052 1.42603C1.14048 1.69881 1 2.06878 1 2.45455V17Z",
    stroke: "#4D6374",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7 4V10",
    stroke: "#4D6374",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4 7H10",
    stroke: "#4D6374",
    strokeLinejoin: "round"
  })));
};

// ---------------------------------------------------------------------------

const IconRobot$1 = props => {
  if (props.modifier === 'inverted') {
    console.warn('IconRobot cannot be inverted');
  }
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 102,
    height: 52
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-robot', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: "icon-robot",
    d: "M0 0h102v52H0z"
  })), /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("mask", {
    id: "icon-robot-mask",
    fill: "#fff"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#icon-robot"
  })), /*#__PURE__*/React.createElement("use", {
    fill: "#FFF",
    xlinkHref: "#icon-robot"
  }), /*#__PURE__*/React.createElement("g", {
    fill: "#888C8F",
    fillRule: "nonzero",
    mask: "url(#icon-robot-mask)",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M52 27.5c0-2.481-2.019-4.5-4.5-4.5a4.505 4.505 0 0 0-4.5 4.5c0 2.481 2.019 4.5 4.5 4.5s4.5-2.019 4.5-4.5zM47.5 29a1.501 1.501 0 0 1 0-3 1.501 1.501 0 0 1 0 3zm15 3c2.481 0 4.5-2.019 4.5-4.5S64.981 23 62.5 23a4.505 4.505 0 0 0-4.5 4.5c0 2.481 2.019 4.5 4.5 4.5zm0-6c.826 0 1.5.672 1.5 1.5s-.674 1.5-1.5 1.5-1.5-.672-1.5-1.5.674-1.5 1.5-1.5zm-3 12a1.5 1.5 0 1 0 0-3h-9a1.5 1.5 0 1 0 0 3h9z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M88 68.276V60.5c0-4.137-3.363-7.5-7.5-7.5H76v-1.5c0-2.481-2.019-4.5-4.5-4.5H61v-3h7.5c2.481 0 4.5-2.019 4.5-4.5V35h4.5c.83 0 1.5-.67 1.5-1.5v-6c0-.83-.67-1.5-1.5-1.5H73v-4.5c0-2.481-2.019-4.5-4.5-4.5h-27a4.505 4.505 0 0 0-4.5 4.5V26h-4.5c-.83 0-1.5.67-1.5 1.5v6c0 .83.67 1.5 1.5 1.5H37v4.5c0 2.481 2.019 4.5 4.5 4.5H49v3H38.5a4.505 4.505 0 0 0-4.5 4.5V53h-4.5a4.505 4.505 0 0 1-4.5-4.5v-7.776c1.742-.622 3-2.271 3-4.224v-3c0-.83-.67-1.5-1.5-1.5h-6c-.83 0-1.5.67-1.5 1.5v3c0 1.953 1.258 3.602 3 4.224V48.5c0 4.135 3.365 7.5 7.5 7.5H34v10.5c0 2.481 2.019 4.5 4.5 4.5h4.43v9H41.5a4.505 4.505 0 0 0-4.5 4.5v3c0 .83.67 1.5 1.5 1.5h12c.83 0 1.5-.67 1.5-1.5v-3c0-2.481-2.019-4.5-4.5-4.5h-1.57v-9H64v9h-1.5a4.505 4.505 0 0 0-4.5 4.5v3c0 .83.67 1.5 1.5 1.5h12c.83 0 1.5-.67 1.5-1.5v-3c0-2.481-2.019-4.5-4.5-4.5H67v-9h4.5c2.481 0 4.5-2.019 4.5-4.5V56h4.5c2.481 0 4.5 2.019 4.5 4.5v7.776c-1.742.622-3 2.271-3 4.224v3c0 .83.67 1.5 1.5 1.5h6c.83 0 1.5-.67 1.5-1.5v-3c0-1.953-1.258-3.602-3-4.224zM22 35h3v1.5a1.501 1.501 0 0 1-3 0V35zm27 49.5V86h-9v-1.5c0-.826.672-1.5 1.5-1.5h6c.828 0 1.5.674 1.5 1.5zm21 0V86h-9v-1.5c0-.826.674-1.5 1.5-1.5h6c.826 0 1.5.674 1.5 1.5zM76 29v3h-3v-3h3zm-42 3v-3h3v3h-3zm6 7.5v-18a1.5 1.5 0 0 1 1.5-1.5h27c.826 0 1.5.672 1.5 1.5v18c0 .828-.674 1.5-1.5 1.5h-27a1.5 1.5 0 0 1-1.5-1.5zM52 44h6v3h-6v-3zm21 22.5c0 .826-.674 1.5-1.5 1.5h-33c-.828 0-1.5-.674-1.5-1.5v-15a1.5 1.5 0 0 1 1.5-1.5h33c.826 0 1.5.672 1.5 1.5v15zM88 74h-3v-1.5c0-.826.674-1.5 1.5-1.5s1.5.674 1.5 1.5V74z"
  })))));
};

// ---------------------------------------------------------------------------

const IconSave$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 15
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-save', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8.79 9.623a.998.998 0 0 1-.767.376.997.997 0 0 1-.727-.29L4.69 7.125a1 1 0 0 1 1.41-1.42l.91.902V1a1 1 0 0 1 2 0v5.589l.89-.884a1 1 0 0 1 1.409 1.42L8.79 9.623zm4.918-.165a1 1 0 0 1 2 0v3.792a1 1 0 0 1-1 1H1.292a1 1 0 0 1-1-1V9.458a1 1 0 0 1 2 0v2.792h11.416V9.458z",
    stroke: "none"
  })));
};

// ---------------------------------------------------------------------------

const IconSearch$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 14,
    height: 14
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-search', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("g", null, /*#__PURE__*/React.createElement("path", {
    d: "M13.7084 11.3751L11.2747 8.94141"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4.95833 5.54175H0.875"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M4.95833 9.04175H0.875"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.625 12.5417H0.875"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.625 2.04175H0.875"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.62502 9.62516C10.9137 9.62516 11.9584 8.58049 11.9584 7.29183C11.9584 6.00317 10.9137 4.9585 9.62502 4.9585C8.33636 4.9585 7.29169 6.00317 7.29169 7.29183C7.29169 8.58049 8.33636 9.62516 9.62502 9.62516Z",
    fill: "none"
  }))));
};
const IconSearchSmall$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 17
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-search--small', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: "icon-search-small",
    d: "M16.7 15.3c.9-1.2 1.4-2.6 1.4-4.2 0-3.9-3.1-7.1-7-7.1S4 7.2 4 11.1c0 3.9 3.2 7.1 7.1 7.1 1.6 0 3.1-.5 4.2-1.4l3 3c.2.2.5.3.7.3.2 0 .5-.1.7-.3.4-.4.4-1 0-1.4l-3-3.1zm-5.6.8c-2.8 0-5.1-2.2-5.1-5S8.3 6 11.1 6s5.1 2.3 5.1 5.1-2.3 5-5.1 5z"
  })), /*#__PURE__*/React.createElement("g", {
    stroke: "none",
    fillRule: "evenodd",
    transform: "translate(-4 -4)"
  }, /*#__PURE__*/React.createElement("mask", {
    id: "icon-search-small-mask",
    fill: "#fff",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#icon-search-small"
  })), /*#__PURE__*/React.createElement("g", {
    mask: "url(#icon-search-small-mask)",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M0 0h24v24H0z"
  })))));
};
const IconThumbSearch$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 24,
    height: 24
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-notifications', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    d: "M16.682 14.993l6.416 6.416a1.194 1.194 0 11-1.689 1.69l-6.417-6.417"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    clipRule: "evenodd",
    d: "M18.451 9.726a8.726 8.726 0 11-17.453 0 8.726 8.726 0 0117.453 0z",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    d: "M4.769 7.486c.519-1.753 1.95-3.174 3.868-3.561.496-.1.988-.117 1.468-.072M9.646 8.924A7.627 7.627 0 018.12 15.2",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    d: "M7.951 9.022a1.7 1.7 0 011.356-1.768 1.705 1.705 0 012.007 1.334h-.002a9.308 9.308 0 01-1.235 6.783M8.084 10.703a5.95 5.95 0 01-1.623 3.824M13.062 12.131c-.146.93-.408 1.83-.775 2.68",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    d: "M13.192 10.448c0-.724-.064-1.458-.214-2.195h.001a3.401 3.401 0 00-6.67 1.344l.005-.001a4.242 4.242 0 01-1.203 3.877",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    d: "M11.735 4.287a5.082 5.082 0 012.91 3.63h0c.344 1.707.327 3.403.006 5.011M4.557 9.159c.012.257.035.515.087.774l.003-.001a2.548 2.548 0 01-.562 2.159",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

/* eslint-disable no-magic-numbers */
const IconSend$1 = makeIcon('send', 17, 16, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  d: "M15.5 1L5 9.4V14.3L7.52 11.29",
  fill: "none",
  strokeMiterlimit: "10"
}), /*#__PURE__*/React.createElement("path", {
  d: "M1.5 6.6L15.5 1L12.7 15L1.5 6.6Z",
  fill: "none",
  strokeMiterlimit: "10",
  strokeLinecap: "square"
})));
/* eslint-enable no-magic-numbers */

// ---------------------------------------------------------------------------

const IconSeparator$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 14,
    height: 45
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-separator', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    stroke: "#C4CACF",
    d: "M1 1l11 23L1 45",
    fill: "none",
    fillRule: "evenodd",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

// ---------------------------------------------------------------------------

const IconServer$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 17,
    height: 20
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-server', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M16.25 13.38H.417a.416.416 0 0 0-.417.416v5c0 .23.186.417.417.417H16.25c.23 0 .417-.186.417-.417v-5a.416.416 0 0 0-.417-.416zm-.416 5h-15v-4.167h15v4.167zM2.898 16.742h1.667a.416.416 0 1 0 0-.833H2.898a.416.416 0 1 0 0 .833zm10.859-.001a.416.416 0 1 1 0-.834.43.43 0 0 1 .432.417c0 .23-.171.417-.402.417h-.03zM16.25 6.713c.23 0 .417.186.417.416v5c0 .23-.186.417-.417.417H.417A.416.416 0 0 1 0 12.129v-5c0-.23.186-.416.417-.416H16.25zm-.416 5V7.545h-15v4.166h15zM2.898 10.074a.417.417 0 0 1 0-.834h1.667a.416.416 0 1 1 0 .834H2.898zM16.25.045c.23 0 .417.187.417.417v5c0 .23-.186.416-.417.416H.417A.416.416 0 0 1 0 5.462v-5C0 .232.186.045.417.045H16.25zm-.416 5V.878h-15v4.167h15zM2.898 3.407h1.667a.416.416 0 1 0 0-.833H2.898a.416.416 0 1 0 0 .833zm10.859 0h.03c.23 0 .402-.186.402-.416a.43.43 0 0 0-.432-.417.416.416 0 1 0 0 .833zm0 6.667h.03c.23 0 .402-.186.402-.417a.43.43 0 0 0-.432-.416.416.416 0 1 0 0 .833z",
    fillRule: "nonzero",
    stroke: "none"
  })));
};

// ---------------------------------------------------------------------------

const IconServices$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 20,
    height: 20
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-services', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("g", {
    fillRule: "nonzero",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M18.82 0H.42a.4.4 0 0 0-.4.4v16.8c0 .22.178.4.4.4h18.4a.4.4 0 0 0 .4-.4V.4a.4.4 0 0 0-.4-.4zm-.4.8v4H.82v-4h17.6zm-17.6 16V5.6h17.6v11.2H.82z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M15.298 3.886a.402.402 0 0 0 .566-.005l.522-.533.521.533a.4.4 0 0 0 .566.005.4.4 0 0 0 .006-.565l-.533-.546.533-.545a.4.4 0 0 0-.572-.56l-.521.533-.522-.533a.4.4 0 0 0-.572.56l.534.545-.534.546a.4.4 0 0 0 .006.565zM12.408 4h1.6a.4.4 0 0 0 .4-.4V2a.4.4 0 0 0-.4-.4h-1.6a.4.4 0 0 0-.4.4v1.6c0 .22.178.4.4.4zm.4-1.6h.8v.8h-.8v-.8zM9.207 4h1.6a.4.4 0 0 0 0-.8h-1.6a.4.4 0 0 0 0 .8zM9.62 9.557a1.618 1.618 0 1 0 1.617 1.617c0-.892-.724-1.617-1.616-1.617zm0 2.433a.818.818 0 1 1 .817-.817c0 .45-.366.817-.816.817z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M13.683 10.192l-.839-.105a3.27 3.27 0 0 0-.194-.46l.512-.674a.399.399 0 0 0-.002-.485 4.459 4.459 0 0 0-.875-.865.4.4 0 0 0-.485.005l-.666.52a3.408 3.408 0 0 0-.464-.19l-.115-.837a.401.401 0 0 0-.344-.343 4.426 4.426 0 0 0-1.23.008.4.4 0 0 0-.34.346l-.104.838a3.35 3.35 0 0 0-.463.195l-.673-.511a.4.4 0 0 0-.485 0c-.322.248-.62.55-.864.875a.4.4 0 0 0 .005.485l.519.666c-.075.15-.138.306-.19.466l-.836.116a.4.4 0 0 0-.343.344c-.053.408-.051.821.007 1.228a.4.4 0 0 0 .348.34l.838.105c.053.159.118.314.195.463l-.511.673a.4.4 0 0 0 .002.486c.248.322.55.62.874.863a.397.397 0 0 0 .485-.005l.667-.519c.15.075.305.138.465.19l.114.836a.402.402 0 0 0 .344.343 4.53 4.53 0 0 0 1.229-.008.4.4 0 0 0 .34-.347l.105-.84a3.23 3.23 0 0 0 .46-.194l.675.511a.398.398 0 0 0 .484-.001c.328-.25.618-.546.864-.876a.397.397 0 0 0-.005-.484l-.518-.667c.074-.15.137-.305.188-.463l.836-.115a.399.399 0 0 0 .342-.343 4.433 4.433 0 0 0-.006-1.23.4.4 0 0 0-.346-.34zm-1.195 1.271a.4.4 0 0 0-.336.306c-.06.263-.163.514-.303.746a.4.4 0 0 0 .026.452l.485.624c-.09.103-.186.2-.287.29l-.63-.477a.395.395 0 0 0-.451-.02c-.232.142-.482.249-.745.314a.4.4 0 0 0-.3.339l-.098.784a3.873 3.873 0 0 1-.409.002l-.107-.783a.4.4 0 0 0-.305-.334 2.582 2.582 0 0 1-.749-.306.4.4 0 0 0-.452.027l-.623.485c-.102-.09-.2-.186-.291-.287l.477-.629a.4.4 0 0 0 .021-.453 2.585 2.585 0 0 1-.314-.745.4.4 0 0 0-.34-.301l-.783-.097a3.485 3.485 0 0 1-.002-.409l.782-.108a.4.4 0 0 0 .336-.305c.061-.264.164-.516.304-.75a.4.4 0 0 0-.026-.451l-.485-.623c.09-.102.187-.2.287-.292l.63.479a.4.4 0 0 0 .453.02c.23-.143.48-.248.744-.313.16-.04.28-.174.3-.338l.099-.784c.136-.01.272-.01.409-.003l.107.783a.4.4 0 0 0 .306.336c.263.061.514.164.746.304a.4.4 0 0 0 .453-.026l.622-.486c.103.09.2.187.291.288l-.478.63a.4.4 0 0 0-.02.453c.142.231.248.48.313.742a.4.4 0 0 0 .338.301l.784.098c.01.138.01.273.003.41l-.782.107z"
  }))));
};

// ---------------------------------------------------------------------------

const IconShare$1 = makeIcon('share', 14, 16, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  d: "m5.312 6.763 3.51-2.193M5.312 9.237l3.51 2.193M10.8 5.667A2.333 2.333 0 1 0 10.8 1a2.333 2.333 0 0 0 0 4.667ZM10.8 15a2.333 2.333 0 1 0 0-4.667 2.333 2.333 0 0 0 0 4.667ZM3.333 10.333a2.333 2.333 0 1 0 0-4.666 2.333 2.333 0 0 0 0 4.666Z",
  fill: "none",
  strokeMiterlimit: "10",
  strokeLinecap: "round",
  strokeLinejoin: "round"
})));

// ---------------------------------------------------------------------------

const IconSignPost$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 18,
    height: 24
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-sign-post', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M14.127 6.734L1.696 8.104l-.521-4.727 12.431-1.37 3.416 2.045zM2.938 10.257l12.444-1.235.472 4.733L3.408 14.99.015 12.908zM8.488.635v1.87M8.488 16.675v6.786"
  }))));
};

// ---------------------------------------------------------------------------

const IconSlideOpen$1 = props => {
  if (props.modifier === 'inverted') {
    console.warn('IconSlideOpen cannot be inverted');
  }
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 10,
    height: 6
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-slide-open', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    transform: "rotate(-90)"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    stroke: "#7d8Ca9",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "2",
    d: "M9 1L5 5 1 1"
  })));
};
const IconSlideClosed$1 = props => {
  if (props.modifier === 'inverted') {
    console.warn('IconSlideClosed cannot be inverted');
  }
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 10,
    height: 6
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-slide-closed', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    transform: "rotate(90)"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    stroke: "#7d8Ca9",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "2",
    d: "M9 1L5 5 1 1"
  })));
};

// ---------------------------------------------------------------------------

const IconSortAsc$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 10,
    height: 6
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-sort-asc', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink",
    transform: "rotate(180)"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "2",
    d: "M9 1L5 5 1 1"
  })));
};
const IconSortDesc$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 10,
    height: 6
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-sort-desc', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "2",
    d: "M9 1L5 5 1 1"
  })));
};

// ---------------------------------------------------------------------------

const IconSpinner$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 40,
    height: 40
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-spinner', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    opacity: ".2",
    d: "M20.201 5.169c-8.254 0-14.946 6.692-14.946 14.946 0 8.255 6.692 14.946 14.946 14.946s14.946-6.691 14.946-14.946c-.001-8.254-6.692-14.946-14.946-14.946zm0 26.58c-6.425 0-11.634-5.208-11.634-11.634 0-6.425 5.209-11.634 11.634-11.634 6.425 0 11.633 5.209 11.633 11.634 0 6.426-5.208 11.634-11.633 11.634z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M26.013 10.047l1.654-2.866a14.855 14.855 0 0 0-7.466-2.012v3.312c2.119 0 4.1.576 5.812 1.566z"
  }, /*#__PURE__*/React.createElement("animateTransform", {
    attributeType: "xml",
    attributeName: "transform",
    type: "rotate",
    from: "0 20 20",
    to: "360 20 20",
    dur: "0.5s",
    repeatCount: "indefinite"
  }))));
};

// ---------------------------------------------------------------------------

const IconStar$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 16
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-star', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8 1L9.61539 6.16008H15L10.7214 9.37523L12.403 14.5509L8 11.3519L3.597 14.5509L5.27862 9.37523L1 6.16008H6.38462L8 1Z",
    stroke: "#4D6374",
    strokeMiterlimit: "10",
    strokeLinecap: "square",
    strokeLinejoin: "round"
  })));
};

// ---------------------------------------------------------------------------

const IconStatus$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 11
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-status', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M1 8.33333L3.66667 11L11.6667 1",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M15 4.33333H13",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M15 7.66667H10.3333",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M15 11H7.66669",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  })));
};

// ---------------------------------------------------------------------------

const IconStatusInProgress$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 16
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-status--in-progress', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM12 9H7V4H9V7H12V9Z"
  })));
};

// ---------------------------------------------------------------------------

const IconStatusInvestigate$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 15,
    height: 14
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-status--investigate', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M14.5508 12.4045L8.21685 0.5295C7.84085 -0.1765 6.82885 -0.1765 6.45185 0.5295L0.118846 12.4045C-0.236154 13.0705 0.246846 13.8755 1.00185 13.8755H13.6688C14.4228 13.8755 14.9058 13.0705 14.5508 12.4045ZM7.33485 11.8755C6.78285 11.8755 6.33485 11.4275 6.33485 10.8755C6.33485 10.3235 6.78285 9.8755 7.33485 9.8755C7.88685 9.8755 8.33485 10.3235 8.33485 10.8755C8.33485 11.4275 7.88685 11.8755 7.33485 11.8755ZM8.33485 8.8755H6.33485V4.8755H8.33485V8.8755Z"
  })));
};

// ---------------------------------------------------------------------------

const IconStatusNone$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 16
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-status--none', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM8 2C9.3 2 10.5 2.4 11.5 3.1L3.1 11.5C2.4 10.5 2 9.3 2 8C2 4.7 4.7 2 8 2ZM8 14C6.7 14 5.5 13.6 4.5 12.9L12.9 4.5C13.6 5.5 14 6.7 14 8C14 11.3 11.3 14 8 14Z"
  })));
};

// ---------------------------------------------------------------------------

const IconStatusResolved$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 16
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-status--resolved', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM7 11.4L3.6 8L5 6.6L7 8.6L11 4.6L12.4 6L7 11.4Z"
  })));
};

// ---------------------------------------------------------------------------

const IconStatusReviewed$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 12
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-status--reviewed', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.975 12C11.575 12 14.375 8.9 15.575 7.1C16.075 6.4 16.075 5.5 15.575 4.8C14.375 3.1 11.575 0 7.975 0C4.375 0 1.575 3.1 0.375 4.9C-0.125 5.6 -0.125 6.5 0.375 7.1C1.575 8.9 4.375 12 7.975 12ZM7.975 3C9.675 3 10.975 4.3 10.975 6C10.975 7.7 9.675 9 7.975 9C6.275 9 4.975 7.7 4.975 6C4.975 4.3 6.275 3 7.975 3Z"
  })));
};

// ---------------------------------------------------------------------------

const IconStop$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 25,
    height: 25
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-stop', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("g", {
    id: "Page-1",
    stroke: "none",
    strokeWidth: "1",
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("g", {
    id: "Dispute-(Outline)",
    transform: "translate(1.000000, 1.000000)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M19.3884167,5.83533333 L14.1634167,0.613666667 C14.0384167,0.487833333 13.8684167,0.417833333 13.69175,0.417833333 L6.30425,0.417833333 C6.12758333,0.417833333 5.95758333,0.487833333 5.83175,0.613666667 L0.60925,5.83533333 C0.48425,5.96033333 0.413416667,6.13033333 0.413416667,6.30783333 L0.413416667,13.6961667 C0.413416667,13.8728333 0.48425,14.0428333 0.60925,14.1686667 L5.83175,19.392 C5.95758333,19.517 6.12758333,19.5878333 6.30425,19.5878333 L13.69175,19.5878333 C13.8684167,19.5878333 14.0384167,19.517 14.1634167,19.392 L19.38925,14.1686667 C19.5134167,14.0428333 19.58425,13.8728333 19.58425,13.6961667 L19.58425,6.30783333 C19.58425,6.13033333 19.5134167,5.96033333 19.3884167,5.83533333 Z",
    id: "Stroke-3779",
    stroke: "#ae0216",
    fill: "#ae0216",
    fillRule: "nonzero"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.075,6.252 L13.9233333,14.0995",
    id: "Stroke-3780",
    stroke: "#FFFFFF"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M6.075,14.0999167 L13.9233333,6.25241667",
    id: "Stroke-3781",
    stroke: "#FFFFFF"
  })))));
};

// ---------------------------------------------------------------------------

const IconSuccess$1 = props => {
  if (props.modifier === 'inverted') {
    console.warn('IconSuccess cannot be inverted');
  }
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 22,
    height: 22
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-success', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: "icon-success",
    d: "M13.725 4.725L6.75 11.7 4.275 9.225c-.3-.3-.75-.3-1.05 0-.3.3-.3.75 0 1.05l3 3c.15.15.3.225.525.225a.68.68 0 0 0 .525-.225l7.5-7.5c.3-.3.3-.75 0-1.05-.3-.3-.75-.3-1.05 0z"
  })), /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "11",
    cy: "11",
    r: "11",
    fill: "#4d8a47",
    stroke: "none"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(2 2)"
  }, /*#__PURE__*/React.createElement("mask", {
    id: "icon-success-b",
    fill: "#fff",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#icon-success"
  })), /*#__PURE__*/React.createElement("g", {
    fill: "#fff",
    mask: "url(#icon-success-b)",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M0 0h18v18H0z"
  }))))));
};

// ---------------------------------------------------------------------------

const IconTag$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 22,
    height: 22
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-trend-arrow--down', 'icon-tag', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    transform: "translate(1 1)"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M19.333 10.333c-6.916-6.833-5.5-5.416-9.75-9.666C9.333.417 9 .5 8.583.5H1.167a.747.747 0 0 0-.75.75v7.917c0 .166.083.333.25.5.166.166 9.833 9.75 10 9.833.333.167.583.083.833-.167l7.917-8c.166-.333.166-.75-.084-1z"
  }), /*#__PURE__*/React.createElement("circle", {
    cx: "4.583",
    cy: "4.583",
    r: "1.583"
  }))));
};

// ---------------------------------------------------------------------------

const IconTarget$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 14,
    height: 14
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-target', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.29169 12.5417C10.1912 12.5417 12.5417 10.1912 12.5417 7.29175C12.5417 4.39225 10.1912 2.04175 7.29169 2.04175C4.39219 2.04175 2.04169 4.39225 2.04169 7.29175C2.04169 10.1912 4.39219 12.5417 7.29169 12.5417Z",
    fill: "none"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.29169 0.875V3.20833",
    fill: "none"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M13.7083 7.29175H11.375",
    fill: "none"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.29169 13.7083V11.375",
    fill: "none"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M0.875 7.29175H3.20833",
    fill: "none"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.29165 7.87492C7.61381 7.87492 7.87498 7.61375 7.87498 7.29159C7.87498 6.96942 7.61381 6.70825 7.29165 6.70825C6.96948 6.70825 6.70831 6.96942 6.70831 7.29159C6.70831 7.61375 6.96948 7.87492 7.29165 7.87492Z",
    fill: "none"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.29165 7.87492C7.61381 7.87492 7.87498 7.61375 7.87498 7.29159C7.87498 6.96942 7.61381 6.70825 7.29165 6.70825C6.96948 6.70825 6.70831 6.96942 6.70831 7.29159C6.70831 7.61375 6.96948 7.87492 7.29165 7.87492Z",
    fill: "none"
  })));
};

// ---------------------------------------------------------------------------

const IconTech$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 20,
    height: 20
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-technology', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("g", {
    fillRule: "nonzero",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M12.977 16.095a.4.4 0 1 0-.79.128l.361 2.22H9.882a.4.4 0 1 0 0 .8h2.664a.76.76 0 0 0 .82-.666.394.394 0 0 0-.004-.11l-.385-2.372z"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M18.748.528a1.695 1.695 0 0 0-1.215-.507H1.725A1.694 1.694 0 0 0 .034 1.726v10.98a1.698 1.698 0 0 0 1.698 1.716h.695v3.605c0 .659.535 1.194 1.194 1.194h4.011c.658 0 1.194-.535 1.194-1.194v-.776c.001-.013.007-.024.007-.037v-2.792h8.703c.934 0 1.695-.76 1.698-1.711V1.73a1.681 1.681 0 0 0-.486-1.202zm-17.65.551a.847.847 0 0 1 .634-.258H17.55a.883.883 0 0 1 .628.268.89.89 0 0 1 .257.637v9.496h-9.6v-1.208c0-.013-.007-.024-.008-.036v-.79c0-.659-.536-1.194-1.194-1.194H3.621c-.658 0-1.194.535-1.194 1.193v2.035H.834V1.708a.885.885 0 0 1 .264-.629zm6.928 8.535H3.227v-.427c0-.217.177-.393.394-.393h4.011c.217 0 .394.176.394.393v.427zm-4.792.8h4.792v6.4H3.234v-6.4zM1.72 13.622a.898.898 0 0 1-.886-.91v-.69h1.593v1.6H1.72zm5.912 4.8H3.621a.394.394 0 0 1-.394-.394v-.413h4.799v.413a.394.394 0 0 1-.394.394zm9.903-4.8H8.833v-1.6h9.6v.706a.899.899 0 0 1-.898.895z"
  }))));
};

// ---------------------------------------------------------------------------

const IconTrash$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 16
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-trash', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M13.2576 6.09082V13.7272C13.2576 14.0647 13.1216 14.3885 12.8796 14.6271C12.6377 14.8658 12.3095 14.9999 11.9673 14.9999H4.22558C3.88338 14.9999 3.55519 14.8658 3.31322 14.6271C3.07124 14.3885 2.9353 14.0647 2.9353 13.7272V6.09082",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M1 3.54541H15.1931",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M5.51587 3.54545V1H10.677V3.54545",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M10.032 7.99048L6.16113 11.8087",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M10.032 11.8087L6.16113 7.99048",
    fill: "none",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  })));
};

// ---------------------------------------------------------------------------

const IconUnauthorized$1 = makeIcon('unauthorized', 20, 20, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  d: "m17.64 1.763-7.5-1.73a.647.647 0 0 0-.28 0l-7.5 1.73a.625.625 0 0 0-.485.612v8.875c0 5.61 6.596 8.215 7.918 8.68a.626.626 0 0 0 .414 0c1.322-.463 7.918-3.06 7.918-8.68V2.375a.625.625 0 0 0-.484-.612ZM8.787 13.42 5.366 10l.884-.884 2.464 2.465 4.968-5.795.95.812-5.846 6.821Z"
})));

// ---------------------------------------------------------------------------

const IconUpdate$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 16,
    height: 14
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-update', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    stroke: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M9.91 12.795H15M9.91 8.34H15M9.91 3.886H15",
    fill: "none",
    stroke: "#4D6374",
    strokeLinecap: "square",
    strokeMiterlimit: "10"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.364 3.886h-1.91a4.455 4.455 0 1 0 0 8.91h1.91",
    fill: "none",
    stroke: "#4D6374",
    strokeMiterlimit: "10"
  }), /*#__PURE__*/React.createElement("path", {
    d: "m4.818 6.432 2.546-2.546-2.546-2.545",
    fill: "none",
    stroke: "#4D6374",
    strokeLinecap: "square",
    strokeMiterlimit: "10"
  })));
};

// ---------------------------------------------------------------------------

const IconUpload$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 25,
    height: 25
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-upload', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    transform: "rotate(-90)",
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M23.8398 9.48169L10.0878 9.48169",
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M20.5303 6.17212L23.8403 9.48212L20.5303 12.7921",
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M16.9639 6.152L16.9639 1L0.999867 0.999999L0.999867 17.964L16.9639 17.964L16.9639 12.812",
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })));
};

// ---------------------------------------------------------------------------

const IconUser$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 20,
    height: 20
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-user', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M19.858 10.016c0-5.436-4.423-9.86-9.859-9.86C4.562.157.14 4.58.14 10.017c0 5.437 4.423 9.86 9.86 9.86 2.49 0 4.76-.935 6.498-2.463.016-.01.026-.026.041-.039 2.032-1.807 3.32-4.432 3.32-7.358zm-18.886 0C.973 5.039 5.022.99 10 .99c4.978 0 9.026 4.05 9.026 9.026a8.994 8.994 0 0 1-2.836 6.551c-3.063-1.296-3.621-1.892-3.719-2.045v-.575c.518-.574.922-1.32 1.18-2.174a1.76 1.76 0 0 0 .313-2.328V7.728c0-2.312-1.428-3.638-3.917-3.638-2.453 0-3.918 1.36-3.918 3.638v1.716a1.76 1.76 0 0 0 .313 2.327c.256.855.662 1.602 1.18 2.175v.579c-.101.172-.668.762-3.79 2.063a8.996 8.996 0 0 1-2.858-6.572zm3.575 7.176c3.155-1.348 3.746-2.001 3.889-2.46a.413.413 0 0 0 .018-.124v-.827a.416.416 0 0 0-.119-.292c-.505-.517-.897-1.234-1.132-2.077a.418.418 0 0 0-.16-.228.927.927 0 0 1-.175-1.34.418.418 0 0 0 .093-.262V7.728c0-1.809 1.095-2.805 3.085-2.805 2.017 0 3.083.97 3.083 2.805v1.853c0 .096.034.188.094.262a.924.924 0 0 1-.176 1.34.415.415 0 0 0-.16.229c-.235.841-.627 1.559-1.131 2.075a.416.416 0 0 0-.12.292v.828c0 .042.007.084.019.123.086.276.287.904 3.824 2.44a8.97 8.97 0 0 1-10.932.022z",
    fillRule: "nonzero",
    stroke: "none"
  })));
};

// ---------------------------------------------------------------------------

/* eslint-disable no-magic-numbers */
const IconV2Close$1 = makeIcon('v2-close', 16, 16, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  d: "M15.6067 1L1.60669 15",
  strokeMiterlimit: "10",
  strokeLinecap: "square"
}), /*#__PURE__*/React.createElement("path", {
  d: "M15.6067 15L1.60669 1",
  strokeMiterlimit: "10",
  strokeLinecap: "square"
})));
/* eslint-enable no-magic-numbers */

// ---------------------------------------------------------------------------

const IconV2Collapse$1 = makeIcon('v2-collapse', 24, 2, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  d: "M1 1h24",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  strokeMiterlimit: "10"
})));

// ---------------------------------------------------------------------------

const IconV2Expand$1 = makeIcon('v2-expand', 26, 26, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  clipRule: "evenodd",
  d: "M13.5 1a.5.5 0 0 0-1 0v11.5H1a.5.5 0 0 0 0 1h11.5V25a.5.5 0 0 0 1 0V13.5H25a.5.5 0 0 0 0-1H13.5V1Z",
  fillRule: "evenodd"
})));

// ---------------------------------------------------------------------------

const IconV2Info$1 = makeIcon('v2-expand', 24, 24, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("mask", {
  id: "icon-v2-info-mask",
  fill: "#fff"
}, /*#__PURE__*/React.createElement("path", {
  clipRule: "evenodd",
  d: "M12 23c6.075 0 11-4.925 11-11S18.075 1 12 1 1 5.925 1 12s4.925 11 11 11Zm12-11c0 6.627-5.373 12-12 12S0 18.627 0 12 5.373 0 12 0s12 5.373 12 12Zm-11.5-1.59v7.545h-1v-7.546h1Zm.59-3.865a1.09 1.09 0 1 1-2.18 0 1.09 1.09 0 0 1 2.18 0Z"
})), /*#__PURE__*/React.createElement("path", {
  clipRule: "evenodd",
  d: "M12 23c6.075 0 11-4.925 11-11S18.075 1 12 1 1 5.925 1 12s4.925 11 11 11Zm12-11c0 6.627-5.373 12-12 12S0 18.627 0 12 5.373 0 12 0s12 5.373 12 12Zm-11.5-1.59v7.545h-1v-7.546h1Zm.59-3.865a1.09 1.09 0 1 1-2.18 0 1.09 1.09 0 0 1 2.18 0Z",
  fillRule: "evenodd"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12.5 10.41h1v-1h-1v1Zm0 7.545v1h1v-1h-1Zm-1 0h-1v1h1v-1Zm0-7.546v-1h-1v1h1ZM22 12c0 5.523-4.477 10-10 10v2c6.627 0 12-5.373 12-12h-2ZM12 2c5.523 0 10 4.477 10 10h2c0-6.627-5.373-12-12-12v2ZM2 12C2 6.477 6.477 2 12 2V0C5.373 0 0 5.373 0 12h2Zm10 10C6.477 22 2 17.523 2 12H0c0 6.627 5.373 12 12 12v-2Zm0 3c7.18 0 13-5.82 13-13h-2c0 6.075-4.925 11-11 11v2ZM-1 12c0 7.18 5.82 13 13 13v-2C5.925 23 1 18.075 1 12h-2ZM12-1C4.82-1-1 4.82-1 12h2C1 5.925 5.925 1 12 1v-2Zm13 13c0-7.18-5.82-13-13-13v2c6.075 0 11 4.925 11 11h2Zm-11.5-1.09v-.5h-2v.5h2Zm0 6.545v-6.546h-2v6.546h2Zm0 .5v-.5h-2v.5h2Zm-2 1h1v-2h-1v2Zm-1-1.5v.5h2v-.5h-2Zm0-6.546v6.546h2v-6.546h-2Zm0-.5v.5h2v-.5h-2Zm2-1h-1v2h1v-2Zm-.5-.773a2.09 2.09 0 0 0 2.09-2.09h-2c0 .05-.04.09-.09.09v2Zm-2.09-2.09A2.09 2.09 0 0 0 12 8.635v-2a.09.09 0 0 1-.09-.09h-2ZM12 4.454a2.09 2.09 0 0 0-2.09 2.09h2c0-.05.04-.09.09-.09v-2Zm2.09 2.09A2.09 2.09 0 0 0 12 4.455v2c.05 0 .09.04.09.09h2Z",
  mask: "url(#icon-v2-info-mask)"
})));

const IconWarning2$1 = makeIcon('warning-2', 25, 23, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("path", {
  d: "m23.619 18.105-9-15.857c-.944-1.664-3.35-1.665-4.294 0l-9 15.857c-.934 1.644.258 3.687 2.147 3.687h18c1.89 0 3.079-2.044 2.147-3.687Z"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12.472 7.135v7.182M12.472 17.451v.249"
})));

// ---------------------------------------------------------------------------

const IconWarning$1 = props => {
  if (props.modifier === 'inverted') {
    console.warn('IconWarning cannot be inverted');
  }
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 22,
    height: 22
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-warning', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    xmlns: "http://www.w3.org/2000/svg",
    xmlnsXlink: "http://www.w3.org/1999/xlink"
  }), /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("path", {
    id: "icon-warning",
    d: "M14.412 13.098l-4.75-8.906a.75.75 0 0 0-1.324 0l-4.75 8.906a.75.75 0 0 0 .662 1.104h9.5a.75.75 0 0 0 .662-1.104zM9 12.702a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5zm.75-2.25h-1.5v-3h1.5v3z"
  })), /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "11",
    cy: "11",
    fill: "var(--rootkit__palette--yellow600)",
    r: "11",
    stroke: "none"
  }), /*#__PURE__*/React.createElement("g", {
    transform: "translate(2 1)"
  }, /*#__PURE__*/React.createElement("mask", {
    fill: "#fff",
    id: "icon-warning-b",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("use", {
    xlinkHref: "#icon-warning"
  })), /*#__PURE__*/React.createElement("g", {
    fill: "#fff",
    mask: "url(#icon-warning-b)",
    stroke: "none"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M0 0h18v18H0z"
  }))))));
};

// ---------------------------------------------------------------------------

const IconWrench$1 = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const defaultDimensions = {
    width: 14,
    height: 14
  };
  const dimensions = getDimensions(defaultDimensions, props.size);
  const classes = classNames('icon', {
    [`icon--${props.modifier}`]: !isNil(props.modifier)
  }, 'icon-wrench', props.addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: `${dimensions.width}px`,
      height: `${dimensions.height}px`
    }
  }), /*#__PURE__*/React.createElement("svg", _extends({}, props.aria, {
    viewBox: `0 0 ${defaultDimensions.width} ${defaultDimensions.height}`,
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }), /*#__PURE__*/React.createElement("path", {
    fill: "none",
    d: "M12.8412 3.1219L11.1585 4.79696L9.19491 2.84263L10.8771 1.16757C10.4762 1.05343 10.0611 0.997045 9.64435 1.00012C8.8669 1.06693 8.14413 1.4275 7.62317 2.00845C7.10222 2.5894 6.82224 3.34704 6.84022 4.12715C6.83649 4.4332 6.89378 4.73693 7.00877 5.02059L1.62469 9.76595C1.43803 9.93252 1.28671 10.1349 1.17969 10.361C1.07266 10.5871 1.01212 10.8324 1.00164 11.0823C0.991162 11.3323 1.03096 11.5818 1.11869 11.8161C1.20641 12.0504 1.34026 12.2647 1.51233 12.4463V12.4463C1.68863 12.6304 1.9021 12.7748 2.13857 12.8701C2.37503 12.9653 2.62905 13.009 2.88374 12.9985C3.13843 12.9879 3.38795 12.9232 3.6157 12.8087C3.84345 12.6942 4.04421 12.5325 4.20464 12.3345L8.97128 6.97437C9.48073 7.12355 10.0164 7.16076 10.5416 7.08346C11.0951 6.96894 11.6069 6.7057 12.022 6.32205C12.4371 5.9384 12.7397 5.44884 12.8974 4.90605C13.0473 4.31793 13.0278 3.69942 12.8412 3.1219V3.1219Z",
    strokeMiterlimit: "10",
    strokeLinecap: "square"
  })));
};

// ---------------------------------------------------------------------------

const IconAccount = IconAccount$1;
const IconAccountSettings = IconAccountSettings$1;
const IconAddCircle = IconAddCircle$1;
const IconArrowDown = IconArrowDown$1;
const IconArrowThick = IconArrowThick$1;
const IconArrowTrend = IconArrowTrend$1;
const IconArrowUp = IconArrowUp$1;
const IconAttack = IconAttack$1;
const IconAuthorized = IconAuthorized$1;
const IconAuthorizedPartial = IconAuthorizedPartial$1;
const IconCaret = IconCaret$1;
const IconCaretThin = IconCaretThin$1;
const IconCascade = IconCascade$1;
const IconChainBreak = IconChainBreak$1;
const IconChainBreakDiag = IconChainBreakDiag$1;
const IconChainDiag = IconChainDiag$1;
const IconCheck = IconCheck$1;
const IconCli = IconCli$1;
const IconClipboard = IconClipboard$1;
const IconClose = IconClose$1;
const IconCloud = IconCloud$1;
const IconComment = IconComment$1;
const IconCompany = IconCompany$1;
const IconCompass = IconCompass$1;
const IconConfidence = IconConfidence$1;
const IconCopyPaste = IconCopyPaste$1;
const IconCustomer = IconCustomer$1;
const IconDashboard = IconDashboard$1;
const IconDate = IconDate$1;
const IconDeny = IconDeny$1;
const IconDetails = IconDetails$1;
const IconDockBottom = IconDockBottom$1;
const IconDockRight = IconDockRight$1;
const IconDomains = IconDomains$1;
const IconDrawer = IconDrawer$1;
const IconEdit = IconEdit$1;
const IconExternalLink = IconExternalLink$1;
const IconEye = IconEye$1;
const IconFailure = IconFailure$1;
const IconFilter = IconFilter$1;
const IconFilterDismiss = IconFilterDismiss$1;
const IconGrabHandle = IconGrabHandle$1;
const IconHelp = IconHelp$1;
const IconHoc = IconHoc$1;
const IconIPs = IconIPs$1;
const IconImage = IconImage$1;
const IconImpact = IconImpact$1;
const IconInfo = IconInfo$1;
const IconLink = IconLink$1;
const IconList = IconList$1;
const IconLock = IconLock$1;
const IconLockAccount = IconLockAccount$1;
const IconLogout = IconLogout$1;
const IconMap = IconMap$1;
const IconMark = IconMark$1;
const IconMenu = IconMenu$1;
const IconMinus = IconMinus$1;
const IconMore = IconMore$1;
const IconNavCaret = IconNavCaret$1;
const IconNavCollapse = IconNavCollapse$1;
const IconNavExpand = IconNavExpand$1;
const IconNetworks = IconNetworks$1;
const IconNo = IconNo$1;
const IconNote = IconNote$1;
const IconNotifications = IconNotifications$1;
const IconPencil = IconPencil$1;
const IconPeople = IconPeople$1;
const IconPlayer = IconPlayer$1;
const IconPlus = IconPlus$1;
const IconPolicy = IconPolicy$1;
const IconPorts = IconPorts$1;
const IconPromote = IconPromote$1;
const IconQuestion = IconQuestion$1;
const IconRAT = IconRAT$1;
const IconRecon = IconRecon$1;
const IconRemove = IconRemove$1;
const IconRibbon = IconRibbon$1;
const IconRibbonNew = IconRibbonNew$1;
const IconRobot = IconRobot$1;
const IconSave = IconSave$1;
const IconSearch = IconSearch$1;
const IconSearchSmall = IconSearchSmall$1;
const IconSend = IconSend$1;
const IconSeparator = IconSeparator$1;
const IconServer = IconServer$1;
const IconServices = IconServices$1;
const IconShare = IconShare$1;
const IconSignPost = IconSignPost$1;
const IconSlideClosed = IconSlideClosed$1;
const IconSlideOpen = IconSlideOpen$1;
const IconSortAsc = IconSortAsc$1;
const IconSortDesc = IconSortDesc$1;
const IconSpinner = IconSpinner$1;
const IconStar = IconStar$1;
const IconStatus = IconStatus$1;
const IconStatusInProgress = IconStatusInProgress$1;
const IconStatusInvestigate = IconStatusInvestigate$1;
const IconStatusNone = IconStatusNone$1;
const IconStatusResolved = IconStatusResolved$1;
const IconStatusReviewed = IconStatusReviewed$1;
const IconStop = IconStop$1;
const IconSuccess = IconSuccess$1;
const IconTag = IconTag$1;
const IconTarget = IconTarget$1;
const IconTech = IconTech$1;
const IconThumbSearch = IconThumbSearch$1;
const IconTrash = IconTrash$1;
const IconUnauthorized = IconUnauthorized$1;
const IconUpdate = IconUpdate$1;
const IconUpload = IconUpload$1;
const IconUser = IconUser$1;
const IconUtils = _IconUtils;
const IconV2Close = IconV2Close$1;
const IconV2Collapse = IconV2Collapse$1;
const IconV2Expand = IconV2Expand$1;
const IconV2Info = IconV2Info$1;
const IconWarning = IconWarning$1;
const IconWarning2 = IconWarning2$1;
const IconWrench = IconWrench$1;
const isRandoriIcon = isRandoriIcon$1;

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------

// eslint-disable-next-line complexity
const Icon = ({
  kind,
  ...restProps
}) => {
  switch (kind) {
    case 'account':
      return /*#__PURE__*/React.createElement(IconAccount, restProps);
    case 'account-settings':
      return /*#__PURE__*/React.createElement(IconAccountSettings, restProps);
    case 'add-circle':
      return /*#__PURE__*/React.createElement(IconAddCircle, restProps);
    case 'arrow-thick':
      return /*#__PURE__*/React.createElement(IconArrowThick, restProps);
    case 'arrow-trend':
      return /*#__PURE__*/React.createElement(IconArrowTrend, restProps);
    case 'attack':
      return /*#__PURE__*/React.createElement(IconAttack, restProps);
    case 'caret':
      return /*#__PURE__*/React.createElement(IconCaret, restProps);
    case 'caret--thin':
      return /*#__PURE__*/React.createElement(IconSortDesc, restProps);
    case 'caret--thin-rev':
      return /*#__PURE__*/React.createElement(IconCaretThin, restProps);
    case 'cascade':
      return /*#__PURE__*/React.createElement(IconCascade, restProps);
    case 'chain-break':
      return /*#__PURE__*/React.createElement(IconChainBreak, restProps);
    case 'chain-break-diag':
      return /*#__PURE__*/React.createElement(IconChainBreakDiag, restProps);
    case 'chain-diag':
      return /*#__PURE__*/React.createElement(IconChainDiag, restProps);
    case 'check':
      return /*#__PURE__*/React.createElement(IconCheck, restProps);
    case 'cli':
      return /*#__PURE__*/React.createElement(IconCli, restProps);
    case 'clipboard':
      return /*#__PURE__*/React.createElement(IconClipboard, restProps);
    case 'close':
      return /*#__PURE__*/React.createElement(IconClose, restProps);
    case 'cloud':
      return /*#__PURE__*/React.createElement(IconCloud, restProps);
    case 'comment':
      return /*#__PURE__*/React.createElement(IconComment, restProps);
    case 'company':
      return /*#__PURE__*/React.createElement(IconCompany, restProps);
    case 'compass':
      return /*#__PURE__*/React.createElement(IconCompass, restProps);
    case 'confidence':
      return /*#__PURE__*/React.createElement(IconConfidence, restProps);
    case 'copy-paste':
      return /*#__PURE__*/React.createElement(IconCopyPaste, restProps);
    case 'customer':
      return /*#__PURE__*/React.createElement(IconCustomer, restProps);
    case 'dashboard':
      return /*#__PURE__*/React.createElement(IconDashboard, restProps);
    case 'date':
      return /*#__PURE__*/React.createElement(IconDate, restProps);
    case 'deny':
      return /*#__PURE__*/React.createElement(IconDeny, restProps);
    case 'domains':
      return /*#__PURE__*/React.createElement(IconDomains, restProps);
    case 'drawer':
      return /*#__PURE__*/React.createElement(IconDrawer, restProps);
    case 'edit':
      return /*#__PURE__*/React.createElement(IconEdit, restProps);
    case 'external-link':
      return /*#__PURE__*/React.createElement(IconExternalLink, restProps);
    case 'eye':
      return /*#__PURE__*/React.createElement(IconEye, restProps);
    case 'failure':
      return /*#__PURE__*/React.createElement(IconFailure, restProps);
    case 'filter':
      return /*#__PURE__*/React.createElement(IconFilter, restProps);
    case 'filter-dismiss':
      return /*#__PURE__*/React.createElement(IconFilterDismiss, restProps);
    case 'grab-handle':
      return /*#__PURE__*/React.createElement(IconGrabHandle, restProps);
    case 'help':
      return /*#__PURE__*/React.createElement(IconHelp, restProps);
    case 'hoc':
      return /*#__PURE__*/React.createElement(IconHoc, restProps);
    case 'image':
      return /*#__PURE__*/React.createElement(IconImage, restProps);
    case 'impact':
      return /*#__PURE__*/React.createElement(IconImpact, restProps);
    case 'info':
      return /*#__PURE__*/React.createElement(IconInfo, restProps);
    case 'ips':
      return /*#__PURE__*/React.createElement(IconIPs, restProps);
    case 'link':
      return /*#__PURE__*/React.createElement(IconLink, restProps);
    case 'list':
      return /*#__PURE__*/React.createElement(IconList, restProps);
    case 'lock':
      return /*#__PURE__*/React.createElement(IconLock, restProps);
    case 'lock-account':
      return /*#__PURE__*/React.createElement(IconLockAccount, restProps);
    case 'logout':
      return /*#__PURE__*/React.createElement(IconLogout, restProps);
    case 'map':
      return /*#__PURE__*/React.createElement(IconMap, restProps);
    case 'mark':
      return /*#__PURE__*/React.createElement(IconMark, restProps);
    case 'menu':
      return /*#__PURE__*/React.createElement(IconMenu, restProps);
    case 'minus':
      return /*#__PURE__*/React.createElement(IconMinus, restProps);
    case 'more':
      return /*#__PURE__*/React.createElement(IconMore, restProps);
    case 'nav-caret':
      return /*#__PURE__*/React.createElement(IconNavCaret, restProps);
    case 'nav-collapse':
      return /*#__PURE__*/React.createElement(IconNavCollapse, restProps);
    case 'nav-expand':
      return /*#__PURE__*/React.createElement(IconNavExpand, restProps);
    case 'networks':
      return /*#__PURE__*/React.createElement(IconNetworks, restProps);
    case 'no':
      return /*#__PURE__*/React.createElement(IconNo, restProps);
    case 'note':
      return /*#__PURE__*/React.createElement(IconNote, restProps);
    case 'notifications':
      return /*#__PURE__*/React.createElement(IconNotifications, restProps);
    case 'pencil':
      return /*#__PURE__*/React.createElement(IconPencil, restProps);
    case 'people':
      return /*#__PURE__*/React.createElement(IconPeople, restProps);
    case 'player':
      return /*#__PURE__*/React.createElement(IconPlayer, restProps);
    case 'plus':
      return /*#__PURE__*/React.createElement(IconPlus, restProps);
    case 'policy':
      return /*#__PURE__*/React.createElement(IconPolicy, restProps);
    case 'ports':
      return /*#__PURE__*/React.createElement(IconPorts, restProps);
    case 'promote':
      return /*#__PURE__*/React.createElement(IconPromote, restProps);
    case 'question':
      return /*#__PURE__*/React.createElement(IconQuestion, restProps);
    case 'rat':
      return /*#__PURE__*/React.createElement(IconRAT, restProps);
    case 'recon':
      return /*#__PURE__*/React.createElement(IconRecon, restProps);
    case 'remove':
      return /*#__PURE__*/React.createElement(IconRemove, restProps);
    case 'ribbon':
      return /*#__PURE__*/React.createElement(IconRibbon, restProps);
    case 'ribbon-new':
      return /*#__PURE__*/React.createElement(IconRibbonNew, restProps);
    case 'robot':
      return /*#__PURE__*/React.createElement(IconRobot, restProps);
    case 'save':
      return /*#__PURE__*/React.createElement(IconSave, restProps);
    case 'search':
      return /*#__PURE__*/React.createElement(IconSearch, restProps);
    case 'search--small':
      return /*#__PURE__*/React.createElement(IconSearchSmall, restProps);
    case 'send':
      return /*#__PURE__*/React.createElement(IconSend, restProps);
    case 'separator':
      return /*#__PURE__*/React.createElement(IconSeparator, restProps);
    case 'server':
      return /*#__PURE__*/React.createElement(IconServer, restProps);
    case 'services':
      return /*#__PURE__*/React.createElement(IconServices, restProps);
    case 'share':
      return /*#__PURE__*/React.createElement(IconShare, restProps);
    case 'sign-post':
      return /*#__PURE__*/React.createElement(IconSignPost, restProps);
    case 'slide--closed':
      return /*#__PURE__*/React.createElement(IconSlideClosed, restProps);
    case 'slide--open':
      return /*#__PURE__*/React.createElement(IconSlideOpen, restProps);
    case 'sort--asc':
      return /*#__PURE__*/React.createElement(IconSortAsc, restProps);
    case 'sort--desc':
      return /*#__PURE__*/React.createElement(IconSortDesc, restProps);
    case 'spinner':
      return /*#__PURE__*/React.createElement(IconSpinner, restProps);
    case 'star':
      return /*#__PURE__*/React.createElement(IconStar, restProps);
    case 'status':
      return /*#__PURE__*/React.createElement(IconStatus, restProps);
    case 'status--in-progress':
      return /*#__PURE__*/React.createElement(IconStatusInProgress, restProps);
    case 'status--investigate':
      return /*#__PURE__*/React.createElement(IconStatusInvestigate, restProps);
    case 'status--none':
      return /*#__PURE__*/React.createElement(IconStatusNone, restProps);
    case 'status--resolved':
      return /*#__PURE__*/React.createElement(IconStatusResolved, restProps);
    case 'status--reviewed':
      return /*#__PURE__*/React.createElement(IconStatusReviewed, restProps);
    case 'stop':
      return /*#__PURE__*/React.createElement(IconStop, restProps);
    case 'success':
      return /*#__PURE__*/React.createElement(IconSuccess, restProps);
    case 'tag':
      return /*#__PURE__*/React.createElement(IconTag, restProps);
    case 'target':
      return /*#__PURE__*/React.createElement(IconTarget, restProps);
    case 'technology':
      return /*#__PURE__*/React.createElement(IconTech, restProps);
    case 'thumb-search':
      return /*#__PURE__*/React.createElement(IconThumbSearch, restProps);
    case 'trash':
      return /*#__PURE__*/React.createElement(IconTrash, restProps);
    case 'trend-arrow--down':
      return /*#__PURE__*/React.createElement(IconArrowDown, restProps);
    case 'trend-arrow--up':
      return /*#__PURE__*/React.createElement(IconArrowUp, restProps);
    case 'update':
      return /*#__PURE__*/React.createElement(IconUpdate, restProps);
    case 'upload':
      return /*#__PURE__*/React.createElement(IconUpload, restProps);
    case 'user':
      return /*#__PURE__*/React.createElement(IconUser, restProps);
    case 'v2-collapse':
      return /*#__PURE__*/React.createElement(IconV2Collapse, restProps);
    case 'v2-expand':
      return /*#__PURE__*/React.createElement(IconV2Expand, restProps);
    case 'v2-info':
      return /*#__PURE__*/React.createElement(IconV2Info, restProps);
    case 'warning':
      return /*#__PURE__*/React.createElement(IconWarning, restProps);
    case 'warning-2':
      return /*#__PURE__*/React.createElement(IconWarning2, restProps);
    case 'wrench':
      return /*#__PURE__*/React.createElement(IconWrench, restProps);
    case 'dock-right':
      return /*#__PURE__*/React.createElement(IconDockRight, restProps);
    case 'dock-bottom':
      return /*#__PURE__*/React.createElement(IconDockBottom, restProps);
    case 'details':
      return /*#__PURE__*/React.createElement(IconDetails, restProps);
    case 'v2-close':
      return /*#__PURE__*/React.createElement(IconV2Close, restProps);
    case 'authorized':
      return /*#__PURE__*/React.createElement(IconAuthorized, restProps);
    case 'unauthorized':
      return /*#__PURE__*/React.createElement(IconUnauthorized, restProps);
    case 'authorized--partial':
      return /*#__PURE__*/React.createElement(IconAuthorizedPartial, restProps);
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------

const mapModifier = modifier => {
  switch (modifier) {
    case 'primary':
      return 'primary';
    case 'secondary':
    // new variants that were created for achieving Carbon look
    case 'overflow-menu':
    case 'overflow-menu--item':
    case 'filter-rule':
      return 'tertiary';
    case 'tertiary':
      return 'ghost';
    case 'dark':
      return 'secondary';
    default:
      return modifier;
  }
};

// ---------------------------------------------------------------------------

const _ButtonCarbonProxy = props => {
  const {
    addlClasses,
    children,
    disabled,
    icon,
    modifier,
    progress,
    render,
    useCarbon,
    ...restProps
  } = props;
  if (isNil(modifier)) {
    throw new DefaultPropError('Button is missing props.modifier');
  }
  const kind = mapModifier(modifier);
  const classes = classNames(addlClasses);
  return /*#__PURE__*/React.createElement(Button$1, _extends({
    className: classes,
    disabled: disabled,
    kind: kind,
    hasIconOnly: icon?.position === 'only',
    renderIcon: iProps => {
      if (!isNotNil(icon)) {
        return null;
      }
      if (isRandoriIcon(icon.kind)) {
        return null;
      }
      return /*#__PURE__*/React.createElement(CarbonIcon, _extends({
        kind: icon.kind,
        size: 16
      }, iProps));
    }
  }, restProps), children);
};

// ---------------------------------------------------------------------------

const getIcon = props => {
  if (isNil(props)) {
    return /*#__PURE__*/React.createElement(React.Fragment, null);
  }
  return isRandoriIcon(props.kind) ? /*#__PURE__*/React.createElement(Icon, {
    kind: props.kind,
    size: props.size,
    aria: props.aria
  }) : /*#__PURE__*/React.createElement("div", {
    className: "button-carbon-icon"
  }, /*#__PURE__*/React.createElement(CarbonIcon, {
    kind: props.kind,
    size: 16
  }));
};

// @see: https://blog.andrewbran.ch/polymorphic-react-components/#an-alternative-approach
// ---------------------------------------------------------------------------

const _ButtonLegacy = props => {
  if (isNil(props.render)) {
    throw new DefaultPropError('Button is missing props.render');
  }
  if (isNil(props.modifier)) {
    throw new DefaultPropError('Button is missing props.modifier');
  }

  // If asLegacyModifier is present, create a CSS class that combines it with modifier
  const modifier = isNotNil(props.asLegacyModifier) ? `${props.modifier}--${props.asLegacyModifier}` : props.modifier;
  const classes = classNames('button', `button--${modifier}`, {
    'button--icon': isNotNil(props.icon)
  }, {
    'button--is-loading': isNotNil(props.progress)
  }, {
    [`button--icon--${props.icon?.position}`]: isNotNil(props.icon)
  }, props.addlClasses);
  return props.render({
    disabled: props.disabled,
    className: classes,
    children: /*#__PURE__*/React.createElement(React.Fragment, null, isNotNil(props.progress) ? /*#__PURE__*/React.createElement("span", {
      className: "button-progress",
      style: {
        width: `${props.progress}%`
      }
    }) : null, /*#__PURE__*/React.createElement("div", {
      className: "button-flex"
    }, isNotNil(props.icon) && props.icon.position === 'only' ? getIcon(props.icon) : /*#__PURE__*/React.createElement(React.Fragment, null, isNotNil(props.icon) && props.icon.position === 'left' && getIcon(props.icon), /*#__PURE__*/React.createElement("span", {
      className: "button-children"
    }, props.children), isNotNil(props.icon) && props.icon.position === 'right' && getIcon(props.icon))))
  });
};

// ---------------------------------------------------------------------------

const log$a = debug('ROOTKIT:button');
const Button = props => {
  if (isNotNil(props.useCarbon) && !props.useCarbon && props.onClick) {
    console.warn('Passing onClick to legacy button is a noop');
    log$a('Passing onClick to legacy button is a noop');
  }
  if (isNotNil(props.useCarbon) && props.useCarbon) {
    return /*#__PURE__*/React.createElement(_ButtonCarbonProxy, props);
  }
  return /*#__PURE__*/React.createElement(_ButtonLegacy, props);
};
const defaultProps$e = {
  useCarbon: false,
  modifier: 'primary',
  disabled: false,
  // eslint-disable-next-line react/display-name
  render: bProps => {
    return /*#__PURE__*/React.createElement("button", bProps);
  }
};
Button.defaultProps = defaultProps$e;

// ---------------------------------------------------------------------------

const ButtonGroup = props => {
  const {
    addlClasses,
    children,
    modifier,
    ...restProps
  } = props;
  const classes = classNames('button-group', {
    [`button-group--${modifier}`]: isNotNil(modifier)
  }, addlClasses);
  return /*#__PURE__*/React.createElement("div", _extends({}, restProps, {
    className: classes
  }), children);
};

// ---------------------------------------------------------------------------

const ButtonTabs = props => {
  const [activeTab, setActiveTab] = React.useState(props.initialTab);
  React.useEffect(() => {
    setActiveTab(props.initialTab);
  }, [props.initialTab]);
  const classes = classNames('button-tabs', props.addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: classes
  }, props.tabs.map(tab => {
    const _handleClick = e => {
      if (isNotNil(tab.onClick)) {
        setActiveTab(tab.id);
        tab.onClick(e);
      } else {
        setActiveTab(tab.id);
      }
    };
    const isActive = tab.id === activeTab ? 'is-active' : undefined;
    return /*#__PURE__*/React.createElement(Button, {
      key: tab.id,
      disabled: tab.disabled,
      addlClasses: isActive,
      render: bProps => /*#__PURE__*/React.createElement("button", _extends({}, bProps, {
        type: "button",
        onClick: _handleClick
      }))
    }, tab.label);
  }));
};

// ---------------------------------------------------------------------------

const Card = props => {
  const classes = classNames('card', {
    'card--flex': isNotNil(props.flex) && props.flex
  }, {
    'card--grid-child': isNotNil(props.grid) && props.grid
  }, {
    'card--pad': isNotNil(props.pad) && props.pad
  }, {
    [`card--${props.modifier}`]: isNotNil(props.modifier)
  }, props.addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: classes
  }, props.children);
};
Card.defaultProps = {
  pad: true
};

// ---------------------------------------------------------------------------

function getCellStyle(cellIndex, layoutConfig) {
  const cellStyles = !isNil(layoutConfig) && !isNil(layoutConfig.cellStyles) ? layoutConfig.cellStyles : {};
  const cellStyle = cellStyles[cellIndex] ?? {};
  return cellStyle;
}
let Side = /*#__PURE__*/function (Side) {
  Side["right"] = "right";
  Side["left"] = "left";
  return Side;
}({});

var AccordionCardUtils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Side: Side,
  getCellStyle: getCellStyle
});

// ---------------------------------------------------------------------------

function isCellWithProps(el) {
  return el.el !== undefined;
}
const Row$2 = props => {
  const {
    cells,
    addlClasses,
    ...restProps
  } = props;
  const rowClasses = classNames({
    'accordion-card-row': !props.asColumnFlow
  }, {
    'accordion-card-column': props.asColumnFlow
  }, addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: rowClasses
  }, cells.map((Cell, i) => {
    const cellStyle = getCellStyle(i, props.layoutConfig);
    if (isCellWithProps(Cell)) {
      const cellClasses = classNames('accordion-card-cell', Cell.addlClasses);
      return /*#__PURE__*/React.createElement("div", {
        key: i,
        className: cellClasses,
        style: cellStyle
      }, /*#__PURE__*/React.createElement(Cell.el, restProps));
    } else {
      const cellClasses = classNames('accordion-card-cell');
      return /*#__PURE__*/React.createElement("div", {
        key: i,
        className: cellClasses,
        style: cellStyle
      }, /*#__PURE__*/React.createElement(Cell, restProps));
    }
  }));
};

// ---------------------------------------------------------------------------

const Header$2 = props => {
  const classes = classNames('accordion-card-header', {
    ['accordion-card-header--hide']: props.asColumnFlow
  }, {
    ['accordion-card-header--nested']: props.isNested
  }, props.addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: classes
  }, props.labels.map((Label, i) => {
    const cellStyle = getCellStyle(i, props.layoutConfig);
    if (isCellWithProps(Label)) {
      const cellClasses = classNames('accordion-card-cell', 'accordion-card-cell--header', Label.addlClasses);
      return /*#__PURE__*/React.createElement("div", {
        className: cellClasses,
        key: i,
        style: cellStyle
      }, /*#__PURE__*/React.createElement(Label.el, null));
    } else {
      const cellClasses = classNames('accordion-card-cell', 'accordion-card-cell--header');
      return /*#__PURE__*/React.createElement("div", {
        className: cellClasses,
        key: i,
        style: cellStyle
      }, /*#__PURE__*/React.createElement(Label, null));
    }
  }));
};

// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------

const Meta = props => {
  const metaClasses = classNames('accordion-card-meta', {
    ['accordion-card-meta--nested']: props.isNested
  }, props.addlClasses);
  const style = !isNil(props.style) ? props.style : {};
  return /*#__PURE__*/React.createElement("div", {
    className: metaClasses,
    style: style
  }, props.render({
    isExpanded: props.isExpanded,
    isNested: props.isNested,
    layoutConfig: props.layoutConfig
  }));
};

// ---------------------------------------------------------------------------

const Summary = props => {
  const initialState = isNotNil(props.initialState) && props.initialState.isOpen;
  const [_isExpanded, _toggleIsExpanded] = React.useState(initialState);
  const isExpanded = isNotNil(props.manager) ? props.manager.isExpanded : _isExpanded;
  const toggleIsExpanded = isNotNil(props.manager) ? props.manager.toggleIsExpanded : _toggleIsExpanded;
  const wrapperClasses = classNames('accordion-card', {
    ['accordion-card--nested']: props.isNested
  }, {
    ['is-expanded']: isExpanded
  }, props.addlClasses);
  const iconClasses = classNames('accordion-card-expand', {
    ['is-expanded']: isExpanded
  });
  const primaryClasses = classNames('accordion-card-primary', {
    ['accordion-card-primary--nested']: props.isNested
  });
  const secondaryClasses = classNames('accordion-card-secondary');

  // @TODO: Expanding CardRow should be nicely animated
  //
  // @see: https://developers.google.com/web/updates/2017/03/performant-expand-and-collapse
  // @see: https://medium.com/@valdrinkoshi/performant-expand-collapse-animations-93d99e80f7f
  return /*#__PURE__*/React.createElement("div", {
    className: wrapperClasses
  }, /*#__PURE__*/React.createElement("div", {
    className: primaryClasses
  }, isNotNil(props.secondary) && /*#__PURE__*/React.createElement("div", {
    className: iconClasses,
    onClick: () => toggleIsExpanded(!isExpanded)
  }, isExpanded ? /*#__PURE__*/React.createElement(Icon, {
    kind: "minus"
  }) : /*#__PURE__*/React.createElement(Icon, {
    kind: "plus"
  })), props.primary({
    asColumnFlow: props.asColumnFlow,
    isExpanded,
    isNested: props.isNested
  })), isNotNil(props.secondary) && isExpanded && /*#__PURE__*/React.createElement("div", {
    className: secondaryClasses
  }, props.secondary({
    asColumnFlow: props.asColumnFlow,
    isExpanded,
    isNested: true
  })));
};

// ---------------------------------------------------------------------------

const AccordionCard = {
  Header: Header$2,
  Meta: Meta,
  Row: Row$2,
  Summary: Summary,
  Utils: AccordionCardUtils
};

// ---------------------------------------------------------------------------

const defaultProps$d = {
  tagName: 'div',
  isCollapsed: true
};
const CollapseHeight = props => {
  // @see: https://stackoverflow.com/questions/69119542/creating-react-element-dynamically-in-typescript

  const {
    addlClasses,
    isCollapsed,
    tagName = 'div',
    ...restProps
  } = props;
  const htmlEl = React.useRef(null);
  const classes = classNames('rootkit__collapsible-content', addlClasses);
  if (isNil(isCollapsed)) {
    throw new DefaultPropError('Missing props.isCollapsed');
  }
  if (isNil(tagName)) {
    throw new DefaultPropError('Missing props.tagName');
  }
  const contentStyles = {
    maxHeight: isCollapsed ? 0 : 9999,
    // needs to be bigger than expected height
    overflow: isCollapsed ? 'hidden' : 'visible',
    transition: isCollapsed ? 'max-height 0.3s cubic-bezier(0, 1, 0, 1)' : 'max-height 0.3s cubic-bezier(1, 0, 1, 0)'
  };
  const elementProps = {
    ...restProps,
    className: classes,
    ref: htmlEl,
    style: contentStyles
  };
  return /*#__PURE__*/React.createElement(tagName, elementProps);
};
CollapseHeight.defaultProps = defaultProps$d;

// A better name
const Collapse = CollapseHeight;

// ---------------------------------------------------------------------------

class CollapsibleCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isCollapsed: this.props.isCollapsed || false
    };
  }
  render() {
    const classes = classNames('card', 'collapsible-card', {
      'card--pad': this.props.pad
    }, {
      'collapsible-card--is-collapsed': this.state.isCollapsed
    }, this.props.addlClasses);
    return /*#__PURE__*/React.createElement("div", {
      className: classes
    }, /*#__PURE__*/React.createElement("div", {
      className: "collapsible-card-header collapsible-card-trigger",
      onClick: () => this.setState({
        isCollapsed: !this.state.isCollapsed
      })
    }, this.props.header, /*#__PURE__*/React.createElement("div", {
      className: "collapsible-card-trigger-caret"
    }, /*#__PURE__*/React.createElement(Icon, {
      kind: "caret"
    }))), /*#__PURE__*/React.createElement("div", {
      className: "collapsible-card-content"
    }, /*#__PURE__*/React.createElement(CollapseHeight, {
      isCollapsed: this.state.isCollapsed
    }, this.props.children)));
  }
}
_defineProperty(CollapsibleCard, "defaultProps", {
  pad: true
});

// ---------------------------------------------------------------------------

const ContentGridContext = /*#__PURE__*/React.createContext('small-screen');

// ---------------------------------------------------------------------------

const ContentGridContainer = props => {
  const {
    addlGridLayoutClasses,
    children,
    ...domAttributes
  } = props;
  const {
    screenSize,
    ref
  } = useGetScreenSize();
  const gridLayoutClasses = classNames('rootkit__grid-container__layout', screenSize, addlGridLayoutClasses);
  return /*#__PURE__*/React.createElement(ContentGridContext.Provider, {
    value: screenSize
  }, /*#__PURE__*/React.createElement("div", _extends({
    className: "rootkit__grid-container__wrapper",
    ref: ref
  }, domAttributes), /*#__PURE__*/React.createElement("div", {
    className: gridLayoutClasses
  }, children)));
};
const getScreenSize = width => {
  if (width >= 1200) {
    return 'large-screen';
  } else if (width >= 769) {
    return 'medium-screen';
  }
  return 'small-screen';
};
const useGetScreenSize = () => {
  const [measurement, ref] = useMeasure();
  const width = (measurement && measurement.width) ?? 0;
  const screenSize = getScreenSize(width);
  return {
    screenSize,
    ref
  };
};

// ---------------------------------------------------------------------------

const ContentGridItem = props => {
  const grid = React.useContext(ContentGridContext);
  const [columnStart, columnEnd] = Array.isArray(props.columnConfig) ? props.columnConfig : props.columnConfig[grid];
  const [rowStart, rowEnd] = isNotNil(props.rowConfig) ? props.rowConfig[grid] : [0, 0];
  const styles = {
    gridColumn: `${columnStart} / ${columnEnd}`,
    gridRow: `${rowStart} / ${rowEnd}`
  };
  return /*#__PURE__*/React.createElement("div", {
    style: styles
  }, props.children);
};

const contentWidth = ['content-start', 'content-end'];
const fullWidth = ['full-width-start', 'full-width-end'];
const ContentGridColumnConfig = {
  contentWidth,
  fullWidth
};

// ---------------------------------------------------------------------------

const log$9 = debug('ROOTKIT:PopperBase');
const isPopperStyle = styles => {
  return styles.popper !== undefined;
};
const PopperBase = props => {
  if (isNil(props.debug)) {
    throw new DefaultPropError('PopperBase is missing props.debug');
  }
  if (isNil(props.delayShow)) {
    throw new DefaultPropError('PopperBase is missing props.delayShow');
  }
  if (isNil(props.delayHide)) {
    throw new DefaultPropError('PopperBase is missing props.delayHide');
  }
  const _debug = props.debug;
  const [isHovering, setIsHovering] = React.useState(false);
  const [popperElement, setPopperElement] = React.useState(null);
  const [referenceElement, setReferenceElement] = React.useState(null);
  const {
    styles,
    attributes: _attributes
  } = usePopper(referenceElement, popperElement, props.options);
  const attributes = {
    popper: {},
    reference: {},
    ..._attributes
  };
  if (!isPopperStyle(styles)) {
    throw new Error('Popper styles are missing');
  }
  React.useLayoutEffect(() => {
    if (_debug) {
      log$9('debug = true');
      setIsHovering(true);
    }
  }, [_debug, setIsHovering]);
  const _onMouseOut = debounce(props.delayHide, setIsHovering);
  const _onMouseOver = debounce(props.delayShow, setIsHovering);
  const onMouseOut = () => {
    if (_debug) {
      return; // bail
    }
    _onMouseOver.cancel();
    _onMouseOut(false);
  };
  const onMouseOver = () => {
    if (_debug) {
      return; // bail
    }
    _onMouseOut.cancel();
    _onMouseOver(true);
  };

  // Dismiss popper if it gets clicked
  const onClick = () => {
    if (_debug) {
      return; // bail
    }
    _onMouseOut.cancel();
    _onMouseOver.cancel();
    setIsHovering(false);
  };
  return props.render({
    attributes,
    isHoveringRef: isHovering,
    onClick,
    onMouseOut,
    onMouseOver,
    setPopperElement,
    setReferenceElement,
    styles
  });
};
const defaultProps$c = {
  debug: false,
  delayHide: 0,
  delayShow: 0
};
PopperBase.defaultProps = defaultProps$c;

// ---------------------------------------------------------------------------

const InfoPopperView = props => {
  const testId = isNotNil(props.testId) ? {
    'data-testid': props.testId
  } : {};
  if (!isNotNil(props.iconKind)) {
    throw new DefaultPropError('InfoPopperView is missing props.iconKind');
  }
  if (!isNotNil(props.delayShow)) {
    throw new DefaultPropError('InfoPopperView is missing props.delayShow');
  }
  if (!isNotNil(props.delayHide)) {
    throw new DefaultPropError('InfoPopperView is missing props.delayHide');
  }
  const iconProps = {
    kind: props.iconKind,
    size: props.sizeOverride
  };
  const classes = classNames('info-popper', props.addlClasses);
  const tooltipClasses = classNames('info-popper-tooltip', {
    [`info-popper-tooltip--${props.tooltipSize}`]: isNotNil(props.tooltipSize)
  }, props.addlTooltipClasses);
  const delayHide = props.isInteractive ?? false ? props.delayHide : 0;
  return /*#__PURE__*/React.createElement(PopperBase, {
    debug: props.debug,
    delayHide: delayHide,
    delayShow: props.delayShow,
    options: props.options,
    render: pProps => {
      const interactiveProps = {
        onMouseOut: pProps.onMouseOut,
        onMouseOver: pProps.onMouseOver
      };
      const addlProps = props.isInteractive ?? false ? interactiveProps : {};
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", _extends({
        className: classes,
        onClick: pProps.onClick,
        onMouseOut: pProps.onMouseOut,
        onMouseOver: pProps.onMouseOver,
        ref: pProps.setReferenceElement
      }, pProps.attributes.reference, testId), /*#__PURE__*/React.createElement(Icon, _extends({}, iconProps, {
        size: {
          width: 12,
          height: 12
        }
      }))), pProps.isHoveringRef && /*#__PURE__*/createPortal(/*#__PURE__*/React.createElement("div", _extends({
        className: tooltipClasses,
        ref: pProps.setPopperElement,
        style: pProps.styles.popper
      }, pProps.attributes.popper, addlProps), props.children), document.body));
    }
  });
};
const defaultProps$b = {
  delayHide: 100,
  delayShow: 100,
  iconKind: 'v2-info'
};
InfoPopperView.defaultProps = defaultProps$b;
const InfoPopper = InfoPopperView;

// ---------------------------------------------------------------------------

function withCatch({
  log
} = {}) {
  return function (CC) {
    var _Err;
    return _Err = class Err extends React.Component {
      constructor(props) {
        super(props);
        _defineProperty(this, "state", {
          hasError: false
        });
        this._handleReset = this._handleReset.bind(this);
      }

      // @see: https://github.com/piotrwitek/react-redux-typescript-guide/blob/master/playground/src/hoc/with-error-boundary.tsx
      componentDidCatch(error, errorInfo) {
        if (log) {
          log(error, errorInfo);
        }
        this.setState({
          hasError: true
        });
      }
      render() {
        return this.state.hasError ? /*#__PURE__*/React.createElement(CC, _extends({}, this.props, {
          onReset: this._handleReset
        })) : this.props.children;
      }

      // ---------------------------------------------------------------------

      _handleReset() {
        this.setState({
          hasError: false
        });
      }
    }, _defineProperty(_Err, "displayName", `withCatch(${getDisplayName(CC)})`), _Err;
  };
}

function GenericError() {
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", null, "Something went wrong!"));
}
withCatch()(GenericError);
const ErrorMessage = GenericError;

// ---------------------------------------------------------------------------

const Loader = props => {
  const classes = classNames('loader', props.addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: classes,
    "data-testid": "loader"
  }, /*#__PURE__*/React.createElement(Icon, {
    kind: "spinner",
    size: props.size
  }));
};

// ---------------------------------------------------------------------------

/**
 * @deprecated Use RenderEffect instead
 */
function withEffect({
  ErrorComponent,
  LoadingComponent,
  log
} = {}) {
  return function (CC) {
    var _Eff;
    const IsEff = props => LoadingComponent ? /*#__PURE__*/React.createElement(LoadingComponent, props) : /*#__PURE__*/React.createElement(Loader, null);
    const IsErr = props => ErrorComponent ? /*#__PURE__*/React.createElement(ErrorComponent, props) : /*#__PURE__*/React.createElement(ErrorMessage, null);
    return _Eff = class Eff extends React.Component {
      constructor(props) {
        super(props);
        _defineProperty(this, "_resolvingKey", undefined);
        _defineProperty(this, "state", {
          effResult: undefined,
          isErred: false,
          isResolved: false,
          prevKey: undefined
        });
        this._effect = this._effect.bind(this);
      }
      static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.shouldEffectKey !== prevState.prevKey) {
          return {
            isErred: false,
            isResolved: false,
            prevKey: nextProps.shouldEffectKey
          };
        }
        return null;
      }
      componentDidMount() {
        this._effect(this.props.shouldEffectKey);
      }
      componentWillUnmount() {
        this._resolvingKey = undefined;
      }
      componentDidUpdate() {
        if (this.state.isResolved === false) {
          this._effect(this.props.shouldEffectKey);
        }
      }
      render() {
        const {
          effect,
          shouldEffectKey,
          ...restProps
        } = this.props;
        if (this.state.isErred) {
          return /*#__PURE__*/React.createElement(IsErr, restProps);
        }
        if (!this.state.isResolved) {
          return /*#__PURE__*/React.createElement(IsEff, restProps);
        }
        const nextProps = {
          ...restProps,
          effResult: this.state.effResult
        };

        // eslint-disable-next-line
        // @ts-ignore
        return /*#__PURE__*/React.createElement(CC, nextProps);
      }

      // ---------------------------------------------------------------------

      _effect(key) {
        if (key === this._resolvingKey) {
          return; // already resolving
        }
        this._resolvingKey = key;
        this.props.effect().then(result => {
          if (key === this._resolvingKey) {
            this.setState(() => ({
              effResult: result,
              isResolved: true
            }));
          }
        }, err => {
          if (key === this._resolvingKey) {
            if (log) {
              log(err);
            }
            this.setState(() => ({
              isErred: true,
              isResolved: true
            }));
          }
        });
      }
    }, _defineProperty(_Eff, "displayName", `withEffect(${getDisplayName(CC)})`), _defineProperty(_Eff, "propTypes", {
      effect: PropTypes.func.isRequired,
      shouldEffectKey: PropTypes.string.isRequired
    }), _Eff;
  };
}

// ---------------------------------------------------------------------------

// @see: https://typescript-play.js.org/#code/GYVwdgxgLglg9mABAB0QCgJSIN4ChEGIBOAplCEUgApFwC2MAziQHSmNwA2AbiWgOQALEp05x+GXAF9cuKAE9kJRAFUwAIzgAPEgBMAPABUAfIgC8iQ4hJaoJMLsaIa9JiX0wwwEkVWmA-KqIAFyIYCS8RLKgkLAIiMBwcEbWtvaOOCihmOamLgzM+gCGYPKmUsZo2CiIUqGGGKGy+W76apo6BgBKZBRghoruhgDa-Mj8ALrG0zj4hKTklCiY0tFJVTVSGEA

// @see: https://functionalprogramming.slack.com/archives/C7BBY9A95/p1554246402104600
//
// From slack
// Karol Majewski [5:06 PM]
// *Long answer*
//
// @g This definition is what TypeScript docs refer to as a _type assertion in
// disguise_. In other words, since the method does not accept an argument
// related to `T`, it’s not really generic.
//
// ```type P = {
//     p: <T>() => Promise<T>
// }```
//
// Since `T` can be anything, you could as well just write `type P = { p: () => Promise<any> }`.
// Keep in mind there is a difference between these two:
//
// ```type Foo<T> = () => T
// type Bar = <T>() => T```
//
// As a rule of thumb, the latter should be used only when your function
// accepts arguments, for the type parameter resolution is deferred to the
// moment your function is called. And `() => Promise<string>` can never be
// assignable to `<T>() => Promise<T>`because we don’t know what `T` might be.
//
// *Short answer*
//
// Try tackling it this way:
//
// ```function p () {
//     return Promise.resolve('hello')
// }
//
// type Unboxed<T> =
//   T extends Promise<infer U>
//     ? U
//     : never
//
// function foo<T extends { p: () => Promise<any> }>({ p }: T): Promise<Unboxed<ReturnType<T['p']>>> {
//     return p()
// }
//
// foo({ p })```

// ---------------------------------------------------------------------------

/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/explicit-function-return-type */

// Tuning effect dispatching
//
// @see: https://github.com/facebook/create-react-app/issues/6880#issuecomment-486640921
// @see: https://stackoverflow.com/questions/54930197/react-hooks-dispatch-action-from-useeffect
//
// React hook lint rules advised against how RenderEffect was previously
// tuning the dispatching of effects. Another approach is to shift the state
// dependency back out to the consumer by way of React.useCallback(). If
// RenderEffect is performing a side-effect too frequently because the effect
// function being passed is constantly changing, then use React.useCallback to
// make the effection function identity stable.
//
// effect will update when `id` updates
// const _effect = React.useCallback(
//   () => new Promise((resolve, reject) => {
//     dispatch(Store.GET_DATA(id, { success: resolve, failure: reject }))
//   }),
//   [
//     dispatch,
//     props.match.params.id
//   ]
// )
//
// effect will be performed once
// const _effect = React.useCallback(
//   () => new Promise((resolve, reject) => {
//     dispatch(Store.INIT({ success: resolve, failure: reject }))
//   }),
//   []
// )

const log$8 = debug('ROOTKIT:RenderEffect');
function RenderEffect(props) {
  if (!isNotNil(props.effectStateDeps)) {
    throw new DefaultPropError('RenderEffect is missing props.effectStateDeps');
  }

  // eslint-disable-next-line no-magic-numbers
  if (props.effectStateDeps.length > 0) {
    log$8('effectStateDeps is deprecated. See https://github.com/RandoriDev/rootkit/blob/develop/src/hocs/effect/render-effect/render-effect.tsx#L71');
  }
  const _renderErrored = errResult => {
    return isNotNil(props.renderErrored) ? props.renderErrored({
      errResult
    }) : /*#__PURE__*/React.createElement(ErrorMessage, null);
  };
  const _renderLoading = () => {
    return isNotNil(props.renderLoading) ? props.renderLoading({}) : /*#__PURE__*/React.createElement(Loader, null);
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const [{
    isResolved,
    errResult,
    effResult
  }, dispatch] = React.useReducer(effReducer, {
    isResolved: false,
    errResult: undefined,
    effResult: undefined
  });
  const {
    effect
  } = props;
  React.useEffect(() => {
    let isCancelled = false;
    dispatch(RESET());
    log$8('dispatching effect');

    // @TODO: Cancel side-effecting promise
    //
    // As currently implemented, if this effect is cancelled, we still don't
    // cancel the resolution of the Promise. If, for example, the passed
    // effect itself dispatches to, say, a redux store, this *will* still
    // happen, as the internal side-effects of the effect are out-of-scope
    // of RenderEffect.
    effect().then(effResult => {
      if (!isCancelled) {
        dispatch(RESOLVE(effResult));
      }
    }, errResult => {
      if (!isCancelled) {
        dispatch(ERROR(errResult));
      }
    });

    // @see: https://reactjs.org/docs/hooks-effect.html#example-using-hooks-1
    return () => {
      // This effectively cancels the state resolution if the component
      // unmounts, or if the resolution of the effect re-runs.
      isCancelled = true;
    };
  },
  // @see: https://kentcdodds.com/blog/react-hooks-pitfalls
  // @see: https://dmitripavlutin.com/react-hooks-stale-closures/
  [effect]);
  if (isResolved) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    return isNotNil(errResult) ? _renderErrored(errResult) : props.render({
      effResult: effResult
    });
  } else {
    return _renderLoading();
  }
}
RenderEffect.defaultProps = {
  effectStateDeps: []
};
var TypeKeys = /*#__PURE__*/function (TypeKeys) {
  TypeKeys["ERROR"] = "ERROR";
  TypeKeys["RESET"] = "RESET";
  TypeKeys["RESOLVE"] = "RESOLVE";
  return TypeKeys;
}(TypeKeys || {});
function RESET() {
  return {
    type: TypeKeys.RESET
  };
}
function ERROR(payload) {
  return {
    type: TypeKeys.ERROR,
    payload
  };
}

// @TODO: `payload` could be typed as `T`
//
// Changing `payload: unknown` to `payload: T` *could* improve the type safety
// of RenderEffectState, but it requires additional adjustment with low
// payoff. I don't feel terrible about RenderEffectState['effResult']: any,
// because it's never exposed, and it interactss with data over a boundary
// anyways. For strong guarantees about effResult, for instance an effect that
// dispatches an API request, io-ts can be used to both validate the response
// and wrap the response in additional safeties.
function RESOLVE(payload) {
  return {
    type: TypeKeys.RESOLVE,
    payload
  };
}
function effReducer(state, action) {
  switch (action.type) {
    case TypeKeys.RESET:
      return {
        effResult: undefined,
        errResult: undefined,
        isResolved: false
      };
    case TypeKeys.RESOLVE:
      return {
        ...state,
        isResolved: true,
        effResult: action.payload
      };
    case TypeKeys.ERROR:
      return {
        ...state,
        isResolved: true,
        errResult: action.payload
      };
  }
}
const Test = {
  ERROR,
  RESET,
  RESOLVE,
  effReducer
};

// ---------------------------------------------------------------------------

const log$7 = debug('ROOTKIT:RenderEffectPopper');
function RenderEffectPopper(props) {
  const asType = typeof props.as;
  if (!isNotNil(props.delayShow)) {
    throw new DefaultPropError('RenderEffectPopper is missing props.delayShow');
  }
  if (!isNotNil(props.delayHide)) {
    throw new DefaultPropError('RenderEffectPopper is missing props.delayHide');
  }
  if (asType !== 'string' && asType !== 'undefined') {
    log$7("props.as should be a string. Otherwise, you're gonna have a bad time with props.ref");
  }
  const testId = isNotNil(props.testId) ? {
    'data-testid': props.testId
  } : {};
  const classes = classNames('render-effect-popper', props.addlClasses);
  const tooltipClasses = classNames('render-effect-popper-tooltip', {
    [`render-effect-popper-tooltip--${props.tooltipSize}`]: isNotNil(props.tooltipSize)
  }, props.addlTooltipClasses);
  const delayHide = props.isInteractive ?? false ? 100 : 0;
  const TriggerWrapper = emStyled.span`
  `;
  const _renderErrored = errResult => {
    return isNotNil(props.renderErrored) ? props.renderErrored({
      errResult
    }) : /*#__PURE__*/React.createElement(ErrorMessage, null);
  };
  const _renderLoading = () => {
    return isNotNil(props.renderLoading) ? props.renderLoading({}) : /*#__PURE__*/React.createElement(Loader, null);
  };
  return /*#__PURE__*/React.createElement(PopperBase, {
    debug: props.debug,
    delayHide: delayHide,
    delayShow: props.delayShow,
    options: props.options,
    render: pProps => {
      const interactiveProps = {
        onMouseOut: pProps.onMouseOut,
        onMouseOver: pProps.onMouseOver
      };
      const addlProps = props.isInteractive ?? false ? interactiveProps : {};
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TriggerWrapper, _extends({
        as: props.as,
        className: classes,
        onClick: pProps.onClick,
        onMouseOut: pProps.onMouseOut,
        onMouseOver: pProps.onMouseOver,
        ref: pProps.setReferenceElement
      }, pProps.attributes.reference, testId), props.renderTrigger({})), pProps.isHoveringRef && /*#__PURE__*/createPortal(/*#__PURE__*/React.createElement(RenderEffect, {
        effect: props.effect,
        renderErrored: err => {
          return /*#__PURE__*/React.createElement("div", _extends({
            className: tooltipClasses,
            ref: pProps.setPopperElement,
            style: pProps.styles.popper
          }, pProps.attributes.popper, addlProps), _renderErrored(err));
        },
        renderLoading: () => {
          return /*#__PURE__*/React.createElement("div", _extends({
            className: tooltipClasses,
            ref: pProps.setPopperElement,
            style: pProps.styles.popper
          }, pProps.attributes.popper, addlProps), _renderLoading());
        },
        render: resolution => {
          return /*#__PURE__*/React.createElement("div", _extends({
            className: tooltipClasses,
            ref: pProps.setPopperElement,
            style: pProps.styles.popper
          }, pProps.attributes.popper, addlProps), props.renderPopper(resolution));
        }
      }), document.body));
    }
  });
}
const defaultProps$a = {
  delayHide: 100,
  delayShow: 100
};
RenderEffectPopper.defaultProps = defaultProps$a;

// ---------------------------------------------------------------------------

const log$6 = debug('ROOTKIT:RenderPopper');
const RenderPopperView = props => {
  const asType = typeof props.as;
  if (!isNotNil(props.delayShow)) {
    throw new DefaultPropError('RenderPopperView is missing props.delayShow');
  }
  if (!isNotNil(props.delayHide)) {
    throw new DefaultPropError('RenderPopperView is missing props.delayHide');
  }
  if (asType !== 'string' && asType !== 'undefined') {
    log$6("props.as should be a string. Otherwise, you're gonna have a bad time with props.ref");
  }
  const testId = isNotNil(props.testId) ? {
    'data-testid': props.testId
  } : {};
  const classes = classNames('render-popper', props.addlClasses);
  const tooltipClasses = classNames('render-popper-tooltip', {
    [`render-popper-tooltip--${props.tooltipSize}`]: isNotNil(props.tooltipSize)
  }, props.addlTooltipClasses);
  const delayHide = props.isInteractive ?? false ? props.delayHide : 0;
  const Trigger = props.renderTrigger;
  const Tooltip = props.renderPopper;
  const TriggerWrapper = emStyled.span`
  `;
  return /*#__PURE__*/React.createElement(PopperBase, {
    debug: props.debug,
    delayHide: delayHide,
    delayShow: props.delayShow,
    options: props.options,
    render: pProps => {
      const interactiveProps = {
        onMouseOut: pProps.onMouseOut,
        onMouseOver: pProps.onMouseOver
      };
      const addlProps = props.isInteractive ?? false ? interactiveProps : {};
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TriggerWrapper, _extends({
        as: props.as,
        className: classes,
        onClick: pProps.onClick,
        onMouseOut: pProps.onMouseOut,
        onMouseOver: pProps.onMouseOver,
        ref: pProps.setReferenceElement
      }, pProps.attributes.reference, testId), /*#__PURE__*/React.createElement(Trigger, null)), pProps.isHoveringRef && /*#__PURE__*/createPortal(/*#__PURE__*/React.createElement("div", _extends({
        className: tooltipClasses,
        ref: pProps.setPopperElement,
        style: pProps.styles.popper
      }, pProps.attributes.popper, addlProps), /*#__PURE__*/React.createElement(Tooltip, null)), document.body));
    }
  });
};
const defaultProps$9 = {
  delayHide: 100,
  delayShow: 100
};
RenderPopperView.defaultProps = defaultProps$9;
const RenderPopper = RenderPopperView;

const variables = {
  fontHeading: "'Barlow', 'Helvetica Neue', Arial, Helvetica, sans-serif",
  fontBody: "'Open Sans', 'Helvetica Neue', Arial, Helvetica, sans-serif"
};
const h1 = `
  font-size: max(2rem, 3rem);
  font-weight: 500;
  font-family: ${variables.fontHeading};
`;
const h2 = `
  font-size: 2.25rem;
  font-weight: 500;
  font-family: ${variables.fontHeading};
`;
const h3 = `
  font-size: 1.5rem;
  font-weight: 600;
  font-family: ${variables.fontHeading};
`;
const h4 = `
  font-size: 1.25rem;
  font-weight: 600;
  font-family: ${variables.fontHeading};
`;
const h5 = `
  font-size: 1rem;
  font-weight: 700;
  font-family: ${variables.fontBody};
`;
const h6 = `
  font-size: 0.875rem;
  font-weight: 700;
  font-family: ${variables.fontBody};
`;
const body1 = `
  font-size: 1rem;
  font-weight: 400;
  font-family: ${variables.fontBody};
`;
const body2 = `
  font-size: 0.875rem;
  font-weight: 400;
  font-family: ${variables.fontBody};
`;
const caption = `
  font-size: 0.75rem;
  font-weight: 400;
  font-family: ${variables.fontBody};
`;
const table = caption;
const tooltip = caption;
const button = `
  font-weight: 0.875rem;
  font-family: ${variables.fontBody};
`;
const overline = `
  font-size: 0.625rem;
  font-weight: 600;
  font-family: ${variables.fontBody};
  letter-spacing: 1.5px;
  text-transform: uppercase;
`;
const field = `
  font-size: 1rem;
  font-family: ${variables.fontBody};
`;

// TODO: @emotion or other styled components are still a bit WIP. These styles
// are identical to the CSS styles defined by modules/typography, and these
// aren't being consumed yet — I'd like to clarify my approach here soon,
// using either a no-runtime CSS helper, or fully in on emotion. @emotion does
// seem like the community is kind of falling apart around it, so any
// technology choice here should be easily pivoted to something else.
const variantStyles = {
  body1,
  body2,
  button,
  caption,
  field,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  overline,
  table,
  tooltip
};

// ---------------------------------------------------------------------------

const variantToElement = {
  body1: 'p',
  body2: 'p',
  button: 'button',
  caption: 'span',
  field: 'span',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  overline: 'span',
  table: 'span',
  tooltip: 'span'
};
// @TODO: The intersection should be dependent on `as`

const Typography = props => {
  const {
    addlClasses,
    as,
    variant,
    ...domAttributes
  } = props;
  const elem = as ?? variantToElement[variant];
  const classes = classNames({
    [`rootkit__typography--${variant}`]: true
  }, addlClasses);

  // @TODO: Likely replace with React.createElement
  const Component = emStyled.span`
  `;
  return /*#__PURE__*/React.createElement(Component, _extends({}, domAttributes, {
    className: classes,
    as: elem
  }), props.children);
};

// ---------------------------------------------------------------------------

const DescriptionListRow = props => {
  const helperText = props.helperText;
  const termClasses = classNames('rootkit__description-list__term', props.addlTermClasses);
  const descClasses = classNames('rootkit__description-list__desc', props.addlDescClasses);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("dt", {
    className: termClasses
  }, isNotNilOrEmpty(helperText) && /*#__PURE__*/React.createElement(InfoPopper, {
    addlClasses: "rootkit__description-list__term--tooltip"
  }, helperText), /*#__PURE__*/React.createElement("div", {
    className: "rootkit__description-list__term--label"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "overline"
  }, props.term))), /*#__PURE__*/React.createElement("dd", {
    className: descClasses
  }, isString(props.desc) || props.desc === undefined ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, props.desc ?? '--')) : /*#__PURE__*/React.createElement(React.Fragment, null, props.desc(props.term))));
};

// ---------------------------------------------------------------------------

const DescriptionList = props => {
  const [measurements, ref] = useMeasure();
  const marginsOfColumnContainerInPixels = 16 * (props.columns.length - 1);
  const minWidthOfColumnContainerInPixels = 400;
  const width = measurements ? measurements.width : 0;
  const breakingWidth = minWidthOfColumnContainerInPixels * props.columns.length + marginsOfColumnContainerInPixels;
  const classes = classNames('rootkit__description-list', {
    ['rootkit__description-list--single']: width < breakingWidth
  }, props.addlClasses);
  return /*#__PURE__*/React.createElement("dl", {
    className: classes,
    ref: ref
  }, props.columns.map((column, i) => {
    return /*#__PURE__*/React.createElement("div", {
      key: i,
      className: "rootkit__description-list__column"
    }, column.map(descriptions => /*#__PURE__*/React.createElement(DescriptionListRow, _extends({
      key: descriptions.term
    }, descriptions))));
  }));
};

// ---------------------------------------------------------------------------

const useToggle = (initState = false) => {
  const [isToggled, setIsToggled] = React.useState(initState);
  const toggle = () => {
    setIsToggled(!isToggled);
  };
  return {
    isToggled,
    toggle
  };
};

// ---------------------------------------------------------------------------

// @see: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/details

const DetailsComponent = props => {
  const detailsClasses = classNames('rootkit__details', {
    'rootkit__details--is-collapsed': !props.isToggled
  });
  const summaryClasses = classNames('rootkit__details__summary', {
    'rootkit__details__summary--is-collapsed': !props.isToggled
  }, {
    'rootkit__details__summary--no-content': !isNotNilOrEmpty(props.content)
  });
  const triggerClasses = classNames('rootkit__details__trigger', {
    'rootkit__details__trigger--is-collapsed': !props.isToggled
  });
  const contentClasses = classNames('rootkit__details__content', {
    'rootkit__details__content--is-collapsed': !props.isToggled
  });
  return /*#__PURE__*/React.createElement("details", {
    className: detailsClasses,
    key: props.summary,
    open: props.isToggled
  }, /*#__PURE__*/React.createElement("summary", {
    className: summaryClasses,
    onClick: e => {
      // @see: https://github.com/facebook/react/issues/15486
      e.preventDefault();
      props.toggle();
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    as: "span",
    addlClasses: "rootkit__details__summary-label"
  }, props.summary), isNotNilOrEmpty(props.content) && /*#__PURE__*/React.createElement(Icon, {
    kind: "caret--thin-rev",
    addlClasses: triggerClasses
  })), isNotNilOrEmpty(props.content) && /*#__PURE__*/React.createElement(Collapse, {
    addlClasses: contentClasses,
    isCollapsed: !props.isToggled
  }, isString(props.content) ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, props.content)) : /*#__PURE__*/React.createElement(React.Fragment, null, props.content({
    isToggled: props.isToggled,
    toggle: props.toggle
  }))));
};

// ---------------------------------------------------------------------------

// @TODO: Investigate impl of dependent props
//
// @see: https://stackoverflow.com/questions/66524258/how-can-i-write-a-react-component-that-has-dependent-props-in-typescript
// @see: https://stevekinney.github.io/react-and-typescript/dependent-props
// @see: https://www.benmvp.com/blog/conditional-react-props-typescript/
// @see: https://www.youtube.com/watch?v=vXh4PFwZFGI

// export type DetailsStatefulWrapperProps = {
//   toggle?: undefined
// } & Omit<DetailsComponentProps, 'toggle'>

const DetailsStatefulWrapper = props => {
  const {
    isToggled,
    toggle
  } = useToggle(props.isToggled);
  return /*#__PURE__*/React.createElement(DetailsComponent, _extends({}, props, {
    isToggled: isToggled,
    toggle: toggle
  }));
};
const DetailsSubscribedWrapper = DetailsComponent;

// ---------------------------------------------------------------------------

const Details = props => {
  const {
    content,
    isToggled,
    summary,
    toggle
  } = props;

  // If the caller wants to provide its own state tracking, use that state,
  // instead of state provided by `useToggle`.
  if (isNotNil(toggle)) {
    if (isNil$1(isToggled)) {
      throw new DefaultPropError('isToggled must be provided if toggle is provided');
    }
    return /*#__PURE__*/React.createElement(DetailsSubscribedWrapper, {
      content: content,
      isToggled: isToggled,
      summary: summary,
      toggle: toggle
    });
  } else {
    return /*#__PURE__*/React.createElement(DetailsStatefulWrapper, {
      content: content,
      isToggled: isToggled ?? false,
      summary: summary
    });
  }
};

// ---------------------------------------------------------------------------

class DropdownView extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      isOpen: this.props.isOpen || false
    });
    _defineProperty(this, "handleClickOutside", () => {
      this.setState(() => ({
        isOpen: false
      }));
    });
    _defineProperty(this, "handleToggle", () => {
      this.setState(({
        isOpen
      }) => ({
        isOpen: !isOpen
      }));
    });
  }
  componentDidMount() {
    this.props.enableOnClickOutside();
  }
  componentWillUnmount() {
    this.props.disableOnClickOutside();
  }
  render() {
    const classes = classNames('dropdown', {
      [`dropdown--${this.props.modifier}`]: !isNil(this.props.modifier)
    }, {
      'dropdown--is-open': this.state.isOpen
    }, this.props.addlClasses);
    return /*#__PURE__*/React.createElement("div", {
      className: classes
    }, React.Children.map(this.props.children, child => {
      // @TODO: Refactor React.cloneElement to a render prop
      //
      // Using React.cloneElement breaks type safety, as there is no
      // ability to infer what children are or what props they'll
      // accept. A render prop approach will be type safe, but it's more
      // tightly coupled.
      //
      // A sorta-works shim is making the `DropdownChild` type have
      // optional types, so that they're not required at the call site.
      // For instance:
      //
      // <DropdownToggle render{() => ...} />
      // vs.
      // <DropdownToggle isOpen render{() => ...} />
      //
      // @see: https://stackoverflow.com/questions/42261783/how-to-assign-the-correct-typing-to-react-cloneelement-when-giving-properties-to
      // @see: https://stackoverflow.com/questions/41881912/typing-a-react-component-that-clones-its-children-to-add-extra-props-in-typescri
      return /*#__PURE__*/React.cloneElement(child, {
        isOpen: this.state.isOpen,
        handleToggle: this.handleToggle
      });
    }));
  }
}
const Dropdown = onClickOutside(DropdownView);

const __DEFAULT_MAX_HEIGHT__ = 600;

// ---------------------------------------------------------------------------

const DropdownContent = props => {
  const {
    isOpen,
    handleToggle
  } = props;
  const classes = classNames('dropdown-content', {
    [`dropdown-content--${props.modifier}`]: isNotNil(props.modifier)
  }, {
    'dropdown-content--is-open': props.isOpen
  }, props.addlClasses);
  const style = isNotNil(props.maxHeight) ? {
    maxHeight: `${props.maxHeight}px`,
    overflowY: 'auto',
    overflowX: 'hidden'
  } : {};
  return /*#__PURE__*/React.createElement("div", {
    className: classes,
    style: {
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: style
  }, props.render({
    isOpen,
    handleToggle
  })));
};
const defaultProps$8 = {
  maxHeight: __DEFAULT_MAX_HEIGHT__
};
DropdownContent.defaultProps = defaultProps$8;

// ---------------------------------------------------------------------------

class DropdownContainer extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      isOpen: isNotNil(this.props.isOpen) ? this.props.isOpen : false
    });
    // -------------------------------------------------------------------------
    _defineProperty(this, "handleToggle", () => {
      this.setState(({
        isOpen
      }) => ({
        isOpen: !isOpen
      }));
    });
    _defineProperty(this, "handleClick", isOpen => {
      this.setState(() => ({
        isOpen
      }));
    });
  }
  componentDidUpdate(_, prevState) {
    const isPrevStateDiff = prevState.isOpen !== this.state.isOpen;
    const shouldOpen = isPrevStateDiff && this.state.isOpen;
    const shouldClose = isPrevStateDiff && !this.state.isOpen;
    if (shouldOpen && isNotNil(this.props.onOpen)) {
      this.props.onOpen();
    } else if (shouldClose && isNotNil(this.props.onClose)) {
      this.props.onClose();
    }
  }
  render() {
    const openLeft = this.props.openLeft !== undefined;
    const dropdownClasses = classNames('dropdown-new', {
      'dropdown-new--is-open': this.state.isOpen
    }, this.props.addlClasses);
    const dropdownContentClasses = classNames('dropdown-new-content', {
      'dropdown-new-content--is-open': this.state.isOpen
    }, this.props.addlDropdownClasses, openLeft === true ? 'dropdown-open-left' : '');
    const dropdownContentStyles = isNotNil(this.props.maxContentHeight) ? {
      maxHeight: `${this.props.maxContentHeight}px`,
      display: 'flex',
      flexDirection: 'column'
    } : {};
    if (isNil(this.props.render)) {
      throw new DefaultPropError('Button is missing prop.render');
    }
    if (isNil(this.props.renderTrigger)) {
      throw new DefaultPropError('Button is missing prop.renderTrigger');
    }
    return /*#__PURE__*/React.createElement(DropdownNewView, {
      debug: this.props.debug,
      dropdownClasses: dropdownClasses,
      dropdownContentClasses: dropdownContentClasses,
      dropdownContentStyles: dropdownContentStyles,
      handleClick: this.handleClick,
      handleToggle: this.handleToggle,
      isOpen: this.state.isOpen,
      options: this.props.options,
      render: this.props.render,
      renderActions: this.props.renderActions,
      renderHeader: this.props.renderHeader,
      renderTrigger: this.props.renderTrigger,
      triggerLabel: this.props.triggerLabel,
      triggerModifier: this.props.triggerModifier
    });
  }
}
_defineProperty(DropdownContainer, "defaultProps", {
  isOpen: false,
  maxContentHeight: __DEFAULT_MAX_HEIGHT__,
  triggerModifier: 'secondary',
  renderTrigger: tProps => {
    const triggerClasses = classNames('dropdown-new-trigger', 'ignore-react-onclickoutside', {
      'dropdown-new-trigger--is-open': tProps.isOpen
    });
    if (isNil(tProps.triggerLabel)) {
      throw new DefaultPropError('DropdownNewView is missing props.triggerLabel');
    }
    return /*#__PURE__*/React.createElement(Button, {
      addlClasses: triggerClasses,
      modifier: tProps.triggerModifier,
      icon: {
        kind: 'caret--thin-rev',
        position: 'right'
      },
      render: bProps => /*#__PURE__*/React.createElement("button", _extends({}, bProps, {
        onClick: tProps.toggle,
        type: "button"
      }))
    }, tProps.triggerLabel);
  }
});
const DropdownNew = DropdownContainer;

// ---------------------------------------------------------------------------

const DropdownNewView = props => {
  const [popperElement, setPopperElement] = React.useState(null);
  const [referenceElement, setReferenceElement] = React.useState(null);
  const defaultOptions = props.options ?? {
    placement: 'bottom-start'
  };
  const options = {
    ...defaultOptions,
    modifiers: [{
      name: 'offset',
      options: {
        offset: [0, 4]
      }
    }]
  };
  const {
    styles,
    attributes
  } = usePopper(referenceElement, popperElement, options);
  const debug = props.debug ?? false;
  const popperClasses = classNames(props.dropdownContentClasses, 'ignore-react-onclickoutside');
  const dropdownContentStyles = {
    ...props.dropdownContentStyles,
    ...styles.popper
  };
  return /*#__PURE__*/React.createElement("div", {
    className: props.dropdownClasses
  }, /*#__PURE__*/React.createElement("div", _extends({
    className: "dropdown-new-trigger__wrapper",
    ref: setReferenceElement
  }, attributes.reference), props.renderTrigger({
    isOpen: props.isOpen,
    toggle: props.handleToggle,
    triggerLabel: props.triggerLabel,
    triggerModifier: props.triggerModifier
  })), (props.isOpen || debug) && /*#__PURE__*/createPortal(/*#__PURE__*/React.createElement(PortalContent, {
    attributes: attributes.popper,
    classes: popperClasses,
    isOpen: props.isOpen,
    onClick: props.handleClick,
    onToggle: props.handleToggle,
    render: props.render,
    renderActions: props.renderActions,
    renderHeader: props.renderHeader,
    setPopperElement: setPopperElement,
    styles: dropdownContentStyles
  }), document.body));
};
class _PortalContent extends React.Component {
  constructor(...args) {
    super(...args);
    // -------------------------------------------------------------------------
    _defineProperty(this, "handleClickOutside", () => {
      this.props.onClick(false);
    });
  }
  componentDidMount() {
    this.props.enableOnClickOutside();
  }
  componentWillUnmount() {
    this.props.disableOnClickOutside();
  }
  render() {
    return /*#__PURE__*/React.createElement("div", _extends({
      className: this.props.classes,
      ref: this.props.setPopperElement,
      style: this.props.styles
    }, this.props.attributes), isNotNil(this.props.renderHeader) && this.props.renderHeader({
      isOpen: this.props.isOpen,
      toggle: this.props.onToggle
    }), this.props.render({
      isOpen: this.props.isOpen,
      toggle: this.props.onToggle
    }), isNotNil(this.props.renderActions) && this.props.renderActions({
      isOpen: this.props.isOpen,
      toggle: this.props.onToggle
    }));
  }
}
const PortalContent = onClickOutside(_PortalContent);

// ---------------------------------------------------------------------------

const DropdownToggle = props => {
  const {
    isOpen,
    handleToggle
  } = props;
  const classes = classNames('dropdown-toggle', {
    [`dropdown-toggle--${props.modifier}`]: isNotNil(props.modifier)
  }, {
    'dropdown-toggle--is-open': props.isOpen
  }, props.addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: classes
  }, props.render({
    isOpen,
    handleToggle
  }));
};

// ---------------------------------------------------------------------------

const BlockOptions = props => {
  const {
    addOption,
    canAdd,
    isBaseOption,
    removeOption,
    rule,
    setApplyButtonDisabled
  } = props;
  function handleClickAdd(blockType) {
    setApplyButtonDisabled && setApplyButtonDisabled(true);
    addOption && addOption(blockType);
  }
  function handleClickRemove(rule) {
    setApplyButtonDisabled && setApplyButtonDisabled(false);
    removeOption(rule);
  }
  return /*#__PURE__*/React.createElement(ButtonGroup, {
    addlClasses: "options-button-group"
  }, canAdd && addOption ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    addlClasses: "addRule",
    icon: {
      kind: 'add-circle',
      position: 'left'
    },
    modifier: "ghost",
    asLegacyModifier: "secondary",
    render: _props => /*#__PURE__*/React.createElement("button", _extends({}, _props, {
      type: "button",
      onClick: () => handleClickAdd(BlockType.CrudRule)
    }))
  }, "Add Rule"), /*#__PURE__*/React.createElement(Button, {
    icon: {
      kind: 'add-circle',
      position: 'left'
    },
    modifier: "ghost",
    asLegacyModifier: "secondary",
    render: _props => /*#__PURE__*/React.createElement("button", _extends({}, _props, {
      type: "button",
      onClick: () => handleClickAdd(BlockType.CrudRuleGroup)
    }))
  }, "Add Rule Group")) : null, !isBaseOption && /*#__PURE__*/React.createElement(Button, {
    icon: {
      kind: 'trash',
      position: 'left'
    },
    modifier: "danger--ghost",
    asLegacyModifier: canAdd && addOption ? 'secondary' : 'tertiary',
    render: _props => /*#__PURE__*/React.createElement("button", _extends({}, _props, {
      type: "button",
      onClick: () => handleClickRemove(rule)
    }))
  }, canAdd && addOption ? 'Delete' : ''));
};

// ---------------------------------------------------------------------------

const Conjunction = props => {
  const {
    rule,
    updateRule,
    hasActiveTab
  } = props;
  function setConjunction(condition) {
    updateRule(condition);
  }
  return /*#__PURE__*/React.createElement(ButtonTabs, {
    addlClasses: "conjunction-button-group",
    initialTab: hasActiveTab ? rule.condition : undefined,
    tabs: [{
      id: 'AND',
      label: 'AND',
      onClick: () => setConjunction('AND')
    }, {
      id: 'OR',
      label: 'OR',
      onClick: () => setConjunction('OR')
    }]
  });
};

// ---------------------------------------------------------------------------

const BASE_RULE = {
  field: '--',
  id: '--',
  input: 'text',
  type: 'unset',
  ui_id: '--'
};
const makeBaseGroup = () => ({
  condition: 'AND',
  rules: [BASE_RULE]
});

// same as RuleGroupProps but only for renderRuleGroup

// ---------------------------------------------------------------------------

const Rule = props => {
  const {
    renderRule
  } = props;
  const ref = React.useRef(null);
  return /*#__PURE__*/React.createElement("span", {
    className: "rule-block",
    ref: ref
  }, renderRule(props));
};

// ---------------------------------------------------------------------------

const RuleGroup = props => {
  const groupRef = React.useRef(null);
  const isBaseBlock = props.rules.ui_id === props.baseId;
  const updateRule = path => updates => {
    const ruleLens = lensPath(path);
    const newSet = set(ruleLens, updates, props.baseRules);
    props.updateQuery(newSet);
  };
  const insertRule = path => optionType => {
    const insertLens = lensPath(path);
    const insertBlock = optionType === BlockType.CrudRule ? BASE_RULE : makeBaseGroup();
    const newSet = set(insertLens, insertBlock, props.baseRules);
    props.updateQuery(newSet);
  };
  const removeRule = path => () => {
    const newSet = dissocPath(path, props.baseRules);
    props.updateQuery(newSet);
  };
  const isCustomBLock = props.rules.ui_id && props.rules.ui_id !== props.baseId;
  if (isRuleGroup(props.rules)) {
    const hasActiveConjTab = props.rules.rules.length > 1 || isBaseBlock;
    const blockClasses = classNames('block', {
      ['delete-only']: isCustomBLock
    });
    const blockControlClasses = classNames('block-controls', {
      ['base-block']: isBaseBlock
    }, {
      ['custom-block']: isCustomBLock
    });
    const renderRuleGroupProps = {
      ...props,
      // eslint-disable-next-line
      updateRule: updateRule([...props.ruleLensPath]),
      // eslint-disable-next-line
      removeOption: removeRule([...props.ruleLensPath])
    };
    return /*#__PURE__*/React.createElement("div", {
      className: blockControlClasses
    }, /*#__PURE__*/React.createElement("div", {
      className: blockClasses,
      ref: groupRef
    }, !props.rules.ui_id || props.rules.ui_id === props.baseId ? /*#__PURE__*/React.createElement(Conjunction, {
      hasActiveTab: hasActiveConjTab,
      rule: props.rules
      // eslint-disable-next-line
      ,
      updateRule: updateRule([...props.ruleLensPath, 'condition'])
    }) : null, /*#__PURE__*/React.createElement(BlockOptions, {
      rule: props.rules
      // eslint-disable-next-line
      ,
      addOption: insertRule([...props.ruleLensPath, 'rules', props.rules.rules.length]),
      canAdd: !props.rules.ui_id || props.rules.ui_id === props.baseId,
      setApplyButtonDisabled: props.setDisabled
      // eslint-disable-next-line
      ,
      removeOption: removeRule([...props.ruleLensPath]),
      isBaseOption: props.rules.ui_id === props.baseId
    })), isCustomBLock ? props.renderRuleGroup(renderRuleGroupProps) : elemT(props.rules.rules).map((rule, i) => {
      const ui_key = `${rule?.ui_id ?? ''}${i}`;
      return /*#__PURE__*/React.createElement(RuleGroup, {
        baseId: props.baseId
        // eslint-disable-next-line
        ,
        ruleLensPath: [...props.ruleLensPath, 'rules', i],
        updateQuery: props.updateQuery,
        key: ui_key,
        renderRule: props.renderRule,
        renderRuleGroup: props.renderRuleGroup,
        baseRules: props.baseRules,
        setDisabled: props.setDisabled,
        rules: rule
      });
    }));
  } else {
    return /*#__PURE__*/React.createElement(Rule, {
      rule: props.rules,
      renderRule: props.renderRule
      // eslint-disable-next-line
      ,
      updateRule: updateRule([...props.ruleLensPath])
      // eslint-disable-next-line
      ,
      removeOption: removeRule([...props.ruleLensPath])
    });
  }
};

// ---------------------------------------------------------------------------

const Fieldset = props => {
  if (isNil(props.modifier)) {
    throw new DefaultPropError('Fieldset is missing props.modifier');
  }
  const classes = classNames('form-fieldset', `form-fieldset--${props.modifier}`);
  return /*#__PURE__*/React.createElement("fieldset", {
    className: classes
  }, isNotNil(props.legend) && /*#__PURE__*/React.createElement("legend", null, props.legend), props.children);
};
const defaultProps$7 = {
  modifier: 'primary'
};
Fieldset.defaultProps = defaultProps$7;

// ---------------------------------------------------------------------------

const Form = props => {
  const {
    children,
    error,
    modifier,
    ...restProps
  } = props;
  if (isNil(modifier)) {
    throw new DefaultPropError('Form is missing props.modifier');
  }
  const classes = classNames('form', `form--${modifier}`, {
    'form--has-error': error
  });
  return /*#__PURE__*/React.createElement("form", _extends({}, restProps, {
    className: classes
  }), children);
};
const defaultProps$6 = {
  modifier: 'primary'
};
Form.defaultProps = defaultProps$6;

// ---------------------------------------------------------------------------

const InputIcon = props => {
  const handleOnClick = e => {
    if (props.onClick) {
      props.onClick(e);
    }
  };
  const classes = classNames('input-icon', {
    'input-icon-action': !isNil(props.onClick)
  }, `input-icon--${props.name}`, props.addlClasses);
  return /*#__PURE__*/React.createElement("span", {
    className: classes,
    onClick: e => handleOnClick(e)
  }, isRandoriIcon(props.name) ? /*#__PURE__*/React.createElement(Icon, {
    kind: props.name
  }) : /*#__PURE__*/React.createElement(CarbonIcon, {
    kind: props.name
  }));
};

// ---------------------------------------------------------------------------

const Label = props => {
  const htmlFor = props.name ?? 'input-label';
  const helperText = props.helperText;
  return /*#__PURE__*/React.createElement("div", {
    className: "input-label__wrapper"
  }, /*#__PURE__*/React.createElement("label", {
    htmlFor: htmlFor,
    className: classNames('input-label', `input-label--${props.position}`)
  }, props.message), isNotNilOrEmpty(helperText) && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(InfoPopper, {
    addlClasses: "input-label__tooltip",
    isInteractive: true
  }, helperText)));
};

// ---------------------------------------------------------------------------

const InputLeftIcon = props => {
  const handleOnClick = e => {
    if (props.onClick) {
      props.onClick(e);
    }
  };
  const classes = classNames('input-leftIcon', {
    'input-leftIcon-action': !isNil(props.onClick)
  }, `input-leftIcon--${props.name}`, props.addlClasses);
  return /*#__PURE__*/React.createElement("span", {
    className: classes,
    onClick: e => handleOnClick(e)
  }, isRandoriIcon(props.name) ? /*#__PURE__*/React.createElement(Icon, {
    kind: props.name
  }) : /*#__PURE__*/React.createElement(CarbonIcon, {
    kind: props.name
  }));
};

// ---------------------------------------------------------------------------

// @see: https://stackoverflow.com/questions/57195620/css-autocomplete-font-size
// @see: https://css-tricks.com/snippets/css/change-autocomplete-styles-webkit-browsers/
const Input = props => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    addlClasses,
    error,
    icon,
    label,
    leftIcon,
    modifier,
    ...restProps
  } = props; // shake

  const classes = classNames('input', {
    [`input--${props.modifier}`]: !isNil(props.modifier)
  }, {
    'input--disabled': props.disabled
  }, {
    'input--error': props.error
  }, {
    'input--icon': isNotNil(props.icon)
  }, {
    'input--leftIcon': isNotNil(props.leftIcon)
  }, isNotNil(props.label) ? `input-label--${props.label.position}` : '', props.addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: classes
  }, isNotNil(props.label) && (props.label.position === 'top' || props.label.position === 'left') ? /*#__PURE__*/React.createElement(Label, _extends({}, props.label, {
    name: props.name
  })) : null, isNotNil(props.leftIcon) ? /*#__PURE__*/React.createElement(InputLeftIcon, props.leftIcon) : null, /*#__PURE__*/React.createElement("input", restProps), isNotNil(props.icon) ? /*#__PURE__*/React.createElement(InputIcon, props.icon) : null, isNotNil(props.label) && (props.label.position === 'bottom' || props.label.position === 'right') ? /*#__PURE__*/React.createElement(Label, _extends({}, props.label, {
    name: props.name
  })) : null);
};
const defaultProps$5 = {
  modifier: 'primary'
};
Input.defaultProps = defaultProps$5;

// ---------------------------------------------------------------------------

const FormInput = props => {
  if (isNil(props.modifier)) {
    throw new DefaultPropError('FormInput is missing props.modifier');
  }
  return /*#__PURE__*/React.createElement(Input, _extends({}, props, {
    addlClasses: "form-input"
  }));
};
const defaultProps$4 = {
  modifier: 'primary'
};
FormInput.defaultProps = defaultProps$4;

// ---------------------------------------------------------------------------

const RClose = props => {
  return /*#__PURE__*/React.createElement("button", {
    onClick: props.closeToast,
    className: "growler-close-btn"
  }, /*#__PURE__*/React.createElement(Icon, {
    kind: "close",
    size: {
      width: 10,
      height: 10
    }
  }));
};
const GrowlerContainer = props => {
  const opts = {
    position: 'top-center',
    className: 'r-growler-container',
    transition: Slide,
    closeButton: /*#__PURE__*/React.createElement(RClose, null),
    ...props
  };
  return /*#__PURE__*/React.createElement(ToastContainer, opts);
};
const growler = toast;

// ---------------------------------------------------------------------------

const CarbonToggle = props => {
  const {
    addlClasses,
    ...restProps
  } = props;
  const classes = classNames('input-carbon-toggle', addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: classes
  }, /*#__PURE__*/React.createElement(Toggle$1, restProps));
};

// ---------------------------------------------------------------------------

const log$5 = debug('ROOTKIT:input--checkbox');
const webClientLog$1 = debug('RANDORI:ROOTKIT:input--checkbox');
// @see: https://vijayt.com/post/react-forwardref-example-and-why-it-should-not-be-part-of-react-api/
// @see: https://blog.logrocket.com/cleaning-up-the-dom-with-forwardref-in-react/
// @see: https://github.com/facebook/react/issues/13456
const Checkbox = /*#__PURE__*/React.forwardRef((props, ref) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    addlClasses,
    error,
    label,
    modifier,
    onChange,
    ...restProps
  } = props; // shake

  const classes = classNames('input-checkbox', {
    [`input-checkbox--${props.modifier}`]: !isNil(props.modifier)
  }, {
    'input-checkbox--is-checked': props.checked
  }, {
    'input--disabled': props.disabled
  }, {
    'input--error': !isNil(props.error)
  }, props.addlClasses);
  if (props.onClick) {
    log$5('props.onClick was provided. This is probably a mistake. props.onChange should be used to subscribe to state changes.');
    webClientLog$1('props.onClick was provided. This is probably a mistake. props.onChange should be used to subscribe to state changes.');
  }
  return /*#__PURE__*/React.createElement("div", {
    className: classes,
    "data-option-value": props.name
  }, /*#__PURE__*/React.createElement("input", _extends({}, restProps, {
    id: props.id ?? props.name,
    name: props.name,
    ref: ref,
    type: "checkbox",
    onChange: e => {
      const $target = e.target;
      const $closest = $target.closest('.input-checkbox');
      if (isNotNil($closest)) {
        const value = $closest.getAttribute('data-option-value');
        if (isNotNil(value) && isNotNil(props.onChange)) {
          props.onChange(e, value);
        }
      }
    }
  })), /*#__PURE__*/React.createElement("label", {
    htmlFor: props.id ?? props.name
  }, /*#__PURE__*/React.createElement("span", {
    className: "label-text"
  }, props.label)));
});
Checkbox.displayName = 'Checkbox';

// ---------------------------------------------------------------------------

const log$4 = debug('ROOTKIT:input--checkbox');
const webClientLog = debug('RANDORI:ROOTKIT:input--checkbox');
// @TODO: Add handler for keyboard events
class Radio extends React.Component {
  render() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {
      addlClasses,
      error,
      label,
      modifier,
      onChange,
      ...restProps
    } = this.props; // shake

    const classes = classNames('input-radio', {
      [`input-radio--${this.props.modifier}`]: !isNil(this.props.modifier)
    }, {
      'input-radio--is-checked': this.props.checked
    }, {
      'input--disabled': this.props.disabled
    }, this.props.addlClasses);
    if (this.props.onClick) {
      log$4('props.onClick was provided. This is probably a mistake. props.onChange should be used to subscribe to state changes.');
      webClientLog('props.onClick was provided. This is probably a mistake. props.onChange should be used to subscribe to state changes.');
    }
    return /*#__PURE__*/React.createElement("div", {
      className: classes,
      "data-option-value": this.props.name
    }, /*#__PURE__*/React.createElement("input", _extends({}, restProps, {
      id: this.props.id ?? this.props.name,
      name: this.props.name,
      type: "radio",
      onChange: e => {
        const $target = e.target;
        const $closest = $target.closest('.input-radio');
        if (isNotNil($closest)) {
          const value = $closest.getAttribute('data-option-value');
          if (isNotNil(value) && isNotNil(this.props.onChange)) {
            this.props.onChange(e, value);
          }
        }
      }
    })), /*#__PURE__*/React.createElement("label", {
      htmlFor: this.props.id ?? this.props.name
    }, /*#__PURE__*/React.createElement("span", {
      className: "label-text"
    }, this.props.label)));
  }
}

// ---------------------------------------------------------------------------

// @TODO: Consolidate these common modifier strings

const FileInputView = props => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    addlClasses,
    error,
    modifier,
    ...restProps
  } = props; // shake

  const [selectedString, setSelectedString] = React.useState(undefined);
  const _onChange = e => {
    const $target = e.target;

    /* eslint-disable no-magic-numbers */
    if (isNil($target) || isNil($target.files) || isNil($target.files[0])) {
      setSelectedString(undefined);
      if (isNotNil(props.onChange)) props.onChange(e);
    } else if (isNotNil($target) && isNotNil($target.files) && $target.files.length > 1) {
      setSelectedString(`${$target.files.length} files selected`);
      if (isNotNil(props.onChange)) props.onChange(e);
    } else if (isNotNil($target) && isNotNil($target.files) && isNotNil($target.files[0])) {
      setSelectedString($target.files[0].name);
      if (isNotNil(props.onChange)) props.onChange(e);
    }
  };
  /* eslint-enable no-magic-numbers */

  const classes = classNames('input', 'input--file', {
    [`input--${props.modifier}`]: !isNil(props.modifier)
  }, {
    'input--disabled': props.disabled
  }, {
    'input--error': props.error
  }, {
    'input--file-is-selected': isNotNil(selectedString)
  }, props.addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: classes
  }, /*#__PURE__*/React.createElement("input", _extends({
    className: "input--file-element",
    id: props.name,
    name: props.name,
    type: "file"
  }, restProps, {
    onChange: _onChange
  })), /*#__PURE__*/React.createElement("div", {
    className: "file-input-label-wrapper"
  }, /*#__PURE__*/React.createElement(Icon, {
    kind: "upload"
  }), /*#__PURE__*/React.createElement("label", {
    htmlFor: props.name,
    className: "input-label input-label--file"
  }, isNotNil(selectedString) ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "input-label--selected"
  }, selectedString), /*#__PURE__*/React.createElement("span", null, "click to edit")) : /*#__PURE__*/React.createElement(React.Fragment, null, props.label))));
};
FileInputView.defaultProps = {
  label: 'Choose a file',
  modifier: 'primary'
};
const FileInput = FileInputView;

// ---------------------------------------------------------------------------

function Select(props) {
  const {
    addlClasses,
    error,
    label,
    modifier,
    ...restProps
  } = props;
  const classes = classNames('input', {
    [`input--${modifier}`]: !isNil(modifier)
  }, {
    'input--disabled': props.isDisabled
  }, {
    'input--error': error
  }, isNotNil(label) ? `input-label--${label.position}` : '', addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: classes
  }, isNotNil(props.label) && (props.label.position === 'top' || props.label.position === 'left') ? /*#__PURE__*/React.createElement(Label, _extends({}, props.label, {
    name: props.name
  })) : null, /*#__PURE__*/React.createElement(ReactSelect, restProps), isNotNil(props.label) && (props.label.position === 'bottom' || props.label.position === 'right') ? /*#__PURE__*/React.createElement(Label, _extends({}, props.label, {
    name: props.name
  })) : null);
}

// ---------------------------------------------------------------------------

const TextArea = props => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    addlClasses,
    error,
    label,
    modifier,
    ...restProps
  } = props; // shake

  const classes = classNames('textarea', {
    [`textarea--${props.modifier}`]: !isNil(props.modifier)
  }, {
    'textarea--disabled': props.disabled
  }, {
    'textarea--error': props.error
  }, isNotNil(label) ? `textarea-label--${label.position}` : '', props.addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: classes
  }, isNotNil(label) && (label.position === 'top' || label.position === 'left') ? /*#__PURE__*/React.createElement(Label, _extends({}, label, {
    name: props.name
  })) : null, /*#__PURE__*/React.createElement("textarea", restProps), isNotNil(label) && (label.position === 'bottom' || label.position === 'right') ? /*#__PURE__*/React.createElement(Label, _extends({}, label, {
    name: props.name
  })) : null);
};
TextArea.defaultProps = {};

// ---------------------------------------------------------------------------

const Toggle = props => {
  const {
    addlClasses,
    ...restProps
  } = props; // shake

  const classes = classNames('input-toggle', addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: classes
  }, /*#__PURE__*/React.createElement("label", {
    htmlFor: props.name
  }, /*#__PURE__*/React.createElement("input", _extends({
    type: "checkbox"
  }, restProps)), /*#__PURE__*/React.createElement("div", {
    className: "input-toggle-trigger input-toggle-trigger--on"
  }, "on"), /*#__PURE__*/React.createElement("div", {
    className: "input-toggle-trigger input-toggle-trigger--off"
  }, "off")));
};

// ---------------------------------------------------------------------------

const makeClampComponent = maxLines => {
  const component = emStyled.div`
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${maxLines};
    overflow: hidden;
  `;
  return component;
};

// eslint-disable-next-line react/display-name
const LineClampBase = /*#__PURE__*/React.forwardRef((props, ref) => {
  const Clamp = makeClampComponent(props.maxLines);
  return /*#__PURE__*/React.createElement(Clamp, {
    ref: ref,
    className: props.addlClasses
  }, props.children);
});

// ---------------------------------------------------------------------------

const log$3 = debug('ROOTKIT:LineClamp');
const LineClamp = props => {
  const asType = typeof props.as;
  if (!isNotNil(props.delayShow)) {
    throw new DefaultPropError('LineClamp is missing props.delayShow');
  }
  if (!isNotNil(props.delayHide)) {
    throw new DefaultPropError('LineClamp is missing props.delayHide');
  }
  if (asType !== 'string' && asType !== 'undefined') {
    log$3("props.as should be a string. Otherwise, you're gonna have a bad time with props.ref");
  }
  const testId = isNotNil(props.testId) ? {
    'data-testid': props.testId
  } : {};
  const classes = classNames('line-clamp', props.addlClasses);
  const tooltipClasses = classNames('line-clamp__tooltip', {
    [`line-clamp__tooltip--${props.tooltipSize}`]: isNotNil(props.tooltipSize)
  }, props.addlTooltipClasses);
  const delayHide = props.isInteractive ?? false ? props.delayHide : 0;
  const Content = props.renderContent;
  const TriggerWrapper = emStyled.span`
  `;
  const ref = React.useRef(null);
  const shouldPopperAppear = () => {
    if (ref !== null && ref.current !== null) {
      // We infer that the text is truncated when scroll height of the element does not equal height of the element
      // We are returning on differences greater than 5px because there are occurences where the heights are ~1px off,
      // but there is no discernable truncation in the UI
      return Math.abs(ref.current.clientHeight - ref.current.scrollHeight) > 5;
    }
    return false;
  };
  return /*#__PURE__*/React.createElement(PopperBase, {
    debug: props.debug,
    delayHide: delayHide,
    delayShow: props.delayShow,
    options: props.options,
    render: pProps => {
      const interactiveProps = {
        onMouseOut: pProps.onMouseOut,
        onMouseOver: pProps.onMouseOver
      };
      const addlProps = props.isInteractive ?? false ? interactiveProps : {};
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TriggerWrapper, _extends({
        as: props.as,
        className: classes,
        onMouseOut: pProps.onMouseOut,
        onMouseOver: pProps.onMouseOver,
        ref: pProps.setReferenceElement
      }, pProps.attributes.reference, testId), /*#__PURE__*/React.createElement(LineClampBase, {
        maxLines: props.maxLines,
        ref: ref
      }, /*#__PURE__*/React.createElement(Content, null))), pProps.isHoveringRef && shouldPopperAppear() && /*#__PURE__*/createPortal(/*#__PURE__*/React.createElement("div", _extends({
        className: tooltipClasses,
        ref: pProps.setPopperElement,
        style: pProps.styles.popper
      }, pProps.attributes.popper, addlProps), /*#__PURE__*/React.createElement(Content, null)), document.body));
    }
  });
};
const defaultProps$3 = {
  delayHide: 100,
  delayShow: 100
};
LineClamp.defaultProps = defaultProps$3;

// ---------------------------------------------------------------------------

const Link = props => {
  const {
    addlClasses,
    children,
    href,
    ...restProps
  } = props;
  const classes = classNames('rootkit__link', addlClasses);
  const child = children ?? href;
  return /*#__PURE__*/React.createElement("a", _extends({
    className: classes,
    href: href
  }, restProps), child);
};

function Logo({
  addlClasses,
  useText = true,
  ...restProps
}) {
  const classes = classNames('randori-logo', addlClasses);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps, {
    style: {
      width: '100%'
    }
  }), /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 75 81",
    "aria-label": "Randori logo",
    role: "img",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M37.422 0c7.522 0 31.796 15.952 35.21 20.192 3.415 4.24 2.888 35.539 0 40.385-2.887 4.846-29.111 20.192-35.21 20.192S5.099 65.221 2.21 60.577c-2.888-4.644-3.008-35.74 0-40.385C5.219 15.548 29.901 0 37.421 0z",
    fill: "#DA021B"
  }), useText && /*#__PURE__*/React.createElement("path", {
    d: "M9.902 33.715h5.332c1.224 0 2.196.268 2.9.821.738.604 1.124 1.476 1.124 2.616 0 .62-.168 1.173-.503 1.66a2.603 2.603 0 0 1-1.526 1.056v.034c1.04.234 1.626.922 1.76 2.095l.202 1.929c.067.737.318 1.324.72 1.76h-2.129c-.285-.369-.453-.889-.52-1.56l-.15-1.475c-.068-.654-.269-1.123-.604-1.408-.352-.302-.872-.436-1.543-.436h-3.102v4.88H9.902V33.714zm1.961 1.676v3.74h3.102c.772 0 1.358-.168 1.76-.487.37-.335.554-.805.554-1.408 0-.654-.184-1.124-.553-1.409-.369-.302-.956-.436-1.76-.436h-3.103zm13.009 1.392c1.274 0 2.23.318 2.833.956.537.553.805 1.358.805 2.414v5.533h-1.777v-1.14a3.386 3.386 0 0 1-1.24.99c-.554.25-1.208.385-1.963.385-.905 0-1.61-.235-2.129-.688a2.253 2.253 0 0 1-.805-1.777c0-.972.37-1.727 1.14-2.247.705-.503 1.694-.77 2.968-.804l1.895-.05v-.336c0-1.14-.62-1.694-1.861-1.694-.537 0-.956.084-1.275.285-.369.218-.62.554-.72 1.006l-1.895-.15c.184-.956.654-1.66 1.408-2.096.637-.403 1.51-.587 2.616-.587zm1.727 4.946l-1.76.05c-1.527.034-2.281.57-2.281 1.61 0 .302.117.553.386.755.268.2.603.318 1.022.318.738 0 1.358-.218 1.861-.637.503-.436.772-.99.772-1.66v-.436zm7.91-4.946c2.164 0 3.254 1.174 3.254 3.554v5.35H35.85v-5.182c0-1.425-.654-2.13-1.962-2.13-.469 0-.888.168-1.24.504-.403.369-.637.905-.704 1.593v5.214h-1.912v-8.668h1.912v1.006c.335-.403.72-.721 1.14-.923a3.12 3.12 0 0 1 1.425-.318zm10.947-3.303h1.911v12.206H45.59v-.922c-.604.771-1.476 1.157-2.616 1.157-1.29 0-2.297-.453-3.018-1.358-.67-.839-1.006-1.928-1.006-3.253 0-1.274.319-2.33.99-3.169.704-.905 1.693-1.358 2.934-1.358 1.006 0 1.86.47 2.582 1.425V33.48zm-2.13 4.845c-.871 0-1.492.285-1.894.856-.352.486-.52 1.19-.52 2.129 0 .939.15 1.66.486 2.163.386.603 1.006.905 1.861.905.738 0 1.325-.302 1.727-.888.352-.537.537-1.241.537-2.08v-.134c0-.922-.235-1.66-.67-2.196-.403-.503-.923-.755-1.527-.755zm9.588-1.542c1.308 0 2.364.42 3.17 1.29.787.856 1.19 1.946 1.19 3.287 0 1.325-.403 2.415-1.174 3.253-.805.872-1.878 1.308-3.186 1.308-1.324 0-2.38-.436-3.185-1.308-.788-.838-1.174-1.928-1.174-3.253 0-1.341.386-2.431 1.19-3.286.788-.872 1.845-1.291 3.17-1.291zm0 1.56c-.805 0-1.408.301-1.844.938-.369.537-.553 1.24-.553 2.08 0 .838.184 1.525.553 2.062.436.62 1.04.938 1.844.938.788 0 1.409-.318 1.845-.938.368-.554.57-1.241.57-2.063 0-.838-.202-1.542-.57-2.079-.436-.637-1.057-.939-1.845-.939zm9.638-1.56c.42 0 .788.067 1.09.218v1.911a4.126 4.126 0 0 0-1.24-.184c-.504 0-.956.184-1.359.587-.47.452-.687 1.04-.687 1.777v4.594H58.46v-8.668h1.895v1.24c.235-.436.503-.77.822-1.006.385-.318.838-.47 1.374-.47zm2.982-3.303c.369 0 .688.117.939.352.235.235.369.536.369.905 0 .37-.134.688-.386.923a1.292 1.292 0 0 1-.922.352c-.369 0-.67-.118-.922-.352a1.238 1.238 0 0 1-.369-.923c0-.369.117-.67.369-.905.251-.235.553-.352.922-.352zm-.956 3.538h1.912v8.668h-1.912v-8.668z",
    fill: "#FFF"
  }))));
}

// ---------------------------------------------------------------------------

const log$2 = debug('ROOTKIT:Modal');
const Modal = props => {
  if (isNil(props.child) && isNil(props.children)) {
    log$2('Modal - nothing to render');
    return null; // bail
  }
  if (isNotNil(props.children) && isNotNil(props.child)) {
    log$2('Modal received props.children and props.child - will render props.child instead');
  }
  const childProps = isNotNil(props.childProps) ? props.childProps : {};
  const dialogClasses = classNames('modal-dialog', props.addlClasses);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "modal-overlay",
    onClick: props.onClose,
    "data-testid": "modal-overlay"
  }), /*#__PURE__*/React.createElement("div", {
    className: dialogClasses
  }, /*#__PURE__*/React.createElement("span", {
    className: "modal-dialog__close",
    onClick: props.onClose,
    "data-testid": "modal-close"
  }, /*#__PURE__*/React.createElement(Icon, {
    kind: "remove"
  })), /*#__PURE__*/React.createElement("div", {
    className: "modal-dialog__content"
  }, isNotNil(props.child) ? /*#__PURE__*/React.createElement(props.child, childProps) : /*#__PURE__*/React.createElement(React.Fragment, null, props.children))));
};

// ---------------------------------------------------------------------------

const log$1 = debug('ROOTKIT:Modal');
const ModalP = props => {
  if (isNil(props.modifier)) {
    throw new DefaultPropError('Modal is missing props.modifier');
  }
  const dialogClasses = classNames('modal-dialog', `modal-dialog--${props.modifier}`, props.addlClasses);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "modal-overlay",
    onClick: props.onClose,
    "data-testid": "modal-overlay"
  }), /*#__PURE__*/React.createElement("div", {
    className: dialogClasses
  }, isNotNil(props.renderHeader) ? /*#__PURE__*/React.createElement("div", {
    className: "modal-dialog__header"
  }, /*#__PURE__*/React.createElement("div", {
    className: "modal-dialog__header__inner"
  }, props.renderHeader()), /*#__PURE__*/React.createElement("span", {
    className: "modal-dialog__close",
    onClick: props.onClose,
    "data-testid": "modal-close"
  }, /*#__PURE__*/React.createElement(Icon, {
    kind: "remove"
  }))) : /*#__PURE__*/React.createElement("span", {
    className: "modal-dialog__close",
    onClick: props.onClose,
    "data-testid": "modal-close"
  }, /*#__PURE__*/React.createElement(Icon, {
    kind: "remove"
  })), /*#__PURE__*/React.createElement("div", {
    className: "modal-dialog__content"
  }, /*#__PURE__*/React.createElement(ModalContent, props)), isNotNil(props.renderActions) && /*#__PURE__*/React.createElement("div", {
    className: "modal-dialog__actions"
  }, /*#__PURE__*/React.createElement("div", {
    className: "modal-dialog__actions__inner"
  }, props.renderActions()))));
};
const defaultProps$2 = {
  modifier: 'default'
};
ModalP.defaultProps = defaultProps$2;
const ModalContent = props => {
  if (isNotNil(props.children)) {
    log$1('Modal received props.children. This supersedes props.render and props.child');
    return /*#__PURE__*/React.createElement(React.Fragment, null, props.children);
  }
  if (isNotNil(props.render)) {
    log$1('Modal received props.render. This supersedes props.child');
    return /*#__PURE__*/React.createElement(React.Fragment, null, props.render());
  }
  if (isNotNil(props.child)) {
    log$1('Modal received props.child. This should be replaced with props.render or props.children');
    const childProps = isNotNil(props.childProps) ? props.childProps : {};
    return /*#__PURE__*/React.createElement(props.child, childProps);
  }
  return null;
};

function getPageCount(total, itemsPerPage) {
  if (itemsPerPage === 0) {
    return 0;
  } else {
    return Math.ceil(total / itemsPerPage);
  }
}
function getSimpleRange(total, itemsPerPage) {
  if (itemsPerPage === 0) {
    return [];
  }
  return Array(getPageCount(total, itemsPerPage)).fill(undefined).map((_e, index) => index + 1); // number to print
}
function isActive(offset, page, itemsPerPage) {
  return page * itemsPerPage - itemsPerPage === offset;
}
function showPrev(offset) {
  return offset > 0;
}
function showNext(totalItems, offset, itemsPerPage) {
  return totalItems - offset > itemsPerPage;
}
function getActivePageIndex(offset, itemsPerPage) {
  return Math.ceil(offset / itemsPerPage);
}
function getPrevOffset(offset, itemsPerPage) {
  return offset - itemsPerPage;
}
function getNextOffset(offset, itemsPerPage) {
  return offset + itemsPerPage;
}
function getStaticOffset(page, itemsPerPage) {
  return page * itemsPerPage - itemsPerPage;
}

// @see: https://scotch.io/tutorials/build-custom-pagination-with-react
function fetchPageNumbers({
  __NEIGHBORS__,
  __FLAG_LEFT__,
  __FLAG_RIGHT__
}, totalPages, offset, itemsPerPage) {
  const _range = (from, to, step = 1) => {
    let i = from;
    const range = [];
    while (i <= to) {
      range.push(i);
      i += step;
    }
    return range;
  };
  const totalNumbers = __NEIGHBORS__ * 2 + 3;
  const totalBlocks = totalNumbers + 2;
  const currentPage = getActivePageIndex(offset, itemsPerPage) + 1;
  if (totalPages > totalBlocks) {
    let pages = [];
    const leftBound = currentPage - __NEIGHBORS__;
    const rightBound = currentPage + __NEIGHBORS__;
    const beforeLastPage = totalPages - 1;
    const startPage = leftBound > 2 ? leftBound : 2;
    const endPage = rightBound < beforeLastPage ? rightBound : beforeLastPage;
    pages = _range(startPage, endPage);
    const pagesCount = pages.length;
    const singleSpillOffset = totalNumbers - pagesCount - 1;
    const leftSpill = startPage > 2;
    const rightSpill = endPage < beforeLastPage;
    const leftSpillPage = __FLAG_LEFT__;
    const rightSpillPage = __FLAG_RIGHT__;
    if (leftSpill && !rightSpill) {
      const extraPages = _range(startPage - singleSpillOffset, startPage - 1);
      pages = [leftSpillPage, ...extraPages, ...pages];
    } else if (!leftSpill && rightSpill) {
      const extraPages = _range(endPage + 1, endPage + singleSpillOffset);
      pages = [...pages, ...extraPages, rightSpillPage];
    } else if (leftSpill && rightSpill) {
      pages = [leftSpillPage, ...pages, rightSpillPage];
    }
    return [1, ...pages, totalPages];
  }
  return _range(1, totalPages);
}

// ---------------------------------------------------------------------------

const __FLAG_LEFT__ = '…';
const __FLAG_RIGHT__ = '…';
const __NEIGHBORS__ = 2;

// ---------------------------------------------------------------------------

class SimplePager extends React.Component {
  render() {
    const classes = classNames('pager', this.props.addlClasses);
    return /*#__PURE__*/React.createElement("div", {
      onClick: this.props.onClick,
      className: classes
    }, this.props.itemsTotal > 0 && /*#__PURE__*/React.createElement("ul", {
      className: "pager-list"
    }, showPrev(this.props.itemsOffset) && /*#__PURE__*/React.createElement("li", {
      className: "pager-list-item pager-prev",
      "data-pager-offset": getPrevOffset(this.props.itemsOffset, this.props.itemsPerPage)
    }, /*#__PURE__*/React.createElement(Icon, {
      kind: "sort--asc"
    })), getSimpleRange(this.props.itemsTotal, this.props.itemsPerPage).map(el => {
      const classes = classNames('pager-list-item', {
        'is-active': isActive(this.props.itemsOffset, el, this.props.itemsPerPage)
      });
      return /*#__PURE__*/React.createElement("li", {
        key: el,
        className: classes,
        "data-pager-offset": getStaticOffset(el, this.props.itemsPerPage)
      }, el);
    }), showNext(this.props.itemsTotal, this.props.itemsOffset, this.props.itemsPerPage) && /*#__PURE__*/React.createElement("li", {
      className: "pager-list-item pager-next",
      "data-pager-offset": getNextOffset(this.props.itemsOffset, this.props.itemsPerPage)
    }, /*#__PURE__*/React.createElement(Icon, {
      kind: "sort--asc"
    }))));
  }
}

// ---------------------------------------------------------------------------

class TruncatedPager extends React.Component {
  render() {
    const classes = classNames('pager', this.props.addlClasses);
    const pageCount = getPageCount(this.props.itemsTotal, this.props.itemsPerPage);
    const pages = fetchPageNumbers({
      __NEIGHBORS__,
      __FLAG_LEFT__,
      __FLAG_RIGHT__
    }, pageCount, this.props.itemsOffset, this.props.itemsPerPage);
    return /*#__PURE__*/React.createElement("div", {
      onClick: this.props.onClick,
      className: classes
    }, this.props.itemsTotal > 0 && /*#__PURE__*/React.createElement("ul", {
      className: "pager-list"
    }, showPrev(this.props.itemsOffset) && /*#__PURE__*/React.createElement("li", {
      className: "pager-list-item pager-prev",
      "data-pager-offset": getPrevOffset(this.props.itemsOffset, this.props.itemsPerPage)
    }, /*#__PURE__*/React.createElement(Icon, {
      kind: "sort--asc"
    })), pages.map((el, i) => {
      if (typeof el === 'string') {
        return /*#__PURE__*/React.createElement("li", {
          key: `rest-${i}`
        }, el);
      }
      const classes = classNames('pager-list-item', {
        'is-active': isActive(this.props.itemsOffset, el, this.props.itemsPerPage)
      });
      return /*#__PURE__*/React.createElement("li", {
        key: el,
        className: classes,
        "data-pager-offset": getStaticOffset(el, this.props.itemsPerPage)
      }, el);
    }), showNext(this.props.itemsTotal, this.props.itemsOffset, this.props.itemsPerPage) && /*#__PURE__*/React.createElement("li", {
      className: "pager-list-item pager-next",
      "data-pager-offset": getNextOffset(this.props.itemsOffset, this.props.itemsPerPage)
    }, /*#__PURE__*/React.createElement(Icon, {
      kind: "sort--asc"
    }))));
  }
}

// ---------------------------------------------------------------------------

class Pager extends React.Component {
  constructor(...args) {
    super(...args);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _defineProperty(this, "handleClick", e => {
      // eslint-disable-next-line
      const $pagerItem = e.target.closest('.pager-list-item');
      if (!$pagerItem) {
        return;
      }
      // eslint-disable-next-line
      const offsetString = $pagerItem.getAttribute('data-pager-offset');
      const nextOffset = parseInt(offsetString, 10);
      this.props.onClick(e, nextOffset);
    });
  }
  render() {
    const pageCount = getPageCount(this.props.itemsTotal, this.props.itemsPerPage);
    if (pageCount < 2) {
      return null;
    } else if (pageCount < 10) {
      return /*#__PURE__*/React.createElement(SimplePager, _extends({}, this.props, {
        onClick: this.handleClick
      }));
    } else {
      return /*#__PURE__*/React.createElement(TruncatedPager, _extends({}, this.props, {
        onClick: this.handleClick
      }));
    }
  }
}

// from http://stackoverflow.com/a/18473154
const partialCircle = (cx, cy, r, start, end) => {
  const length = end - start;
  if (length === 0) return [];
  const fromX = r * Math.cos(start) + cx;
  const fromY = r * Math.sin(start) + cy;
  const toX = r * Math.cos(end) + cx;
  const toY = r * Math.sin(end) + cy;
  const large = Math.abs(length) <= Math.PI ? '0' : '1';
  const sweep = length < 0 ? '0' : '1';
  return [['M', fromX, fromY], ['A', r, r, 0, large, sweep, toX, toY]];
};
function degreesToRadians(degrees) {
  return degrees * Math.PI / 180;
}
function extractPercentage(value, percentage) {
  return value * percentage / 100;
}
function valueBetween(value, min, max) {
  if (value > max) return max;
  if (value < min) return min;
  return value;
}
function evaluateViewBoxSize(ratio, baseSize) {
  // Wide ratio
  if (ratio > 1) {
    return `0 0 ${baseSize} ${baseSize / ratio}`;
  }
  // Narrow/squared ratio
  return `0 0 ${baseSize * ratio} ${baseSize}`;
}

// turn data into attributes for use in a chart segment

function dataToSegmentAttrs({
  data,
  lengthAngle,
  paddingAngle
}) {
  const normalizedTotalAngle = valueBetween(lengthAngle, -360, 360);
  const numberOfPaddings = Math.abs(normalizedTotalAngle) === 360 ? data.segments.length : data.segments.length - 1;
  const singlePaddingDegrees = Math.abs(paddingAngle) * Math.sign(lengthAngle);
  const degreesTakenByPadding = singlePaddingDegrees * numberOfPaddings;
  const degreesTakenByPaths = normalizedTotalAngle - degreesTakenByPadding;
  let lastSegmentEnd = 0;
  return data.segments.map(dataEntry => {
    const valueInPercentage = data.total === 0 ? 0 : dataEntry.value / data.total * 100;
    const degrees = extractPercentage(degreesTakenByPaths, valueInPercentage);
    const startOffset = lastSegmentEnd;
    lastSegmentEnd = lastSegmentEnd + degrees + singlePaddingDegrees;
    return {
      percentage: valueInPercentage,
      degrees,
      startOffset,
      ...dataEntry
    };
  });
}
function makePathCommands(cx, cy, startAngle, lengthAngle, radius) {
  const patchedLengthAngle = valueBetween(lengthAngle, -359.999, 359.999);
  return partialCircle(cx, cy, radius, degreesToRadians(startAngle), degreesToRadians(startAngle + patchedLengthAngle)).map(command => command.join(' ')).join(' ');
}

class PieChartView extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("svg", _extends({
      viewBox: evaluateViewBoxSize(this.props.ratio, this.props.viewBoxSize),
      width: "100%",
      height: "100%",
      style: {
        display: 'block'
      }
    }, this.props.addlAttrs), this.props.segments, this.props.centerLabel), this.props.children);
  }
}
const PieChart = PieChartView;

class Path extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isHovering: false
    };
    this.onMouseOverToggle = this.onMouseOverToggle.bind(this);
  }
  onMouseOverToggle(isLeaving) {
    this.setState({
      isHovering: !isLeaving
    });
  }
  render() {
    const {
      cx,
      cy,
      isActive,
      lineWidth,
      lengthAngle,
      radius,
      startAngle
    } = this.props;
    const actualRadio = radius - lineWidth / 2;
    const pathCommands = makePathCommands(cx, cy, startAngle, lengthAngle, actualRadio);
    const classes = classNames({
      'pie-chart-path': true,
      active: isActive
    });
    return this.props.renderPath({
      classes,
      cx,
      cy,
      startAngle,
      pathCommands,
      isHovering: this.state.isHovering,
      onMouseEnter: () => this.onMouseOverToggle(false),
      onMouseLeave: () => this.onMouseOverToggle(true)
    });
  }
}

// ---------------------------------------------------------------------------

const Pill = props => {
  const {
    children,
    solid,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const classes = classNames('pill', {
    [`pill--${props.modifier}`]: isNotNil(props.modifier)
  }, {
    'is-solid': props.solid
  }, props.addlClasses, props.className);
  return /*#__PURE__*/React.createElement("span", _extends({
    className: classes
  }, restProps), props.children);
};
Pill.defaultProps = {
  modifier: 'grey',
  solid: false
};

const Pulse = props => {
  const {
    aria,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const classes = classNames('pulse', {
    [`pulse--${props.modifier}`]: !isNil(props.modifier)
  }, props.addlClasses);
  return /*#__PURE__*/React.createElement("div", _extends({}, restProps, {
    className: classes
  }), /*#__PURE__*/React.createElement("span", {
    className: "pulse-wrapper",
    style: {
      width: '12px'
    }
  }, /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 12 12"
  }, /*#__PURE__*/React.createElement("circle", {
    className: "pulse-background",
    cx: "50%",
    cy: "50%",
    r: "6px"
  }), /*#__PURE__*/React.createElement("circle", {
    className: "pulse-foreground",
    cx: "50%",
    cy: "50%",
    r: "3px"
  }))), /*#__PURE__*/React.createElement("span", {
    className: "pulse-label"
  }, props.label));
};
const Pin = props => {
  const {
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const classes = classNames('pin', {
    [`pin--${props.modifier}`]: !isNil(props.modifier)
  }, props.addlClasses);
  const addlStyles = props.style ? props.style : {};
  return /*#__PURE__*/React.createElement("div", _extends({}, restProps, {
    className: classes
  }), /*#__PURE__*/React.createElement("span", _extends({}, restProps, {
    className: "pin-wrapper",
    style: {
      width: '12px',
      ...addlStyles
    }
  }), /*#__PURE__*/React.createElement("svg", {
    viewBox: "0 0 12 12"
  }, /*#__PURE__*/React.createElement("circle", {
    className: "pin-background",
    cx: "50%",
    cy: "50%",
    r: "6px"
  }), /*#__PURE__*/React.createElement("circle", {
    className: "pin-foreground",
    cx: "50%",
    cy: "50%",
    r: "3px"
  }))));
};

// ---------------------------------------------------------------------------

const ReadMore = props => {
  const classes = classNames('read-more', {
    'read-more--is-expanded': props.isExpanded
  });
  const iconClasses = classNames('read-more-icon', {
    'read-more-icon--is-expanded': props.isExpanded
  });
  return /*#__PURE__*/React.createElement("div", {
    className: classes,
    onClick: props.onClick
  }, /*#__PURE__*/React.createElement("span", {
    className: "read-more-text"
  }, "Read ", props.isExpanded ? 'less' : 'more'), /*#__PURE__*/React.createElement(Icon, {
    kind: "caret",
    addlClasses: iconClasses
  }));
};
ReadMore.defaultProps = {
  isExpanded: false
};

// ---------------------------------------------------------------------------

const ScrollContainer = props => {
  const {
    axis
  } = props;
  const scrollContainerClasses = classNames('scroll-container', getScrollAxisClass(axis), props.addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: scrollContainerClasses
  }, props.children);
};
const getScrollAxisClass = axis => {
  const scrollX = 'scroll-container--x';
  const scrollY = 'scroll-container--y';
  switch (axis) {
    case 'x':
      return scrollX;
    case 'y':
      return scrollY;
    case 'xy':
      return classNames(scrollX, scrollY);
    default:
      throw new ExhaustiveError(axis);
  }
};

// ---------------------------------------------------------------------------

const CalcScrollbar = () => {
  React.useLayoutEffect(() => {
    const calc = throttle(1000, setScrollbarWidthVar);
    const listenForScroll = () => {
      calc();
    };
    document.addEventListener('scroll', listenForScroll);
    return () => {
      calc.cancel();
      document.removeEventListener('scroll', listenForScroll);
    };
  });
  return null;
};

// ---------------------------------------------------------------------------

const SectionComponent = props => {
  const sectionClasses = classNames('rootkit__section', {
    'rootkit__section--is-collapsed': !props.isToggled
  }, props.addlClasses);
  const labelClasses = classNames('rootkit__section__label', {
    'rootkit__section__label--is-collapsed': !props.isToggled
  });
  const contentClasses = classNames('rootkit__section__content', {
    'rootkit__section__content--is-collapsed': !props.isToggled
  });
  const iconClasses = classNames('rootkit__section__label__icon', {
    ['rootkit__section__label__icon--is-collapsed']: !props.isToggled
  });
  const labelVariant = props.labelVariant ?? 'h3';
  const labelAs = props.labelAs ?? 'h1';
  return /*#__PURE__*/React.createElement("section", {
    className: sectionClasses,
    id: props.id
  }, /*#__PURE__*/React.createElement("div", {
    onClick: props.toggle,
    className: labelClasses
  }, /*#__PURE__*/React.createElement("span", {
    className: iconClasses
  }, props.isToggled ? /*#__PURE__*/React.createElement(Icon, {
    kind: "v2-collapse",
    size: {
      width: 12,
      height: 1
    }
  }) : /*#__PURE__*/React.createElement(Icon, {
    kind: "v2-expand",
    size: {
      width: 12,
      height: 12
    }
  })), isString(props.label) ? /*#__PURE__*/React.createElement(Typography, {
    addlClasses: "rootkit__section__label__text",
    variant: labelVariant,
    as: labelAs
  }, props.label) : props.label(), isNotNilOrEmpty(props.labelDesc) && /*#__PURE__*/React.createElement(InfoPopper, {
    addlClasses: "rootkit__section__label__info-popper"
  }, isString(props.labelDesc) ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, props.labelDesc)) : /*#__PURE__*/React.createElement(React.Fragment, null, props.labelDesc({
    isToggled: props.isToggled,
    toggle: props.toggle
  })))), /*#__PURE__*/React.createElement(Collapse, {
    addlClasses: contentClasses,
    isCollapsed: !props.isToggled
  }, props.children));
};

// ---------------------------------------------------------------------------

const SectionStatefulWrapper = props => {
  const {
    isToggled,
    toggle
  } = useToggle(props.isToggled);
  return /*#__PURE__*/React.createElement(SectionComponent, _extends({}, props, {
    isToggled: isToggled,
    toggle: toggle
  }));
};
const SectionSubscribedWrapper = SectionComponent;

// ---------------------------------------------------------------------------

const Section = props => {
  const {
    isToggled,
    label,
    labelDesc,
    toggle,
    ...restProps
  } = props;

  // @see: Notes in `details` component

  if (isNotNil(toggle)) {
    if (isNil$1(isToggled)) {
      throw new DefaultPropError('isToggled must be provided if toggle is provided');
    }
    return /*#__PURE__*/React.createElement(SectionSubscribedWrapper, _extends({}, restProps, {
      isToggled: isToggled,
      label: label,
      labelDesc: labelDesc,
      toggle: toggle
    }));
  } else {
    return /*#__PURE__*/React.createElement(SectionStatefulWrapper, _extends({}, restProps, {
      isToggled: isToggled ?? true,
      label: label,
      labelDesc: labelDesc
    }));
  }
};

// ---------------------------------------------------------------------------

const log = debug('ROOTKIT:Slideout');
class _Slideout extends React.Component {
  constructor(...args) {
    super(...args);
    // -------------------------------------------------------------------------
    _defineProperty(this, "handleClickOutside", () => {
      this.props.onClose();
    });
  }
  componentDidMount() {
    this.props.enableOnClickOutside();
  }
  componentWillUnmount() {
    this.props.disableOnClickOutside();
  }
  render() {
    return /*#__PURE__*/React.createElement(SlideoutP, this.props);
  }
}
_defineProperty(_Slideout, "defaultProps", {
  modifier: 'default'
});
const Slideout = onClickOutside(_Slideout);
const SlideoutP = props => {
  if (isNil(props.modifier)) {
    throw new DefaultPropError('Modal is missing props.modifier');
  }
  const slideoutClasses = classNames('modal-slideout', `modal-slideout--${props.modifier}`, props.addlClasses);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: slideoutClasses
  }, isNotNil(props.renderHeader) ? /*#__PURE__*/React.createElement("div", {
    className: "modal-slideout__header"
  }, /*#__PURE__*/React.createElement("div", {
    className: "modal-slideout__header__inner"
  }, props.renderHeader()), /*#__PURE__*/React.createElement("span", {
    className: "modal-slideout__close",
    onClick: props.onClose,
    "data-testid": "slideout-close"
  }, /*#__PURE__*/React.createElement(Icon, {
    kind: "remove"
  }))) : /*#__PURE__*/React.createElement("span", {
    className: "modal-slideout__close",
    onClick: props.onClose,
    "data-testid": "slideout-close"
  }, /*#__PURE__*/React.createElement(Icon, {
    kind: "remove"
  })), /*#__PURE__*/React.createElement("div", {
    className: "modal-slideout__content"
  }, /*#__PURE__*/React.createElement(SlideoutContent, props)), isNotNil(props.renderActions) && /*#__PURE__*/React.createElement("div", {
    className: "modal-slideout__actions"
  }, /*#__PURE__*/React.createElement("div", {
    className: "modal-slideout__actions__inner"
  }, props.renderActions()))));
};
const SlideoutContent = props => {
  if (isNotNil(props.children)) {
    log('Slideout received props.children. This supersedes props.render');
    return /*#__PURE__*/React.createElement(React.Fragment, null, props.children);
  }
  if (isNotNil(props.render)) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, props.render());
  }
  return null;
};

// ---------------------------------------------------------------------------

const StatBlock = props => {
  const classes = classNames('stat-block', {
    'stat-block--is-highlighted': props.isHighlighted
  }, {
    'stat-block--has-action': isNotNil(props.onClick)
  }, props.addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: classes,
    onClick: props.onClick,
    "data-stat-id": props.label.message
  }, props.label.position === 'top' && /*#__PURE__*/React.createElement("label", {
    className: "stat-label"
  }, props.label.message), /*#__PURE__*/React.createElement("div", {
    className: "stat"
  }, props.stat), isNotNil(props.children) && /*#__PURE__*/React.createElement("div", {
    className: "stat-block-children"
  }, props.children), props.label.position === 'bottom' && /*#__PURE__*/React.createElement("label", {
    className: "stat-label"
  }, props.label.message));
};
StatBlock.defaultProps = {
  isHighlighted: false
};

// ---------------------------------------------------------------------------

const StatBlockGroup = props => {
  const {
    addlClasses,
    children,
    modifier,
    ...restProps
  } = props;
  const modifiers = isNotNil(modifier) ? [].concat(modifier).map(mod => `stat-block-group--${mod}`) : undefined;
  const classes = classNames('stat-block-group', addlClasses, modifiers);
  return /*#__PURE__*/React.createElement(Card, _extends({
    addlClasses: classes
  }, restProps), children);
};

// ---------------------------------------------------------------------------

const TableBody = props => {
  const {
    children,
    addlClasses,
    modifier,
    ...restProps
  } = props;
  const classes = classNames('table-body', {
    [`table-body--${props.modifier}`]: !isNil(props.modifier)
  }, props.addlClasses);
  return /*#__PURE__*/React.createElement("tbody", _extends({}, restProps, {
    className: classes
  }), props.children);
};
const Body = TableBody;

var Body$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Body: Body
});

// ---------------------------------------------------------------------------

function basicRenderer({
  data,
  modifier
}, index) {
  const classes = classNames('table-cell', {
    [`table-cell--${modifier}`]: !isNil(modifier)
  });
  return /*#__PURE__*/React.createElement("td", {
    key: `${index}-${data}`,
    className: classes
  }, data);
}
function componentRenderer({
  component: Component,
  key,
  modifier
}, index) {
  const classes = classNames('table-cell', {
    [`table-cell--${modifier}`]: !isNil(modifier)
  });
  return /*#__PURE__*/React.createElement("td", {
    key: `${index}-${key}`,
    className: classes
  }, /*#__PURE__*/React.createElement("span", {
    onClick: e => {
      e.stopPropagation();
    }
  }, /*#__PURE__*/React.createElement(Component, null)));
}
const Cell = {
  basicRenderer,
  componentRenderer
};

var Cell$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Cell: Cell
});

// ---------------------------------------------------------------------------

const Container = props => {
  const {
    children,
    addlClasses,
    modifier,
    selectable,
    ...restProps
  } = props;
  const classes = classNames('table', {
    [`table--${props.modifier}`]: !isNil(props.modifier)
  }, {
    'table--is-selectable': props.selectable
  }, props.addlClasses);
  return /*#__PURE__*/React.createElement("table", _extends({}, restProps, {
    className: classes
  }), props.children);
};
Container.defaultProps = {
  selectable: false
};

var Container$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Container: Container
});

// ---------------------------------------------------------------------------

const Sort = ({
  direction
}) => {
  if (direction === 'asc') {
    return /*#__PURE__*/React.createElement(Icon, {
      kind: "trend-arrow--up"
    });
  } else if (direction === 'desc') {
    return /*#__PURE__*/React.createElement(Icon, {
      kind: "trend-arrow--down"
    });
  } else {
    throw new Error('Unkown sort direction');
  }
};

var Sort$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Sort: Sort
});

// ---------------------------------------------------------------------------

const TableHeader = props => {
  const {
    addlClasses,
    modifier,
    headers,
    ...restProps
  } = props;
  const classes = classNames('table-header', {
    [`table-header--${props.modifier}`]: !isNil(props.modifier)
  }, props.addlClasses);
  return /*#__PURE__*/React.createElement("thead", _extends({}, restProps, {
    className: classes
  }), /*#__PURE__*/React.createElement("tr", null, props.headers.map((cell, index) => {
    const {
      label,
      sort,
      fieldName,
      ...restProps
    } = cell;
    const classes = classNames('table-column-label', {
      [`table-column-label--${cell.sort}`]: !isNil(cell.sort)
    });
    return /*#__PURE__*/React.createElement("th", _extends({}, restProps, {
      key: cell.fieldName || index,
      className: classes,
      "data-sort-field": cell.fieldName,
      "data-sort-direction": cell.sort
    }), cell.label, cell.sort && /*#__PURE__*/React.createElement(Sort, {
      direction: cell.sort
    }));
  })));
};
const Header = TableHeader;

var Header$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Header: Header
});

// ---------------------------------------------------------------------------

const TableRow = props => {
  const {
    id,
    cells,
    ...restRowProps
  } = props.row;
  const classes = classNames('table-row', {
    [`table-row--${props.modifier}`]: !isNil(props.modifier)
  }, props.addlClasses);
  return /*#__PURE__*/React.createElement("tr", _extends({}, restRowProps, {
    className: classes,
    "data-row-id": props.id
  }), props.row.cells.map((cell, index) => {
    switch (cell.kind) {
      case 'basic':
        return Cell.basicRenderer(cell, index);
      case 'component':
        return Cell.componentRenderer(cell, index);
      default:
        return null;
    }
  }));
};
const Row = TableRow;

var Row$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Row: Row
});

const Table$1 = {
  ...Body$1,
  ...Cell$1,
  ...Container$1,
  ...Header$1,
  ...Row$1,
  ...Sort$1
};

// ---------------------------------------------------------------------------

const Table = props => {
  const {
    children,
    addlClasses,
    ...restProps
  } = props;
  const classes = classNames('rootkit__table', addlClasses);
  return /*#__PURE__*/React.createElement("table", _extends({}, restProps, {
    className: classes
  }), children);
};

// ---------------------------------------------------------------------------

const TBody = props => {
  const {
    addlClasses,
    children,
    ...restProps
  } = props;
  const classes = classNames('rootkit__table__body', addlClasses);
  return /*#__PURE__*/React.createElement("tbody", _extends({}, restProps, {
    className: classes
  }), children);
};

// ---------------------------------------------------------------------------

const Td = props => {
  if (isNil(props.render)) {
    throw new DefaultPropError('Missing props.render');
  }
  const {
    addlClasses,
    children,
    render,
    ...restProps
  } = props;
  const classes = classNames('rootkit__table__cell', addlClasses);
  return /*#__PURE__*/React.createElement("td", _extends({}, restProps, {
    className: classes
  }), render({
    children
  }));
};
const defaultProps$1 = {
  render: ({
    children
  }) => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, children);
  }
};
Td.defaultProps = defaultProps$1;

// ---------------------------------------------------------------------------

const TFoot = props => {
  const {
    addlClasses,
    children,
    ...restProps
  } = props;
  const classes = classNames('rootkit__table__foot', addlClasses);
  return /*#__PURE__*/React.createElement("tfoot", _extends({}, restProps, {
    className: classes
  }), children);
};

// ---------------------------------------------------------------------------

const Th = props => {
  if (isNil(props.render)) {
    throw new DefaultPropError('Missing props.render');
  }
  const {
    addlClasses,
    children,
    render,
    ...restProps
  } = props;
  const classes = classNames('rootkit__table__header', addlClasses);
  return /*#__PURE__*/React.createElement("th", _extends({}, restProps, {
    className: classes
  }), render({
    children
  }));
};
const defaultProps = {
  render: ({
    children
  }) => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, children);
  }
};
Th.defaultProps = defaultProps;

// ---------------------------------------------------------------------------

const THead = props => {
  const {
    addlClasses,
    children,
    ...restProps
  } = props;
  const classes = classNames('rootkit__table__head', addlClasses);
  return /*#__PURE__*/React.createElement("thead", _extends({}, restProps, {
    className: classes
  }), children);
};

// ---------------------------------------------------------------------------

const Tr = props => {
  const {
    addlClasses,
    children,
    ...restProps
  } = props;
  const classes = classNames('rootkit__table__row', addlClasses);
  return /*#__PURE__*/React.createElement("tr", _extends({}, restProps, {
    className: classes
  }), children);
};

var TableElements = /*#__PURE__*/Object.freeze({
  __proto__: null,
  TBody: TBody,
  TFoot: TFoot,
  THead: THead,
  Table: Table,
  Td: Td,
  Th: Th,
  Tr: Tr
});

// ---------------------------------------------------------------------------

/**
 * Convert `@randori/rootkit/table-v2['headers']` to `@carbon/react/DataTable['headers']`
 *
 * @param headers - the `@randori/rootkit/table-v2` style headers
 *
 * @returns headers compatible with `@carbon/react/DataTable`
 */
const mapToDataTableHeaders = headers => {
  return headers.map((Header, i) => {
    if (isRenderProps(Header)) {
      const header = /*#__PURE__*/React.createElement(Header, null);
      return {
        header,
        key: i.toString()
      };
    }
    if (isString(Header)) {
      const key = Header.split(' ').join('-').toLowerCase();
      return {
        header: Header,
        key
      };
    }
    return {
      header: Header,
      key: i.toString()
    };
  });
};

/**
 * Convert `@randori/rootkit/table-v2['rows']` to `@carbon/react/DataTable['rows']`
 *
 * @param headers - the `@carbon/react` style headers
 * @param rows - the `@randori/rootkit/table-v2` style rows
 *
 * @returns rows compatible with `@carbon/react/DataTable`
 */
const mapToDataTableRows = (headers, rows) => {
  return rows.map((row, i) => {
    const mapped = row.map((Cell, i) => {
      const {
        key
      } = headers[i];
      const value = isRenderProps(Cell) ? /*#__PURE__*/React.createElement(Cell, null) : Cell;
      return {
        [key]: value
      };
    });
    const flattened = mapped.reduce((acc, el) => ({
      ...acc,
      ...el
    }), {});
    const convertedCell = {
      id: i.toString(),
      ...flattened
    };
    return convertedCell;
  });
};

/**
 *
 * Convert `@randori/rootkit/table-v2` headers and rows to `@carbon/react/DataTable` headers and rows
 *
 * @param headers - the `@randori/rootkit/table-v2` style headers
 * @param rows - the `@randori/rootkit/table-v2` style rows
 *
 * @returns an object that contains headers and rows suitable for `@carbon/react/DataTable`
 */
const mapToDataTable = (headers, rows) => {
  const mappedHeaders = mapToDataTableHeaders(headers);
  const mappedRows = mapToDataTableRows(mappedHeaders, rows);
  return {
    headers: mappedHeaders,
    rows: mappedRows
  };
};

var tableV2_utils = /*#__PURE__*/Object.freeze({
  __proto__: null,
  mapToDataTable: mapToDataTable,
  mapToDataTableHeaders: mapToDataTableHeaders,
  mapToDataTableRows: mapToDataTableRows
});

// ---------------------------------------------------------------------------

function TableV2(props) {
  props.rows.forEach(row => {
    if (row.length !== props.headers.length) {
      throw new DependentPropError('row.length !== props.headers.length');
    }
  });
  return /*#__PURE__*/React.createElement(Table, {
    addlClasses: props.addlClasses
  }, /*#__PURE__*/React.createElement(THead, null, /*#__PURE__*/React.createElement(Tr, null, props.headers.map((th, i) => {
    if (isRenderProps(th)) {
      return /*#__PURE__*/React.createElement(Th, {
        key: i,
        render: th
      });
    } else {
      return /*#__PURE__*/React.createElement(Th, {
        key: i
      }, th);
    }
  }))), /*#__PURE__*/React.createElement(TBody, null, props.rows.map((row, i) => {
    return /*#__PURE__*/React.createElement(Tr, {
      key: i
    }, row.map((td, i) => {
      if (isRenderProps(td)) {
        return /*#__PURE__*/React.createElement(Td, {
          key: i,
          render: td
        });
      } else {
        return /*#__PURE__*/React.createElement(Td, {
          key: i
        }, td);
      }
    }));
  })));
}
const TableV2Elements = TableElements;

// ---------------------------------------------------------------------------

const AddTag$1 = props => {
  const classes = classNames('click-tag', 'click-tag--add', {
    'is-active': props.isActive
  }, {
    'is-disabled': props.isDisabled
  }, props.addlClasses);
  const buttonClasses = classNames({
    'is-active': props.isActive
  }, {
    'is-disabled': props.isDisabled
  }, props.addlClasses);
  const addlAttrs = isNotNil(props.addlAttrs) ? props.addlAttrs : {};
  const onClick = props.isDisabled ? () => undefined : props.onClick;
  if (props.asButton) {
    return /*#__PURE__*/React.createElement(Button, {
      modifier: props.buttonModifier,
      icon: {
        kind: 'tag',
        position: 'left',
        size: {
          width: 14,
          height: 14
        }
      },
      disabled: props.isDisabled,
      addlClasses: buttonClasses,
      render: props => /*#__PURE__*/React.createElement("button", _extends({}, props, {
        onClick: onClick
      }, addlAttrs))
    }, props.buttonTitle);
  } else {
    return /*#__PURE__*/React.createElement("div", _extends({
      className: classes,
      onClick: onClick
    }, addlAttrs), /*#__PURE__*/React.createElement("span", {
      className: "click-tag-icon"
    }, /*#__PURE__*/React.createElement(Icon, {
      kind: "tag",
      size: {
        width: 9,
        height: 9
      }
    })), /*#__PURE__*/React.createElement("span", {
      className: "click-tag-text"
    }, props.buttonTitle));
  }
};

let TagsModifiers = /*#__PURE__*/function (TagsModifiers) {
  TagsModifiers["interesting"] = "interesting";
  return TagsModifiers;
}({});
let ActiveTagState = /*#__PURE__*/function (ActiveTagState) {
  ActiveTagState["checked"] = "checked";
  ActiveTagState["unchecked"] = "unchecked";
  ActiveTagState["indeterminate"] = "indeterminate";
  return ActiveTagState;
}({});

// ---------------------------------------------------------------------------

const initialState = {
  addTagValue: '',
  isCreateVisible: false,
  isTagValid: false,
  searchTagValue: ''
};
const findTags$1 = searchValue => tag => {
  const _searchValue = searchValue.toLowerCase();
  const _tag = tag.toLowerCase();
  return _tag.startsWith(_searchValue);
};
const sortByCaseInsensitive$1 = (a, b) => {
  return a.toLowerCase().localeCompare(b.toLowerCase());
};
class AddList extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", initialState);
    // -------------------------------------------------------------------------
    _defineProperty(this, "handleSearchChange", e => {
      this.setState({
        searchTagValue: e.target.value
      });
    });
    _defineProperty(this, "handleAddChange", e => {
      const {
        validate
      } = this.props;
      this.setState({
        addTagValue: e.target.value,
        isTagValid: validate(e.target.value)
      });
    });
    _defineProperty(this, "handleToggle", () => {
      this.setState(({
        isCreateVisible
      }) => ({
        isCreateVisible: !isCreateVisible
      }));
    });
    _defineProperty(this, "onCreateTag", handleDropdownToggle => () => {
      if (this.props.onCreate && !isEmpty(this.state.addTagValue)) {
        this.props.onCreate(this.state.addTagValue);
        this.setState({
          isCreateVisible: false,
          addTagValue: '',
          searchTagValue: ''
        });
        handleDropdownToggle();
      }
    });
    _defineProperty(this, "onApplyTag", handleDropdownToggle => {
      if (this.props.onApply) {
        this.props.onApply();
        this.setState({
          isCreateVisible: false,
          addTagValue: '',
          searchTagValue: ''
        });
        handleDropdownToggle();
      }
    });
  }
  render() {
    const addTagActionClasses = classNames('add-tag-action-submit', {
      disabled: !this.state.isTagValid
    });
    const addTagInputClasses = classNames({
      invalid: !this.state.isTagValid
    });
    const tags = this.props.tags.filter(findTags$1(this.state.searchTagValue)).sort(sortByCaseInsensitive$1);
    return /*#__PURE__*/React.createElement(DropdownNew, {
      addlDropdownClasses: "add-tags",
      renderTrigger: dProps => /*#__PURE__*/React.createElement(AddTag$1, {
        addlAttrs: this.props.addlAttrs,
        asButton: this.props.asButton,
        buttonModifier: this.props.buttonModifier,
        buttonTitle: this.props.buttonTitle,
        isActive: dProps.isOpen,
        isDisabled: this.props.isDisabled,
        onClick: dProps.toggle
      }),
      render: dProps => {
        const onClick = () => this.onApplyTag(dProps.toggle);
        const onCreateTag = this.onCreateTag(dProps.toggle);
        const onKeyUp = validateEnterKeyPress(() => this.onApplyTag(dProps.toggle));
        return /*#__PURE__*/React.createElement("div", {
          className: "add-tags__content"
        }, /*#__PURE__*/React.createElement("div", {
          className: "add-tags__search"
        }, /*#__PURE__*/React.createElement(Input, {
          modifier: "primary",
          name: "search-tags",
          onChange: this.handleSearchChange,
          onKeyUp: onKeyUp,
          placeholder: "Search tags",
          type: "text",
          value: this.state.searchTagValue
        })), /*#__PURE__*/React.createElement("ul", {
          className: "add-tags__list dropdown-new-options"
        }, tags.length > 0 ? tags.map((tag, index) => {
          const setCheckRef = checkbox => {
            if (isNotNil(checkbox)) {
              checkbox.indeterminate = this.props.activeTagStates[tag] === ActiveTagState.indeterminate;
            }
          };
          return /*#__PURE__*/React.createElement("li", {
            key: index,
            className: "add-tags__list__item dropdown-new-option",
            "data-add-tag": tag
          }, /*#__PURE__*/React.createElement(Checkbox, {
            checked: this.props.activeTagStates[tag] === ActiveTagState.checked,
            id: tag,
            label: tag,
            name: tag,
            ref: setCheckRef,
            onChange: (_e, tag) => {
              if (isNotNil(tag)) {
                this.props.onStage(tag);
              }
            }
          }));
        }) : /*#__PURE__*/React.createElement("li", {
          className: "add-tags__empty__item"
        }, "No more tags")), this.state.isCreateVisible ? /*#__PURE__*/React.createElement(CreateTag, {
          addTagActionClasses: addTagActionClasses,
          addTagInputClasses: addTagInputClasses,
          addTagValue: this.state.addTagValue,
          isTagValid: this.state.isTagValid,
          onAddChange: this.handleAddChange,
          onCreateTag: onCreateTag,
          onKeyUp: onKeyUp,
          onToggle: this.handleToggle,
          pattern: this.props.pattern
        }) : /*#__PURE__*/React.createElement("div", {
          className: "add_tags__actions"
        }, /*#__PURE__*/React.createElement(ButtonGroup, {
          addlClasses: "add_tags__actions__apply"
        }, this.props.allowCreate && /*#__PURE__*/React.createElement(Button, {
          addlClasses: "add-tag-action-trigger",
          icon: {
            kind: 'plus',
            position: 'left',
            size: {
              height: 10,
              width: 10
            }
          },
          modifier: "secondary",
          render: bProps => /*#__PURE__*/React.createElement("button", _extends({}, bProps, {
            onClick: this.handleToggle
          }))
        }, "Create new tag"), /*#__PURE__*/React.createElement(Button, {
          render: props => /*#__PURE__*/React.createElement("button", _extends({}, props, {
            onClick: onClick
          }))
        }, "Apply"))));
      }
    });
  }
}
const CreateTag = props => {
  return /*#__PURE__*/React.createElement("div", {
    className: "add-tags__form_actions",
    onKeyUp: props.onKeyUp
  }, /*#__PURE__*/React.createElement("div", {
    className: "add-tags__form_actions__actions"
  }, /*#__PURE__*/React.createElement(Input, {
    addlClasses: props.addTagInputClasses,
    autoFocus: true,
    name: "create-tag",
    onChange: props.onAddChange,
    placeholder: "Add tag",
    type: "text",
    value: props.addTagValue
  }), /*#__PURE__*/React.createElement(ButtonGroup, null, /*#__PURE__*/React.createElement(Button, {
    modifier: "secondary",
    render: bProps => /*#__PURE__*/React.createElement("button", _extends({}, bProps, {
      onClick: props.onToggle
    }))
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    addlClasses: props.addTagActionClasses,
    disabled: !props.isTagValid,
    render: bProps => /*#__PURE__*/React.createElement("button", _extends({}, bProps, {
      onClick: props.onCreateTag
    }))
  }, "Add"))), isNotNil(props.pattern) && /*#__PURE__*/React.createElement("div", {
    className: "add-tags-actions__pattern"
  }, props.pattern));
};

// ---------------------------------------------------------------------------

const MoreTag = props => {
  const classes = classNames('click-tag', 'click-tag--more', {
    'is-active': props.isActive
  }, props.addlClasses);
  return /*#__PURE__*/React.createElement("div", {
    className: classes,
    onClick: props.onClick
  }, /*#__PURE__*/React.createElement("span", {
    className: "click-tag-text"
  }, "More"), /*#__PURE__*/React.createElement("span", {
    className: "click-tag-action click-tag-action--more"
  }, /*#__PURE__*/React.createElement(Icon, {
    kind: "caret",
    size: {
      width: 6,
      height: 4
    }
  })));
};

// ---------------------------------------------------------------------------

class Tag extends React.Component {
  constructor(props) {
    super(props);
  }
  render() {
    const classes = classNames('click-tag', {
      clickable: this.props.canClick
    }, this.props.addlClasses);
    return /*#__PURE__*/React.createElement("div", {
      className: classes,
      "data-tag-name": this.props.tag,
      id: "tag",
      title: this.props.tag
    }, /*#__PURE__*/React.createElement("span", {
      className: "click-tag-text"
    }, this.props.tag), this.props.canRemove && /*#__PURE__*/React.createElement("span", {
      className: "click-tag-action click-tag-action--remove",
      "data-remove-tag": this.props.tag
    }, /*#__PURE__*/React.createElement(Icon, {
      kind: "remove",
      size: {
        width: 6,
        height: 6
      }
    })));
  }
}

// ---------------------------------------------------------------------------

class MoreList extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(Dropdown, {
      addlClasses: "more-tag-dropdown"
    }, /*#__PURE__*/React.createElement(DropdownToggle, {
      addlClasses: "more-tag-dropdown-toggle",
      render: ({
        handleToggle,
        isOpen
      }) => /*#__PURE__*/React.createElement(MoreTag, {
        onClick: handleToggle,
        isActive: isOpen
      })
    }), /*#__PURE__*/React.createElement(DropdownContent, {
      addlClasses: "more-tag-dropdown-content",
      maxHeight: 220,
      render: () => /*#__PURE__*/React.createElement("ul", {
        className: "tag-list"
      }, this.props.tags.sort().map((tag, index) => /*#__PURE__*/React.createElement("li", {
        key: index,
        className: "tag-list-item"
      }, /*#__PURE__*/React.createElement(Tag, {
        addlClasses: this.props.tagsModifier,
        canClick: this.props.canClick,
        canRemove: this.props.canRemove,
        tag: tag
      }))))
    }));
  }
}

// ---------------------------------------------------------------------------

// @TODO: create narrow type of allowable attributes
//
// We need to *also* allow data-attrs, which this type won't satisfy. We'll
// need to create a better mapped type. Essentially:
//
// Are you an allowed DOM attributes? Otherwise, do you begin with data-*
// type AddlAttrs = Partial<React.HTMLAttributes<HTMLElement>>

class TagList extends React.Component {
  constructor(...args) {
    super(...args);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _defineProperty(this, "_handleEvent", e => {
      // eslint-disable-next-line
      const removeTag = e.target.closest('.click-tag-action--remove');
      // eslint-disable-next-line
      const dataEl = e.target.closest('.click-tag');
      // eslint-disable-next-line
      const id = dataEl && dataEl.id;
      if (removeTag) {
        // eslint-disable-next-line
        const removeTagName = removeTag.getAttribute('data-remove-tag');
        this.props.onRemove(removeTagName);
        // eslint-disable-next-line
      } else if (id && this.props.onClickTagName) {
        // eslint-disable-next-line
        const {
          dataset
        } = dataEl;
        // eslint-disable-next-line
        this.props.onClickTagName(dataset.tagName);
      }
    });
  }
  render() {
    const classes = classNames('tag-list', this.props.addlClasses);
    const [one, two, three, ...rest] = this.props.selectedTags.sort();
    const isDisabled = this.props.isDisabled ?? false;
    const addlAttrs = isNotNil(this.props.addlAttrs) ? this.props.addlAttrs : {};
    if (isNil(this.props.readOnly)) {
      throw new DefaultPropError('TagList is missing props.readOnly');
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, this.props.readOnly && this.props.selectedTags.length < 1 ? /*#__PURE__*/React.createElement("div", _extends({
      className: classes
    }, addlAttrs), isNotNil(this.props.renderEmptyMessage) && /*#__PURE__*/React.createElement(React.Fragment, null, this.props.renderEmptyMessage())) : /*#__PURE__*/React.createElement("ul", _extends({
      className: classes,
      onClick: this._handleEvent
    }, addlAttrs), [one, two, three].filter(Boolean).map((tag, index) => /*#__PURE__*/React.createElement("li", {
      key: index,
      className: "tag-list-item"
    }, /*#__PURE__*/React.createElement(Tag, {
      addlClasses: this.props.tagsModifier,
      canClick: !!this.props.onClickTagName,
      canRemove: !this.props.readOnly,
      tag: tag
    }))), rest.length > 0 && /*#__PURE__*/React.createElement("li", {
      className: "tag-list-item"
    }, /*#__PURE__*/React.createElement(MoreList, {
      tags: rest,
      canClick: !!this.props.onClickTagName,
      canRemove: !this.props.readOnly,
      tagsModifier: this.props.tagsModifier
    })), !this.props.readOnly && /*#__PURE__*/React.createElement("li", {
      className: "tag-list-item"
    }, /*#__PURE__*/React.createElement(AddList, {
      activeTagStates: this.props.activeTagStates,
      tags: without(this.props.selectedTags, this.props.availableTags),
      addlAttrs: this.props.addlButtonAttrs,
      allowCreate: this.props.allowCreate,
      asButton: this.props.asButton,
      buttonModifier: this.props.buttonModifier,
      buttonTitle: this.props.buttonTitle,
      isDisabled: isDisabled,
      onApply: this.props.onApply,
      onCreate: this.props.onCreate,
      onStage: this.props.onStage,
      pattern: this.props.pattern,
      validate: this.props.validate
    }))));
  }
}
_defineProperty(TagList, "defaultProps", {
  allowCreate: true,
  buttonTitle: 'Tag',
  readOnly: false,
  renderEmptyMessage: () => /*#__PURE__*/React.createElement("p", null, "No Tags"),
  validate: _tag => true
});

// ---------------------------------------------------------------------------

const RenderList = props => {
  const [search, setSearch] = React.useState('');
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "add-tags__search"
  }, /*#__PURE__*/React.createElement(Input, {
    modifier: "primary",
    name: "search-tags",
    onChange: e => setSearch(e.currentTarget.value),
    placeholder: "Search tags",
    type: "text",
    value: search
  })), /*#__PURE__*/React.createElement("ul", {
    className: "add-tags__list dropdown-new-options"
  }, props.tags.length > 0 ? props.tags.filter(findTags(search)).sort(sortByCaseInsensitive).map(({
    tag,
    state
  }) => {
    const setCheckRef = checkbox => {
      if (isNotNil(checkbox)) {
        checkbox.indeterminate = state === ActiveTagState.indeterminate;
      }
    };
    return /*#__PURE__*/React.createElement("li", {
      key: tag,
      className: "add-tags__list__item dropdown-new-option",
      "data-add-tag": tag
    }, /*#__PURE__*/React.createElement(Checkbox, {
      checked: state === ActiveTagState.checked,
      id: tag,
      label: tag,
      name: tag,
      onChange: props.onToggle,
      ref: setCheckRef
    }));
  }) : /*#__PURE__*/React.createElement("li", {
    className: "add-tags__empty__item"
  }, "No more tags")));
};
const findTags = searchValue => ({
  tag
}) => {
  const _searchValue = searchValue.toLowerCase();
  const _tag = tag.toLowerCase();
  return _tag.startsWith(_searchValue);
};
const sortByCaseInsensitive = ({
  tag: a
}, {
  tag: b
}) => {
  return a.toLowerCase().localeCompare(b.toLowerCase());
};

// ---------------------------------------------------------------------------

class AddTag extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      addTagValue: '',
      isCreateVisible: false,
      isTagValid: false
    });
    // -------------------------------------------------------------------------
    _defineProperty(this, "handleAddChange", e => {
      const {
        validate
      } = this.props;
      this.setState({
        addTagValue: e.target.value,
        isTagValid: validate(e.target.value)
      });
    });
    _defineProperty(this, "handleToggle", () => {
      this.setState(({
        isCreateVisible
      }) => ({
        isCreateVisible: !isCreateVisible
      }));
    });
    _defineProperty(this, "onCreateTag", () => () => {
      if (this.props.onCreate && !isEmpty(this.state.addTagValue)) {
        this.props.onCreate(this.state.addTagValue);
        this.setState({
          isCreateVisible: false,
          addTagValue: ''
        });
      }
    });
    _defineProperty(this, "onApplyTag", () => {
      if (this.props.onApply) {
        this.props.onApply();
        this.setState({
          isCreateVisible: false,
          addTagValue: ''
        });
      }
    });
  }
  render() {
    if (isNil(this.props.showActions)) {
      throw new DefaultPropError('AddTag is missing props.showActions');
    }
    const addTagActionClasses = classNames('add-tag-action-submit', {
      disabled: !this.state.isTagValid
    });
    const addTagInputClasses = classNames({
      invalid: !this.state.isTagValid
    });
    const onClick = () => this.onApplyTag();
    const onCreateTag = this.onCreateTag();
    const onKeyUp = validateEnterKeyPress(() => this.onApplyTag());
    const _tags = this.props.tags.map(tag => {
      return {
        tag,
        state: this.props.activeTagStates[tag]
      };
    });
    return /*#__PURE__*/React.createElement("div", {
      className: "add-tags__content"
    }, /*#__PURE__*/React.createElement(RenderList, {
      tags: _tags,
      onToggle: (_e, tag) => {
        if (isNotNil(tag)) {
          this.props.onStage(tag);
        }
      }
    }), this.props.showActions && /*#__PURE__*/React.createElement(React.Fragment, null, this.state.isCreateVisible ? /*#__PURE__*/React.createElement(CreateTag, {
      addTagActionClasses: addTagActionClasses,
      addTagInputClasses: addTagInputClasses,
      addTagValue: this.state.addTagValue,
      isTagValid: this.state.isTagValid,
      onAddChange: this.handleAddChange,
      onCreateTag: onCreateTag,
      onKeyUp: onKeyUp,
      onToggle: this.handleToggle,
      pattern: this.props.pattern
    }) : /*#__PURE__*/React.createElement("div", {
      className: "add_tags__actions"
    }, /*#__PURE__*/React.createElement(ButtonGroup, {
      addlClasses: "add_tags__actions__apply"
    }, this.props.allowCreate && /*#__PURE__*/React.createElement(Button, {
      addlClasses: "add-tag-action-trigger",
      modifier: "secondary",
      render: bProps => /*#__PURE__*/React.createElement("button", _extends({}, bProps, {
        onClick: this.handleToggle
      })),
      icon: {
        kind: 'plus',
        position: 'left',
        size: {
          height: 10,
          width: 10
        }
      }
    }, "Create new tag"), /*#__PURE__*/React.createElement(Button, {
      render: props => /*#__PURE__*/React.createElement("button", _extends({}, props, {
        onClick: onClick
      }))
    }, "Apply")))));
  }
}
_defineProperty(AddTag, "defaultProps", {
  showActions: true
});

const defaultGetDimensions = function defaultGetDimensions(el) {
  return [el.clientWidth, el.clientHeight];
};
function withDimensions(CC, injectedProps) {
  const getDimensions = injectedProps ? injectedProps.getDimensions || defaultGetDimensions : defaultGetDimensions;
  const globalResize = injectedProps ? injectedProps.globalResize || false : false;
  class Dimensions extends React.Component {
    constructor(props) {
      super(props);
      _defineProperty(this, "_parent", void 0);
      _defineProperty(this, "_wrapperRef", void 0);
      _defineProperty(this, "rqf", void 0);
      _defineProperty(this, "state", {
        containerHeight: 0,
        containerWidth: 0
      });
      _defineProperty(this, "onResize", () => {
        if (this.rqf) return;
        this.rqf = window.requestAnimationFrame(() => {
          this.rqf = null;
          this.updateDimensions();
        });
      });
      _defineProperty(this, "updateDimensions", () => {
        if (!this._parent) {
          return;
        }
        const dimensions = getDimensions(this._parent);
        if (dimensions[0] !== this.state.containerWidth || dimensions[1] !== this.state.containerHeight) {
          this.setState({
            containerHeight: dimensions[1],
            containerWidth: dimensions[0]
          });
        }
      });
      this._wrapperRef = /*#__PURE__*/React.createRef();
    }
    componentDidMount() {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this._parent = this._wrapperRef.current.parentNode;
      this.updateDimensions();
      if (globalResize) {
        window.addEventListener('resize', this.onResize, false);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        onElementResize(this._parent, this.updateDimensions);
      }
    }
    componentWillUnmount() {
      window.removeEventListener('resize', this.onResize);
    }
    render() {
      const {
        passedRef,
        ...restProps
      } = this.props;
      const nextProps = {
        ...this.state,
        ...restProps,
        ref: passedRef,
        updateDimensions: this.updateDimensions
      };
      return (
        /*#__PURE__*/
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        React.createElement("span", {
          ref: this._wrapperRef
        }, /*#__PURE__*/React.createElement(CC, nextProps))
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  _defineProperty(Dimensions, "getDisplayName", `withDimensions(${getDisplayName(CC)})`);
  hoistNonReactStatics(Dimensions, CC);
  return /*#__PURE__*/React.forwardRef((props, ref) =>
  /*#__PURE__*/
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  React.createElement(Dimensions, _extends({}, props, {
    passedRef: ref
  })));
}

export { AccordionCard, ActiveTagState, AddTag, AreaGraph, ArrayOperator, Avatar, AvatarUtils, Axis, BASE_RULE, BarGraph, BlockOptions, BlockType, Button, ButtonGroup, ButtonTabs, CalcScrollbar, CarbonIcon, CarbonToggle, Card, Checkbox, Collapse, CollapseHeight, CollapsibleCard, Condition, Conjunction, ContentGridColumnConfig, ContentGridContainer, ContentGridItem, CrudRule, CrudRuleGroup, DefaultPropError, DependentPropError, DescriptionList, Details, Dropdown, DropdownContainer, DropdownContent, DropdownNew, DropdownToggle, EtcOperator, ExhaustiveError, Fieldset, FileInput, Form, FormInput, GenericOperator, GrowlerContainer, Icon, IconAccount, IconAccountSettings, IconAddCircle, IconArrowDown, IconArrowThick, IconArrowTrend, IconArrowUp, IconAttack, IconAuthorized, IconAuthorizedPartial, IconCaret, IconCaretThin, IconCascade, IconChainBreak, IconChainBreakDiag, IconChainDiag, IconCheck, IconCli, IconClipboard, IconClose, IconCloud, IconComment, IconCompany, IconCompass, IconConfidence, IconCopyPaste, IconCustomer, IconDashboard, IconDate, IconDeny, IconDetails, IconDockBottom, IconDockRight, IconDomains, IconDrawer, IconEdit, IconExternalLink, IconEye, IconFailure, IconFilter, IconFilterDismiss, IconGrabHandle, IconHelp, IconHoc, IconIPs, IconImage, IconImpact, IconInfo, IconLink, IconList, IconLock, IconLockAccount, IconLogout, IconMap, IconMark, IconMenu, IconMinus, IconMore, IconNavCaret, IconNavCollapse, IconNavExpand, IconNetworks, IconNo, IconNote, IconNotifications, IconPencil, IconPeople, IconPlayer, IconPlus, IconPolicy, IconPorts, IconPromote, IconQuestion, IconRAT, IconRecon, IconRemove, IconRibbon, IconRibbonNew, IconRobot, IconSave, IconSearch, IconSearchSmall, IconSend, IconSeparator, IconServer, IconServices, IconShare, IconSignPost, IconSlideClosed, IconSlideOpen, IconSortAsc, IconSortDesc, IconSpinner, IconStar, IconStatus, IconStatusInProgress, IconStatusInvestigate, IconStatusNone, IconStatusResolved, IconStatusReviewed, IconStop, IconSuccess, IconTag, IconTarget, IconTech, IconThumbSearch, IconTrash, IconUnauthorized, IconUpdate, IconUpload, IconUser, IconUtils, IconV2Close, IconV2Collapse, IconV2Expand, IconV2Info, IconWarning, IconWarning2, IconWrench, InetOperator, InfoPopper, Input, InputIcon, JsonOperator, Label, LineClamp, LineClampBase, Link, Loader, Logo, Modal, ModalP, Operator, OperatorLabel, Pager, Path, PieChart, PieChartView, Pill, Pin, PopperBase, PortalContent, Pulse, Radio, ReadMore, RegexOperator, RenderEffect, RenderEffectPopper, RenderList, RenderPopper, Rule, RuleGroup, RuleOrRuleGroup, ScrollContainer, Section, Select, Slideout, StackedBarGraph, StatBlock, StatBlockGroup, Table$1 as Table, TableElements, tableV2_utils as TableUtils, TableV2, TableV2Elements, TagList, TagsModifiers, Test, TextArea, Toggle, TsTzDataOperator, Typography, __ENTER_KEY__, compose, contentWidth, dataToSegmentAttrs, degreesToRadians, elemT, evaluateViewBoxSize, extractPercentage, fullWidth, getOperatorLabel, growler, isNotEmpty, isNotNil, isNotNilOrEmpty, isRandoriIcon, isRenderProps, isRule, isRuleGroup, makeBaseGroup, makeClampComponent, makePathCommands, partialCircle, setScrollbarWidthVar, useToggle, validateEnterKeyPress, valueBetween, variantStyles, withCatch, withDimensions, withEffect };
